import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, subDays } from 'date-fns';
import { Calendar, Users, Settings, ChevronLeft, ChevronRight, ChevronDown, User, Plus, X, Trash2, Search, AlertTriangle, Edit2, Map, Check, CheckCircle, Copy, Home, LayoutGrid, BarChart3, TrendingUp, Clock, DollarSign, Award, Target, Wind, FileDown, FileText, ListTodo, RefreshCw } from 'lucide-react';
import axios from 'axios';
import LiveMap from '../LiveMap';
import TimelineFullscreen from '../TimelineFullscreen';
import Toolbar from './Toolbar';
import Timeline from './Timeline';
import PeopleList from './PeopleList';
import CarList from './CarList';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Add custom CSS for slider styling and animations
const sliderStyles = `
  .slider-thumb-black::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #000000;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 0 2px rgba(0,0,0,0.3);
  }

  .slider-thumb-black::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #000000;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 0 2px rgba(0,0,0,0.3);
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .animate-slide-in-right {
    animation: slideInRight 0.3s ease-out forwards;
  }
`;

// Inject styles into document head
if (!document.getElementById('slider-custom-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = 'slider-custom-styles';
  styleSheet.innerText = sliderStyles;
  document.head.appendChild(styleSheet);
}

const DailyWorkPlanning = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Mark task as completed
  const markTaskCompleted = async (taskId, completionNotes = '') => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      await axios.post(
        `${API_BASE_URL}/api/task-assignments/${taskId}/complete/`,
        { completion_notes: completionNotes },
        { headers }
      );

      // Reload task assignments
      await loadTaskAssignments();
      alert('Task marked as completed successfully!');
    } catch (error) {
      console.error('Error marking task as completed:', error);
      alert('Failed to mark task as completed. Please try again.');
    }
  };

  // Unmark task completion
  const unmarkTaskCompleted = async (taskId) => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      await axios.post(
        `${API_BASE_URL}/api/task-assignments/${taskId}/uncomplete/`,
        {},
        { headers }
      );

      // Reload task assignments
      await loadTaskAssignments();
      alert('Task completion status removed successfully!');
    } catch (error) {
      console.error('Error removing task completion:', error);
      alert('Failed to remove task completion. Please try again.');
    }
  };

  // Convert time string to minutes since midnight
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert minutes since midnight to time string
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Group consecutive time slots with the same assignment into blocks
  const groupTimeSlotsByAssignment = (personId, timeSlots) => {
    const blocks = [];
    let currentBlock = null;

    timeSlots.forEach((timeSlot, index) => {
      const assignments = getAllPersonAssignmentsAtTime(personId, timeSlot);
      const primaryAssignment = assignments.find(a => !a.isAlternative);
      const alternativeAssignments = assignments.filter(a => a.isAlternative);

      // Use primary assignment for block grouping, alternatives will be shown as overlays
      const assignment = primaryAssignment || assignments[0] || null;

      if (assignment) {
        // Check if this assignment is the same as the current block
        if (currentBlock && currentBlock.assignment &&
            currentBlock.assignment.id === assignment.id &&
            currentBlock.assignment.turbineName === assignment.turbineName &&
            currentBlock.assignment.category === assignment.category &&
            currentBlock.assignment.subcategory === assignment.subcategory) {
          // Extend current block
          currentBlock.endSlotIndex = index;
          currentBlock.endTime = timeSlot;
          currentBlock.slotCount++;
          // Add alternative assignments to this slot
          if (alternativeAssignments.length > 0) {
            if (!currentBlock.alternativeAssignments) currentBlock.alternativeAssignments = {};
            currentBlock.alternativeAssignments[timeSlot] = alternativeAssignments;
          }
        } else {
          // Start new block for assignment
          if (currentBlock) blocks.push(currentBlock);
          currentBlock = {
            assignment,
            startSlotIndex: index,
            endSlotIndex: index,
            startTime: timeSlot,
            endTime: timeSlot,
            slotCount: 1,
            type: 'assigned',
            alternativeAssignments: alternativeAssignments.length > 0 ? { [timeSlot]: alternativeAssignments } : {}
          };
        }
      } else {
        // No assignment - close current block and add single empty slot
        if (currentBlock) {
          blocks.push(currentBlock);
          currentBlock = null;
        }
        // Don't group empty slots - add each as separate
        blocks.push({
          assignment: null,
          startSlotIndex: index,
          endSlotIndex: index,
          startTime: timeSlot,
          endTime: timeSlot,
          slotCount: 1,
          type: 'empty'
        });
      }
    });

    // Add final block if exists
    if (currentBlock) blocks.push(currentBlock);

    return blocks;
  };

  // Group consecutive time slots for cars with the same assignment into blocks
  const groupCarTimeSlotsByAssignment = (carId, timeSlots) => {
    const blocks = [];
    let currentBlock = null;

    timeSlots.forEach((timeSlot, index) => {
      const assignment = getCarAssignmentAtTime(carId, timeSlot);

      if (assignment) {
        // Check if this assignment is the same as the current block
        if (currentBlock && currentBlock.assignment &&
            currentBlock.assignment.id === assignment.id &&
            currentBlock.assignment.turbineName === assignment.turbineName &&
            currentBlock.assignment.category === assignment.category &&
            currentBlock.assignment.subcategory === assignment.subcategory) {
          // Extend current block
          currentBlock.endSlotIndex = index;
          currentBlock.endTime = timeSlot;
          currentBlock.slotCount++;
        } else {
          // Start new block for assignment
          if (currentBlock) blocks.push(currentBlock);
          currentBlock = {
            assignment,
            startSlotIndex: index,
            endSlotIndex: index,
            startTime: timeSlot,
            endTime: timeSlot,
            slotCount: 1,
            type: 'assigned'
          };
        }
      } else {
        // No assignment - close current block and add single empty slot
        if (currentBlock) {
          blocks.push(currentBlock);
          currentBlock = null;
        }
        // Don't group empty slots - add each as separate
        blocks.push({
          assignment: null,
          startSlotIndex: index,
          endSlotIndex: index,
          startTime: timeSlot,
          endTime: timeSlot,
          slotCount: 1,
          type: 'empty'
        });
      }
    });

    // Add final block if exists
    if (currentBlock) blocks.push(currentBlock);

    return blocks;
  };

  // Open copy tasks modal
  const openCopyModal = () => {
    const currentDateString = format(selectedDate, 'yyyy-MM-dd');
    const nextDay = addDays(selectedDate, 1);

    // Get incomplete assignments for current date, only from selected turbines
    const allCurrentAssignments = [];
    Object.keys(turbineAssignments).forEach(turbineId => {
      // Only include tasks from turbines that are selected for work planning
      if (!selectedTurbines.has(parseInt(turbineId))) return;

      turbineAssignments[turbineId].forEach(assignment => {
        if (assignment.date === currentDateString && !assignment.is_completed) {
          allCurrentAssignments.push(assignment);
        }
      });
    });

    if (allCurrentAssignments.length === 0) {
      alert('No incomplete tasks found for the current date on selected turbines.');
      return;
    }

    setCopyTargetDate(nextDay);
    setCopyTasksData({ tasks: allCurrentAssignments, existingCount: 0 });
    setShowCopyModal(true);
  };

  // Analyze people availability for copy modal - returns detailed info per task
  const analyzePeopleAvailability = (targetDate, tasks) => {
    if (!targetDate || !tasks || tasks.length === 0) {
      return { allTasks: [], summary: { ok: 0, duplicates: 0, noOneAvailable: 0, someUnavailable: 0 } };
    }

    // Get people assigned to project on target date
    // Note: targetDateAssignments are already filtered by backend (project + target date)
    const peopleOnTargetDate = targetDateAssignments.map(pa => pa.user);

    // Use targetDateTaskAssignments loaded from backend for target date
    // Note: these are in backend format (turbine_id, subcategory_name, start_time, etc.)
    const targetDayAssignments = targetDateTaskAssignments;

    const allTasks = []; // All tasks with their status
    const summary = { ok: 0, duplicates: 0, noOneAvailable: 0, someUnavailable: 0 };

    tasks.forEach(task => {
      const taskInfo = {
        taskId: task.id,
        turbineId: task.turbineId,
        category: task.category,
        subcategory: task.subcategory,
        startTime: task.startTime,
        endTime: task.endTime,
        notOnProject: [],
        hasConflicts: [],
        available: [],
        isDuplicate: false,
        existingTime: null,
        status: 'ok' // 'ok', 'duplicate', 'no_people', 'some_unavailable'
      };

      // Check if task already exists on target date (same turbine + subcategory)
      // Note: backend uses turbine_id and subcategory_name
      const existingTask = targetDayAssignments.find(a =>
        a.turbine_id === task.turbineId &&
        (a.subcategory_name === task.subcategory || a.subcategory === task.subcategory)
      );
      if (existingTask) {
        taskInfo.isDuplicate = true;
        taskInfo.existingTime = `${existingTask.start_time}-${existingTask.end_time}`;
      }

      (task.assignedPeople || []).forEach(personId => {
        const person = allPeople.find(p => p.id === personId);
        const personName = person
          ? (person.first_name && person.last_name
            ? `${person.first_name} ${person.last_name}`
            : person.username)
          : `ID: ${personId}`;

        // Check if person is on project on target date
        if (!peopleOnTargetDate.includes(personId)) {
          taskInfo.notOnProject.push({ id: personId, name: personName });
          return;
        }

        // Check for time conflicts
        // Note: backend uses assigned_people, start_time, end_time
        const startMinutes = timeToMinutes(task.startTime);
        const endMinutes = timeToMinutes(task.endTime);

        const hasConflict = targetDayAssignments.some(existingAssignment => {
          if (!existingAssignment.assigned_people?.includes(personId)) return false;
          const existingStartMinutes = timeToMinutes(existingAssignment.start_time);
          const existingEndMinutes = timeToMinutes(existingAssignment.end_time);
          return !(endMinutes <= existingStartMinutes || startMinutes >= existingEndMinutes);
        });

        if (hasConflict) {
          taskInfo.hasConflicts.push({ id: personId, name: personName });
        } else {
          taskInfo.available.push({ id: personId, name: personName });
        }
      });

      // Determine task status
      if (taskInfo.available.length === 0 && (task.assignedPeople || []).length > 0) {
        taskInfo.status = 'no_people';
        summary.noOneAvailable++;
      } else if (taskInfo.notOnProject.length > 0 || taskInfo.hasConflicts.length > 0) {
        taskInfo.status = 'some_unavailable';
        summary.someUnavailable++;
      } else if (taskInfo.isDuplicate) {
        taskInfo.status = 'duplicate';
        summary.duplicates++;
      } else {
        taskInfo.status = 'ok';
        summary.ok++;
      }

      allTasks.push(taskInfo);
    });

    return { allTasks, summary };
  };

  // Execute copy tasks to selected date
  const executeCopyTasks = async () => {
    if (!copyTargetDate || copyTasksData.tasks.length === 0) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'application/json'
      };

      const targetDateString = format(copyTargetDate, 'yyyy-MM-dd');

      // Get people assigned to project on TARGET date (not current date)
      // Note: targetDateAssignments are already filtered by backend (project + target date)
      const peopleOnTargetDate = targetDateAssignments.map(pa => pa.user);

      let copiedCount = 0;
      let errors = [];

      console.log('Target date string:', targetDateString);
      console.log('People available on target date:', peopleOnTargetDate.length);
      console.log('Copying', copyTasksData.tasks.length, 'incomplete tasks');

      for (const assignment of copyTasksData.tasks) {
        try {
          // Check people availability for target date
          // Note: targetDateTaskAssignments uses backend format (assigned_people, start_time, end_time)
          const availablePeople = assignment.assignedPeople.filter(personId => {
            // First check: Is person assigned to project on target date?
            if (!peopleOnTargetDate.includes(personId)) {
              console.log(`Person ${personId} not assigned to project on ${targetDateString}`);
              return false;
            }

            const startMinutes = timeToMinutes(assignment.startTime);
            const endMinutes = timeToMinutes(assignment.endTime);

            // Second check: Time conflicts with existing tasks
            const hasConflict = targetDateTaskAssignments.some(existingAssignment => {
              if (!existingAssignment.assigned_people?.includes(personId)) return false;
              const existingStartMinutes = timeToMinutes(existingAssignment.start_time);
              const existingEndMinutes = timeToMinutes(existingAssignment.end_time);
              return !(endMinutes <= existingStartMinutes || startMinutes >= existingEndMinutes);
            });

            return !hasConflict;
          });

          console.log(`Task T${assignment.turbineId}: ${assignment.subcategory} - Originally ${assignment.assignedPeople.length} people, ${availablePeople.length} available on target date`);

          // Use turbine FK ID if available (preserves exact turbine reference),
          // fallback to turbine_id (turbine_number) for legacy assignments
          const newAssignment = {
            project: parseInt(selectedProject),
            ...(assignment.turbineFk ? { turbine: assignment.turbineFk } : { turbine_id: assignment.turbineId }),
            subcategory_id: assignment.subcategory_id,
            start_time: assignment.startTime,
            end_time: assignment.endTime,
            people_needed: assignment.peopleNeeded,
            assigned_people: availablePeople,
            assigned_car: null,
            is_alternative: assignment.isAlternative || false,
            date: targetDateString
          };

          console.log('Copying task assignment:', newAssignment);
          await axios.post(`${API_BASE_URL}/api/task-assignments/`, newAssignment, { headers });
          copiedCount++;
        } catch (error) {
          console.error(`Error copying task ${assignment.id}:`, error);
          errors.push(`T${assignment.turbineId}: ${assignment.subcategory}`);
        }
      }

      // Reload assignments
      await loadTaskAssignments();

      // Close modal
      setShowCopyModal(false);

      if (errors.length > 0) {
        alert(`Copied ${copiedCount} tasks successfully.\n\nFailed to copy ${errors.length} tasks:\n${errors.join('\n')}`);
      } else {
        alert(`Successfully copied ${copiedCount} incomplete tasks to ${format(copyTargetDate, 'EEE, MMM d, yyyy')}!`);
      }

      // Switch to target date to see copied tasks
      setSelectedDate(copyTargetDate);

    } catch (error) {
      console.error('Error copying tasks:', error);
      alert('Failed to copy tasks. Please try again.');
    }
  };

  // Legacy function name for compatibility
  const copyTasksToNextDay = openCopyModal;

  // Update task time
  const updateTaskTime = async (taskId, newStartTime, newEndTime) => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'application/json'
      };

      await axios.patch(
        `${API_BASE_URL}/api/task-assignments/${taskId}/`,
        {
          start_time: newStartTime,
          end_time: newEndTime
        },
        { headers }
      );

      // Reload task assignments
      await loadTaskAssignments();
    } catch (error) {
      console.error('Error updating task time:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to update task time. Please try again.');
    }
  };

  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [turbines, setTurbines] = useState([]);
  const [selectedTurbines, setSelectedTurbines] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [taskCategories, setTaskCategories] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [newTaskData, setNewTaskData] = useState({
    categoryName: '',
    categoryColor: '#3B82F6', // Default blue
    subcategories: [{ name: '', description: '' }]
  });
  const [showTurbineSelection, setShowTurbineSelection] = useState(true);
  const [turbineAssignments, setTurbineAssignments] = useState({});
  const [showTaskAssignModal, setShowTaskAssignModal] = useState(false);
  const [selectedTaskForAssign, setSelectedTaskForAssign] = useState(null);
  const [selectedTurbineForAssign, setSelectedTurbineForAssign] = useState(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [taskAssignData, setTaskAssignData] = useState({
    startTime: '08:00',
    endTime: '16:00',
    peopleNeeded: 2,
    assignedPeople: [],
    assignedCar: null,
    isAlternative: false
  });
  const [peopleSearchTerm, setPeopleSearchTerm] = useState('');
  const [showTimeSliders, setShowTimeSliders] = useState(null); // ID of task showing sliders
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAssignmentEditModal, setShowAssignmentEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [timelineStartTime, setTimelineStartTime] = useState('07:00');
  const [timelineEndTime, setTimelineEndTime] = useState('19:00');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState(null);
  const [targetDateAssignments, setTargetDateAssignments] = useState([]);
  const [targetDateTaskAssignments, setTargetDateTaskAssignments] = useState([]);
  const [copyTasksData, setCopyTasksData] = useState({ tasks: [], existingCount: 0 });
  const [isTimelineFullscreen, setIsTimelineFullscreen] = useState(false);
  const [collapsedTurbines, setCollapsedTurbines] = useState(new Set());
  const [projectCars, setProjectCars] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [collapsedColumns, setCollapsedColumns] = useState({
    people: false,
    cars: false
  });
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [selectedTaskForPeopleFilter, setSelectedTaskForPeopleFilter] = useState(null);
  const [showLiveMap, setShowLiveMap] = useState(false);
  const [liveMapKey, setLiveMapKey] = useState(0);
    const [showTimeline, setShowTimeline] = useState(false);
  const [showProjectOverview, setShowProjectOverview] = useState(false);
  const [expandedOverviewTurbine, setExpandedOverviewTurbine] = useState(null);
  const [selectedOverviewTask, setSelectedOverviewTask] = useState(null);
  const [allProjectAssignments, setAllProjectAssignments] = useState({});
  const [loadingProjectOverview, setLoadingProjectOverview] = useState(false);
  const [overviewActiveTab, setOverviewActiveTab] = useState('turbines'); // 'turbines' or 'analytics'

  // Quick Add Modal states
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddStep, setQuickAddStep] = useState(1); // 1: select task, 2: select people, 3: select car
  const [quickAddTurbineId, setQuickAddTurbineId] = useState(null);
  const [quickAddSelectedTasks, setQuickAddSelectedTasks] = useState([]); // Array of tasks (unlimited)
  const [quickAddTaskTimes, setQuickAddTaskTimes] = useState([]); // Array of {startTime, endTime} for each task
  const [quickAddPeople, setQuickAddPeople] = useState([]);
  const [quickAddCar, setQuickAddCar] = useState(null);
  const [quickAddStartTime, setQuickAddStartTime] = useState('07:00');
  const [quickAddEndTime, setQuickAddEndTime] = useState('19:00');
  const [quickAddIndividualTimes, setQuickAddIndividualTimes] = useState({}); // { personId: { startTime, endTime } }
  const [quickAddPeopleSearch, setQuickAddPeopleSearch] = useState('');
  const [quickAddTaskSearch, setQuickAddTaskSearch] = useState(''); // Search for tasks in Step 1
  const [quickAddModalPosition, setQuickAddModalPosition] = useState({ x: 0, y: 0 });
  const [quickAddModalSize, setQuickAddModalSize] = useState({ width: 896, height: 600 }); // max-w-4xl = 896px
  const [isDraggingModal, setIsDraggingModal] = useState(false);
  const [isResizingModal, setIsResizingModal] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Task List Edit Modal states
  const [showTaskListEditModal, setShowTaskListEditModal] = useState(false);
  const [editingTaskListData, setEditingTaskListData] = useState(null);
  const [taskListFormData, setTaskListFormData] = useState({
    name: '',
    description: '',
    is_default: false,
    project: null,
    categories: []
  });
  const [expandedTaskListCategories, setExpandedTaskListCategories] = useState(new Set());
  const [editingTaskListCategory, setEditingTaskListCategory] = useState(null);
  const [taskListCategoryFormData, setTaskListCategoryFormData] = useState({
    categoryName: '',
    categoryColor: '#3B82F6',
    subcategories: [{ name: '', description: '', estimated_hours: '' }]
  });

  // Drag and drop states
  const [draggedTask, setDraggedTask] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [resizeHandle, setResizeHandle] = useState(null); // 'left', 'right', or null
  const [dragMode, setDragMode] = useState(null); // 'move', 'resize', or null
  const [dropZone, setDropZone] = useState(null); // Current drop zone {personId, timeSlot}

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load project-specific data when project changes
  useEffect(() => {
    console.log('🟡 selectedProject changed to:', selectedProject);
    console.trace('Stack trace for selectedProject change');
    if (selectedProject) {
      loadProjectData();
      loadAllProjectAssignments(); // Load all assignments for Quick Add completed status check
    }
  }, [selectedProject]);

  // Reload assignments when date or project changes
  useEffect(() => {
    if (selectedProject && selectedDate) {
      loadTaskAssignments();
      loadProjectAssignments();
    }
  }, [selectedDate, selectedProject]);

  // Load cars when project or date changes (independent of turbine assignments)
  useEffect(() => {
    if (selectedProject && selectedDate) {
      loadProjectCars();
    }
  }, [selectedProject, selectedDate]);

  // Also reload cars when turbine assignments change (for task-assigned cars)
  useEffect(() => {
    if (selectedProject && selectedDate && Object.keys(turbineAssignments).length >= 0) {
      loadProjectCars();
    }
  }, [turbineAssignments]);

  // Sync selectedTaskForPeopleFilter with updated turbineAssignments data
  // This ensures the "Available People" list is refreshed after person assignment changes
  useEffect(() => {
    if (selectedTaskForPeopleFilter) {
      // Find the updated version of the selected task in turbineAssignments
      const allAssignments = Object.values(turbineAssignments).flat();
      const updatedTask = allAssignments.find(a => a.id === selectedTaskForPeopleFilter.id);

      if (updatedTask) {
        // Only update if assignedPeople has changed to avoid infinite loops
        if (JSON.stringify(updatedTask.assignedPeople) !== JSON.stringify(selectedTaskForPeopleFilter.assignedPeople)) {
          setSelectedTaskForPeopleFilter(updatedTask);
        }
      } else {
        // Task was deleted, clear selection
        setSelectedTaskForPeopleFilter(null);
      }
    }
  }, [turbineAssignments]);

  // Debug: Track quickAddTurbineId changes
  useEffect(() => {
    if (showQuickAddModal) {
      console.log('🟡 Quick Add Modal opened - quickAddTurbineId is:', quickAddTurbineId);
    }
  }, [showQuickAddModal, quickAddTurbineId]);

  // Load assignments for target date when copy modal date changes
  useEffect(() => {
    if (copyTargetDate && showCopyModal) {
      loadAssignmentsForTargetDate(copyTargetDate);
      loadTaskAssignmentsForTargetDate(copyTargetDate);
    }
  }, [copyTargetDate, showCopyModal]);

  // Helper function to get categories for a specific turbine based on its TaskList
  const getCategoriesForTurbine = (turbineId) => {
    const turbine = turbines.find(t => t.id === turbineId || t.turbine_number === turbineId);
    if (turbine?.task_list_data?.categories) {
      return turbine.task_list_data.categories;
    }
    return [];
  };

  // Get categories for the currently selected turbine in Quick Add modal
  const quickAddCategories = quickAddTurbineId ? getCategoriesForTurbine(quickAddTurbineId) : taskCategories;

  // Helper function to calculate proportional task times
  const calculateProportionalTaskTimes = (numTasks, startTime, endTime) => {
    if (numTasks === 0) return [];
    if (numTasks === 1) return [{ startTime, endTime }];

    const toMinutes = (t) => parseInt(t.split(':')[0]) * 60 + parseInt(t.split(':')[1]);
    const toTime = (mins) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    const startMins = toMinutes(startTime);
    const endMins = toMinutes(endTime);
    const totalMins = endMins - startMins;
    const minsPerTask = Math.floor(totalMins / numTasks);

    const times = [];
    let currentStart = startMins;
    for (let i = 0; i < numTasks; i++) {
      const taskEnd = i === numTasks - 1 ? endMins : currentStart + minsPerTask;
      times.push({
        startTime: toTime(currentStart),
        endTime: toTime(taskEnd)
      });
      currentStart = taskEnd;
    }
    return times;
  };

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isTimelineFullscreen) {
        setIsTimelineFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isTimelineFullscreen]);

  // Handle Quick Add Modal dragging at document level (allows scrolling behind modal)
  useEffect(() => {
    if (!isDraggingModal && !isResizingModal) return;

    const handleMouseMove = (e) => {
      if (isDraggingModal) {
        setQuickAddModalPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
      if (isResizingModal) {
        const newWidth = Math.max(400, Math.min(1400, e.clientX - dragStart.x));
        const newHeight = Math.max(300, Math.min(900, e.clientY - dragStart.y));
        setQuickAddModalSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDraggingModal(false);
      setIsResizingModal(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingModal, isResizingModal, dragStart]);

  // Load all project assignments when projects are loaded (for people experience modal)
  // Need projects to be loaded first to get model info
  useEffect(() => {
    if (projects.length > 0) {
      loadAllProjectAssignments();
    }
  }, [projects]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      // Load projects and users (assignments loaded separately when project/date selected)
      const [projectsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/projects/`, { headers }),
        axios.get(`${API_BASE_URL}/api/users/minimal/`, { headers })
      ]);

      setProjects(projectsResponse.data);
      setAllPeople(usersResponse.data);

      // Auto-select first project if available
      if (projectsResponse.data.length > 0) {
        setSelectedProject(projectsResponse.data[0].id.toString());
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load resource planning assignments for selected project and date
  const loadProjectAssignments = async () => {
    if (!selectedProject || !selectedDate) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const dateString = format(selectedDate, 'yyyy-MM-dd');

      // Load only assignments for selected project and date range (with minimal fields)
      const response = await axios.get(
        `${API_BASE_URL}/api/resource-planning-assignments/?project=${selectedProject}&start_date=${dateString}&end_date=${dateString}&fields=minimal`,
        { headers }
      );

      setProjectAssignments(response.data);
    } catch (error) {
      console.error('Error loading project assignments:', error);
    }
  };

  // Load resource planning assignments for a specific target date (used by copy modal)
  const loadAssignmentsForTargetDate = async (targetDate) => {
    if (!selectedProject || !targetDate) {
      setTargetDateAssignments([]);
      return;
    }

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const dateString = format(targetDate, 'yyyy-MM-dd');

      const response = await axios.get(
        `${API_BASE_URL}/api/resource-planning-assignments/?project=${selectedProject}&start_date=${dateString}&end_date=${dateString}&fields=minimal`,
        { headers }
      );

      setTargetDateAssignments(response.data);
    } catch (error) {
      console.error('Error loading target date assignments:', error);
      setTargetDateAssignments([]);
    }
  };

  // Load task assignments for a specific target date (used by copy modal)
  const loadTaskAssignmentsForTargetDate = async (targetDate) => {
    if (!selectedProject || !targetDate) {
      setTargetDateTaskAssignments([]);
      return;
    }

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const dateString = format(targetDate, 'yyyy-MM-dd');

      const response = await axios.get(
        `${API_BASE_URL}/api/task-assignments/?project=${selectedProject}&date=${dateString}`,
        { headers }
      );

      setTargetDateTaskAssignments(response.data);
    } catch (error) {
      console.error('Error loading target date task assignments:', error);
      setTargetDateTaskAssignments([]);
    }
  };

  const loadProjectData = async () => {
    if (!selectedProject) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      // Load project details
      const projectResponse = await axios.get(`${API_BASE_URL}/api/projects/${selectedProject}/`, { headers });
      const project = projectResponse.data;

      // Load turbines from ProjectTurbine API with full TaskList data
      let loadedTurbines = [];
      try {
        const turbinesResponse = await axios.get(`${API_BASE_URL}/api/project-turbines/?project=${selectedProject}&include_tasks=true`, { headers });
        loadedTurbines = turbinesResponse.data.map(turbine => ({
          id: turbine.turbine_number, // Use turbine_number as ID for backwards compatibility
          db_id: turbine.id, // Actual database ID for PATCH requests
          turbine_number: turbine.turbine_number,
          turbine_id: turbine.turbine_id,
          name: `${project.type_of_turbine || 'Turbine'} ${turbine.turbine_number}`,
          type: project.type_of_turbine || 'Wind Turbine',
          model: turbine.model || project.model || '',
          power_rating: turbine.power_rating || project.power_rating || '',
          latitude: turbine.latitude,
          longitude: turbine.longitude,
          status: 'operational',
          task_list: turbine.task_list,
          task_list_data: turbine.task_list_data // Full TaskList with categories and subcategories
        }));
      } catch (error) {
        console.log('ProjectTurbine API not available, falling back to number_of_turbines');
      }

      // Fallback: if no turbines from API, generate from number_of_turbines
      if (loadedTurbines.length === 0) {
        const numberOfTurbines = parseInt(project.number_of_turbines) || 0;
        loadedTurbines = Array.from({ length: numberOfTurbines }, (_, index) => ({
          id: index + 1,
          db_id: null, // No database ID for generated turbines
          turbine_number: index + 1,
          turbine_id: `T${index + 1}`,
          name: `${project.type_of_turbine || 'Turbine'} ${index + 1}`,
          type: project.type_of_turbine || 'Wind Turbine',
          model: project.model || '',
          power_rating: project.power_rating || '',
          status: 'operational',
          task_list: null,
          task_list_data: null
        }));
      }

      setTurbines(loadedTurbines);

      // Select all turbines by default
      setSelectedTurbines(new Set(loadedTurbines.map(t => t.id)));

      // Build combined task categories from all turbines' TaskLists
      const allCategories = [];
      const seenCategoryIds = new Set();
      loadedTurbines.forEach(turbine => {
        if (turbine.task_list_data?.categories) {
          turbine.task_list_data.categories.forEach(cat => {
            if (!seenCategoryIds.has(cat.id)) {
              seenCategoryIds.add(cat.id);
              allCategories.push(cat);
            }
          });
        }
      });
      setTaskCategories(allCategories);

      // After loading turbines, reload assignments to properly set active turbines
      if (selectedDate) {
        await loadTaskAssignments();
        await loadProjectCars(); // Load cars after project data is loaded
      }
    } catch (error) {
      console.error('Error loading project data:', error);
    }
  };

  // Open Task List Edit Modal
  const openTaskListEditModal = (turbineId) => {
    const turbine = turbines.find(t => t.id === turbineId || t.turbine_number === turbineId);
    console.log('openTaskListEditModal - turbineId param:', turbineId, 'found turbine:', turbine);

    if (!turbine || !turbine.task_list_data) {
      alert('No task list assigned to this turbine');
      return;
    }

    const taskListData = turbine.task_list_data;
    const project = projects.find(p => p.id.toString() === selectedProject);

    // For default task lists, create a copy with project name suffix
    // For non-default, use "(edited)" suffix
    const newName = taskListData.is_default
      ? `${taskListData.name} - ${project?.name || 'Project'}`
      : `${taskListData.name} (edited)`;

    // Store the actual turbine.db_id (database ID) and turbine_number for display
    setEditingTaskListData({
      originalId: taskListData.id,
      turbineDbId: turbine.db_id,  // Actual database ID of ProjectTurbine
      turbineNumber: turbine.turbine_number,  // Display number (T1, T2, etc.)
      isDefault: taskListData.is_default
    });
    console.log('editingTaskListData set with turbineDbId:', turbine.db_id, 'turbineNumber:', turbine.turbine_number);

    setTaskListFormData({
      name: newName,
      description: taskListData.description || '',
      is_default: false,
      project: parseInt(selectedProject),
      categories: taskListData.categories?.length > 0
        ? taskListData.categories.map(cat => ({
            name: cat.name,
            color: cat.color || '#3B82F6',
            subcategories: cat.subcategories?.length > 0
              ? cat.subcategories.map(sub => ({
                  name: sub.name,
                  description: sub.description || '',
                  estimated_hours: sub.estimated_hours || ''
                }))
              : [{ name: '', description: '', estimated_hours: '' }]
          }))
        : [{ name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
    });

    setExpandedTaskListCategories(new Set());
    setEditingTaskListCategory(null);
    setShowTaskListEditModal(true);
  };

  // Save Task List (create copy)
  const handleSaveTaskListEdit = async () => {
    if (!taskListFormData.name.trim()) {
      alert('Please enter a task list name');
      return;
    }

    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    // Filter out empty categories and subcategories
    const cleanedCategories = taskListFormData.categories
      .filter(cat => cat.name.trim())
      .map(cat => ({
        name: cat.name.trim(),
        color: cat.color || '#3B82F6',
        subcategories: cat.subcategories
          .filter(sub => sub.name.trim())
          .map(sub => ({
            name: sub.name.trim(),
            description: sub.description || '',
            estimated_hours: sub.estimated_hours || null
          }))
      }));

    const payload = {
      name: taskListFormData.name.trim(),
      description: taskListFormData.description.trim(),
      is_default: false,
      project: parseInt(selectedProject),
      categories: cleanedCategories
    };

    try {
      // Create new task list
      console.log('Creating new task list with payload:', payload);
      const response = await axios.post(`${API_BASE_URL}/api/task-lists/`, payload, { headers });
      const newTaskListId = response.data.id;
      console.log('New task list created with ID:', newTaskListId);

      // Update the turbine to use the new task list
      const turbineDbId = editingTaskListData?.turbineDbId;
      const turbineNumber = editingTaskListData?.turbineNumber;
      console.log('Turbine DB ID to update:', turbineDbId, 'Turbine number:', turbineNumber);

      if (turbineDbId) {
        console.log(`Updating turbine ${turbineDbId} (T${turbineNumber}) with new task_list: ${newTaskListId}`);
        await axios.patch(
          `${API_BASE_URL}/api/project-turbines/${turbineDbId}/`,
          { task_list: newTaskListId },
          { headers }
        );
        console.log('Turbine updated successfully');
      } else {
        console.error('No turbine DB ID found in editingTaskListData:', editingTaskListData);
      }

      // Close both modals
      setShowTaskListEditModal(false);
      setEditingTaskListData(null);
      setShowQuickAddModal(false);
      setQuickAddStep(1);
      setQuickAddSelectedTasks([]);
      setQuickAddTaskTimes([]);
      setQuickAddPeople([]);
      setQuickAddCar(null);

      // Reload project data to get updated task lists
      await loadProjectData();

      alert(`Task list "${taskListFormData.name}" saved and assigned to turbine T${turbineNumber} successfully!`);
    } catch (error) {
      console.error('Error saving task list:', error);
      const errorMsg = error.response?.data?.name?.[0] || error.response?.data?.detail || error.message;
      alert('Error saving task list: ' + errorMsg);
    }
  };

  // Task List Category handlers
  const addTaskListCategory = () => {
    setTaskListFormData(prev => ({
      ...prev,
      categories: [...prev.categories, { name: '', color: '#3B82F6', subcategories: [{ name: '', description: '', estimated_hours: '' }] }]
    }));
  };

  const removeTaskListCategory = (categoryIndex) => {
    if (taskListFormData.categories.length <= 1) return;
    setTaskListFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, idx) => idx !== categoryIndex)
    }));
  };

  const toggleTaskListCategoryExpansion = (categoryIndex) => {
    setExpandedTaskListCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryIndex)) {
        newSet.delete(categoryIndex);
      } else {
        newSet.add(categoryIndex);
      }
      return newSet;
    });
  };

  const loadTaskAssignments = async () => {
    if (!selectedProject || !selectedDate) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      // Load task assignments for this project and date
      const assignmentsResponse = await axios.get(
        `${API_BASE_URL}/api/task-assignments/?project=${selectedProject}&date=${format(selectedDate, 'yyyy-MM-dd')}`,
        { headers }
      );

      // Get project data for fallback
      const project = projects.find(p => p.id.toString() === selectedProject);

      // Convert backend format to frontend format and group by turbine
      const groupedAssignments = {};
      assignmentsResponse.data.forEach(assignment => {
        // Find turbine data for this assignment (to get model and power_rating)
        // assignment.turbine = ProjectTurbine FK ID, assignment.turbine_id = turbine_number
        const turbineData = turbines.find(t => t.id === assignment.turbine || t.turbine_number === assignment.turbine_id);

        const frontendAssignment = {
          id: assignment.id,
          turbineId: assignment.turbine_id,
          turbineFk: assignment.turbine, // ProjectTurbine FK ID for accurate copying
          turbineName: `T${assignment.turbine_id}`,
          category: assignment.category,
          categoryColor: assignment.category_color,
          subcategory: assignment.subcategory_name || assignment.subcategory,
          subcategory_id: assignment.subcategory,
          startTime: assignment.start_time,
          endTime: assignment.end_time,
          peopleNeeded: assignment.people_needed,
          assignedPeople: assignment.assigned_people,
          individual_times: assignment.individual_times || {},
          assignedCar: assignment.assigned_car,
          date: assignment.date,
          is_completed: assignment.is_completed,
          completed_at: assignment.completed_at,
          completed_by: assignment.completed_by,
          completion_notes: assignment.completion_notes,
          isAlternative: assignment.is_alternative || false,
          // Add turbine-specific model/power_rating
          turbineModel: turbineData?.model || project?.model || '',
          powerRating: turbineData?.power_rating || project?.power_rating || '',
          turbineType: turbineData?.type || project?.type_of_turbine || ''
        };

        if (!groupedAssignments[assignment.turbine_id]) {
          groupedAssignments[assignment.turbine_id] = [];
        }
        groupedAssignments[assignment.turbine_id].push(frontendAssignment);
      });

      setTurbineAssignments(groupedAssignments);

      // Auto-select turbines that have assignments
      const turbinesWithAssignments = new Set(
        assignmentsResponse.data.map(assignment => assignment.turbine_id)
      );

      if (turbinesWithAssignments.size > 0) {
        setSelectedTurbines(prev => {
          const newSet = new Set(prev);
          turbinesWithAssignments.forEach(turbineId => {
            newSet.add(turbineId);
          });
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error loading task assignments:', error);
      // Continue without assignments if endpoint doesn't exist yet
      setTurbineAssignments({});
    }
  };

  // Load all assignments for selected project (for Project Overview - all dates)
  const loadAllProjectAssignments = async () => {
    if (!selectedProject) return;

    setLoadingProjectOverview(true);
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      // Load task assignments for selected project (all dates)
      const assignmentsResponse = await axios.get(
        `${API_BASE_URL}/api/task-assignments/?project=${selectedProject}`,
        { headers }
      );

      // Convert backend format to frontend format and group by turbine
      const groupedAssignments = {};
      const project = projects.find(p => p.id.toString() === selectedProject);

      assignmentsResponse.data.forEach(assignment => {
        // Find turbine data for this assignment (to get model and power_rating)
        // assignment.turbine = ProjectTurbine FK ID, assignment.turbine_id = turbine_number
        const turbineData = turbines.find(t => t.id === assignment.turbine || t.turbine_number === assignment.turbine_id);

        const frontendAssignment = {
          id: assignment.id,
          turbineId: assignment.turbine_id,
          turbineFk: assignment.turbine, // ProjectTurbine FK ID for accurate copying
          turbineName: `T${assignment.turbine_id}`,
          category: assignment.category,
          categoryColor: assignment.category_color,
          subcategory: assignment.subcategory_name || assignment.subcategory,
          subcategory_id: assignment.subcategory,
          startTime: assignment.start_time,
          endTime: assignment.end_time,
          peopleNeeded: assignment.people_needed,
          assignedPeople: assignment.assigned_people,
          individual_times: assignment.individual_times || {},
          assignedCar: assignment.assigned_car,
          date: assignment.date,
          is_completed: assignment.is_completed,
          completed_at: assignment.completed_at,
          completed_by: assignment.completed_by,
          completion_notes: assignment.completion_notes,
          isAlternative: assignment.is_alternative || false,
          projectId: assignment.project,
          projectName: project?.name || 'Unknown Project',
          // Use turbine-specific model/power_rating if available, fallback to project level
          turbineModel: turbineData?.model || project?.model || 'Unknown Model',
          powerRating: turbineData?.power_rating || project?.power_rating || '',
          turbineType: turbineData?.type || project?.type_of_turbine || ''
        };

        if (!groupedAssignments[assignment.turbine_id]) {
          groupedAssignments[assignment.turbine_id] = [];
        }
        groupedAssignments[assignment.turbine_id].push(frontendAssignment);
      });

      setAllProjectAssignments(groupedAssignments);
    } catch (error) {
      console.error('Error loading project assignments:', error);
      setAllProjectAssignments({});
    } finally {
      setLoadingProjectOverview(false);
    }
  };

  const loadProjectCars = async () => {
    if (!selectedProject || !selectedDate) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      // Load all cars and vehicle assignments (same as daily-planning)
      const [carsResponse, vehicleAssignmentsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/cars/`, { headers }),
        axios.get(`${API_BASE_URL}/api/vehicle-assignments/`, { headers })
      ]);

      setAllCars(carsResponse.data);
      setVehicleAssignments(vehicleAssignmentsResponse.data);

      const dateString = format(selectedDate, 'yyyy-MM-dd');

      // Get people assigned to this project on this date
      // Note: projectAssignments are already filtered by backend (project + date)
      const projectPeopleIds = projectAssignments.map(assignment => assignment.user);

      // Get cars from two sources:
      // 1. Cars with actual task assignments on this date
      // 2. Cars available from vehicle-assignments for this project on this date

      // Get all assignments for the selected date from turbineAssignments
      const allTaskAssignments = [];
      Object.keys(turbineAssignments).forEach(turbineId => {
        turbineAssignments[turbineId].forEach(assignment => {
          if (assignment.date === dateString && assignment.assignedCar) {
            allTaskAssignments.push(assignment);
          }
        });
      });

      // Get unique car IDs that have tasks today
      const carIdsWithTasks = [...new Set(allTaskAssignments.map(assignment => assignment.assignedCar))];

      // Get cars available from vehicle-assignments for this project
      const carsFromVehicleAssignments = vehicleAssignmentsResponse.data
        .filter(assignment => {
          // Must be assigned to this project
          if (assignment.project !== parseInt(selectedProject)) return false;

          // Must have active status (not returned)
          if (assignment.status !== 'assigned') return false;

          // Check if the assignment covers the selected date (compare only dates, not times)
          const assignedDate = new Date(assignment.assigned_date);
          const returnDate = assignment.expected_return_date ? new Date(assignment.expected_return_date) : null;
          const selectedDateObj = new Date(dateString);

          // Normalize dates to remove time component for comparison
          const assignedDateOnly = new Date(assignedDate.getFullYear(), assignedDate.getMonth(), assignedDate.getDate());
          const returnDateOnly = returnDate ? new Date(returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate()) : null;
          const selectedDateOnly = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());

          // Assignment must be active on the selected date
          if (returnDateOnly) {
            return selectedDateOnly >= assignedDateOnly && selectedDateOnly <= returnDateOnly;
          } else {
            // If no return date, assume it's active from assigned date onward
            return selectedDateOnly >= assignedDateOnly;
          }
        })
        .map(assignment => assignment.car);

      // Combine both sources and remove duplicates
      const allAvailableCarIds = [...new Set([...carIdsWithTasks, ...carsFromVehicleAssignments])];

      console.log('DEBUG loadProjectCars:', {
        dateString,
        allTaskAssignments: allTaskAssignments.length,
        carIdsWithTasks,
        carsFromVehicleAssignments,
        allAvailableCarIds,
        allCarsCount: carsResponse.data.length
      });

      // Find the corresponding car objects
      const uniqueCarsForProject = allAvailableCarIds.map(carId => {
        const car = carsResponse.data.find(c => c.id === carId);
        if (car) {
          // Find a vehicle assignment for this car and project (for compatibility)
          const vehicleAssignment = vehicleAssignmentsResponse.data.find(assignment =>
            assignment.car === carId && assignment.project === parseInt(selectedProject)
          );

          return {
            ...car,
            assignmentId: vehicleAssignment?.id || null,
            assignedUser: vehicleAssignment?.user || null,
            hasTaskAssignment: carIdsWithTasks.includes(carId) // Track if car has actual tasks
          };
        } else {
          console.warn(`Car with ID ${carId} not found in cars data`);
          return null;
        }
      }).filter(Boolean);

      console.log('Final cars for project:', uniqueCarsForProject);
      setProjectCars(uniqueCarsForProject);
    } catch (error) {
      console.error('Error loading cars:', error);
      // Continue without cars if endpoint doesn't exist yet
      setProjectCars([]);
      setAllCars([]);
      setVehicleAssignments([]);
    }
  };

  const handleDateChange = (direction) => {
    if (direction === 'prev') {
      setSelectedDate(prev => subDays(prev, 1));
    } else {
      setSelectedDate(prev => addDays(prev, 1));
    }
  };

  // Get people assigned to the selected project for the selected date
  // Note: projectAssignments are already filtered by backend (project + date)
  const getProjectPeopleForDate = () => {
    if (!projectAssignments || !allPeople) return [];

    return projectAssignments.map(assignment => {
      const person = allPeople.find(u => u.id === assignment.user);

      // Guard against missing person data
      if (!person) {
        console.warn('Person not found for assignment:', assignment);
        return null;
      }

      return {
        ...person,
        assignmentId: assignment.id,
        role: assignment.role || 'Team Member'
      };
    }).filter(Boolean); // Remove any null/undefined entries
  };

  const projectPeople = getProjectPeopleForDate();

  // Handle turbine selection
  const toggleTurbineSelection = (turbineId) => {
    setSelectedTurbines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(turbineId)) {
        newSet.delete(turbineId);
      } else {
        newSet.add(turbineId);
      }
      return newSet;
    });
  };

  // Task assignment functions
  const handleAssignTaskToTurbine = (task, subcategory, turbineId) => {
    setSelectedTaskForAssign({ task, subcategory });
    setSelectedTurbineForAssign(turbineId);
    setEditingAssignmentId(null); // Clear editing state for new assignment
    setTaskAssignData({
      startTime: '08:00',
      endTime: '16:00',
      peopleNeeded: 2,
      assignedPeople: [],
      assignedCar: null,
      isAlternative: false,
      individualTimes: {}
    });
    setShowTaskAssignModal(true);
  };

  // Drag & Drop handlers for subcategories
  const handleDragStart = (e, category, subcategory) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      type: 'subcategory',
      category: category,
      subcategory: subcategory
    }));
    e.dataTransfer.effectAllowed = 'copy';

    // Add visual feedback
    e.target.style.opacity = '0.5';

    // Create custom drag image
    const dragText = `📋 ${subcategory.name}`;
    const dragElement = document.createElement('div');
    dragElement.textContent = dragText;
    dragElement.style.cssText = 'position: absolute; top: -1000px; background: white; border: 1px solid #ccc; padding: 4px 8px; border-radius: 4px; font-size: 12px;';
    document.body.appendChild(dragElement);
    e.dataTransfer.setDragImage(dragElement, 0, 0);

    setTimeout(() => document.body.removeChild(dragElement), 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    e.currentTarget.style.borderColor = '#3B82F6';
    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.borderColor = '';
    e.currentTarget.style.backgroundColor = '';
  };

  const handleDrop = (e, turbineId) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '';
    e.currentTarget.style.backgroundColor = '';

    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.type === 'subcategory') {
        handleAssignTaskToTurbine(data.category, data.subcategory, turbineId);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  // Export functions
  const generateExportData = () => {
    const projectName = projects.find(p => p.id === parseInt(selectedProject))?.name || 'Unknown Project';
    const dateStr = format(selectedDate, 'EEE, MMM d, yyyy');

    // Get all assignments for the day from API data
    const allAssignments = getAllAssignments();

    // Group by turbine
    const turbineGroups = {};

    // First add all active turbines (even if they have no assignments)
    activeTurbines.forEach(turbine => {
      turbineGroups[turbine.id] = {
        name: `${turbine.type} Number ${turbine.id}`,
        assignments: []
      };
    });

    // Then add assignments to their respective turbines
    allAssignments.forEach(assignment => {
      if (turbineGroups[assignment.turbineId]) {
        turbineGroups[assignment.turbineId].assignments.push(assignment);
      } else {
        // If turbine not in active turbines, create entry for it
        turbineGroups[assignment.turbineId] = {
          name: assignment.turbineName || `Turbine ${assignment.turbineId}`,
          assignments: [assignment]
        };
      }
    });

    return {
      projectName,
      dateStr,
      turbineGroups,
      totalTurbines: Object.keys(turbineGroups).length,
      totalAssignments: allAssignments.length,
      timeRange: `${timelineStartTime} - ${timelineEndTime}`
    };
  };

  const generateExportText = () => {
    const data = generateExportData();
    const allAssignments = getAllAssignments();

    let text = `${data.projectName} - ${data.dateStr}\n`;
    text += `Time Range: ${data.timeRange} | Tasks: ${allAssignments.length}\n`;
    text += `===============================================================================\n\n`;

    if (allAssignments.length === 0) {
      text += `No tasks scheduled for this date.\n\n`;
    } else {
      // SECTION 1: Tasks with individual times per person
      text += `TASK DETAILS\n`;
      text += `${'-'.repeat(80)}\n`;

      // Group by turbine
      const turbineGroups = {};
      allAssignments.forEach(assignment => {
        const turbineKey = assignment.turbineId;
        if (!turbineGroups[turbineKey]) {
          turbineGroups[turbineKey] = [];
        }
        turbineGroups[turbineKey].push(assignment);
      });

      Object.keys(turbineGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(turbineId => {
        text += `\nT${turbineId}\n`;

        turbineGroups[turbineId].forEach(assignment => {
          text += `  ${assignment.category} > ${assignment.subcategory}\n`;

          if (assignment.assignedPeople && assignment.assignedPeople.length > 0) {
            assignment.assignedPeople.forEach(personId => {
              const person = allPeople.find(p => p.id === personId);
              const personName = person
                ? `${person.last_name} ${person.first_name}`
                : `ID:${personId}`;

              // Get individual time or default
              const individualTimes = assignment.individual_times?.[personId];
              const startTime = individualTimes?.start_time || assignment.startTime;
              const endTime = individualTimes?.end_time || assignment.endTime;

              text += `    • ${startTime}-${endTime} | ${personName}\n`;
            });
          } else {
            text += `    • ${assignment.startTime}-${assignment.endTime} | No personnel assigned\n`;
          }

          if (assignment.assignedCar) {
            const car = projectCars.find(c => c.id === assignment.assignedCar);
            if (car) {
              text += `    🚗 ${car.plate_number} (${car.car_brand} ${car.car_model})\n`;
            }
          }
        });
      });

      // SECTION 2: Personnel Schedule (grouped by person)
      text += `\n\n`;
      text += `PERSONNEL SCHEDULE\n`;
      text += `${'-'.repeat(80)}\n`;

      // Build schedule per person
      const personSchedules = {};

      allAssignments.forEach(assignment => {
        assignment.assignedPeople?.forEach(personId => {
          if (!personSchedules[personId]) {
            personSchedules[personId] = [];
          }

          const individualTimes = assignment.individual_times?.[personId];
          const startTime = individualTimes?.start_time || assignment.startTime;
          const endTime = individualTimes?.end_time || assignment.endTime;

          personSchedules[personId].push({
            startTime,
            endTime,
            turbineId: assignment.turbineId,
            task: assignment.subcategory,
            category: assignment.category
          });
        });
      });

      // Sort and display each person's schedule
      Object.keys(personSchedules).forEach(personId => {
        const person = allPeople.find(p => p.id === parseInt(personId));
        const personName = person
          ? `${person.last_name} ${person.first_name}`
          : `ID:${personId}`;

        // Sort by start time
        const schedule = personSchedules[personId].sort((a, b) =>
          a.startTime.localeCompare(b.startTime)
        );

        // Calculate total hours
        let totalMinutes = 0;
        schedule.forEach(task => {
          const [startH, startM] = task.startTime.split(':').map(Number);
          const [endH, endM] = task.endTime.split(':').map(Number);
          totalMinutes += (endH * 60 + endM) - (startH * 60 + startM);
        });
        const totalHours = Math.floor(totalMinutes / 60);
        const totalMins = totalMinutes % 60;

        text += `\n${personName} (${totalHours}h${totalMins > 0 ? ` ${totalMins}m` : ''} total)\n`;

        schedule.forEach(task => {
          text += `  ${task.startTime}-${task.endTime} | T${task.turbineId} - ${task.task}\n`;
        });
      });

      // Summary stats
      const activePeople = Object.keys(personSchedules).length;
      text += `\n${'-'.repeat(80)}\n`;
      text += `Summary: ${activePeople} personnel | ${allAssignments.length} tasks\n`;
    }

    text += `\n\nGenerated: ${format(new Date(), 'HH:mm dd/MM/yyyy')} | Ventro Planning System`;

    return text;
  };

  const handleSaveTaskAssignment = async () => {
    if (!selectedTaskForAssign || !selectedTurbineForAssign) return;

    // Check for time conflicts for assigned people (using individual times)
    const conflictingPeople = [];
    const allAssignments = getAllAssignments();

    taskAssignData.assignedPeople.forEach(personId => {
      const person = allPeople.find(p => p.id === personId);
      const conflicts = [];

      // Get this person's individual time for the new task
      const personNewTime = taskAssignData.individualTimes?.[personId] || {
        startTime: taskAssignData.startTime,
        endTime: taskAssignData.endTime
      };
      const newStart = timeToMinutes(personNewTime.startTime);
      const newEnd = timeToMinutes(personNewTime.endTime);

      // Check all existing assignments for this person
      allAssignments.forEach(assignment => {
        // Skip the current assignment if we're editing it
        if (editingAssignmentId && assignment.id === editingAssignmentId) return;

        if (assignment.assignedPeople && assignment.assignedPeople.includes(personId)) {
          // Get this person's individual time in the existing assignment
          const existingIndividualTime = assignment.individual_times?.[personId];
          const existingStart = timeToMinutes(existingIndividualTime?.start_time || assignment.startTime);
          const existingEnd = timeToMinutes(existingIndividualTime?.end_time || assignment.endTime);

          // Check if there's an overlap
          if (!(newEnd <= existingStart || newStart >= existingEnd)) {
            const displayStart = existingIndividualTime?.start_time || assignment.startTime;
            const displayEnd = existingIndividualTime?.end_time || assignment.endTime;
            conflicts.push({
              turbine: `T${assignment.turbineId}`,
              task: assignment.subcategory,
              time: `${displayStart}-${displayEnd}`
            });
          }
        }
      });

      if (conflicts.length > 0) {
        conflictingPeople.push({
          person: person?.first_name && person?.last_name
            ? `${person.last_name} ${person.first_name}`
            : person?.username || 'Unknown',
          personTime: `${personNewTime.startTime}-${personNewTime.endTime}`,
          conflicts
        });
      }
    });

    // If there are conflicts, show alert
    if (conflictingPeople.length > 0) {
      let conflictMessage = "⚠️ TIME CONFLICT DETECTED!\n\n";

      conflictingPeople.forEach(({ person, personTime, conflicts }) => {
        conflictMessage += `👤 ${person} (${personTime}):\n`;
        conflicts.forEach(conflict => {
          conflictMessage += `   • ${conflict.turbine}: ${conflict.task} (${conflict.time})\n`;
        });
        conflictMessage += "\n";
      });

      conflictMessage += "Do you want to proceed anyway?";

      const proceed = window.confirm(conflictMessage);
      if (!proceed) {
        return; // Don't save if user cancels
      }
    }

    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Convert individual times to backend format
    const individualTimesForBackend = {};
    taskAssignData.assignedPeople.forEach(personId => {
      const times = taskAssignData.individualTimes?.[personId];
      if (times) {
        individualTimesForBackend[personId] = {
          start_time: times.startTime,
          end_time: times.endTime
        };
      } else {
        individualTimesForBackend[personId] = {
          start_time: taskAssignData.startTime,
          end_time: taskAssignData.endTime
        };
      }
    });

    const newAssignment = {
      project: parseInt(selectedProject),
      turbine_id: selectedTurbineForAssign,
      subcategory_id: selectedTaskForAssign.subcategory.id,
      start_time: taskAssignData.startTime,
      end_time: taskAssignData.endTime,
      people_needed: taskAssignData.peopleNeeded,
      assigned_people: taskAssignData.assignedPeople,
      assigned_car: taskAssignData.assignedCar,
      is_alternative: taskAssignData.isAlternative,
      date: format(selectedDate, 'yyyy-MM-dd'),
      individual_times: individualTimesForBackend
    };

    try {
      let savedAssignment;

      if (editingAssignmentId) {
        // Update existing assignment
        const response = await axios.put(`${API_BASE_URL}/api/task-assignments/${editingAssignmentId}/`, newAssignment, { headers });
        savedAssignment = response.data;
      } else {
        // Create new assignment
        const response = await axios.post(`${API_BASE_URL}/api/task-assignments/`, newAssignment, { headers });
        savedAssignment = response.data;
      }

      setShowTaskAssignModal(false);
      setSelectedTaskForAssign(null);
      setSelectedTurbineForAssign(null);
      setEditingAssignmentId(null);

      // Reload assignments from backend to ensure sync
      await loadTaskAssignments();
      // Refresh all project assignments for experience tracking
      loadAllProjectAssignments();
    } catch (error) {
      console.error('Error saving task assignment:', error);
      alert('Error saving assignment. Please try again.');
    }
  };

  const handlePersonToggle = (personId) => {
    setTaskAssignData(prev => {
      const isRemoving = prev.assignedPeople.includes(personId);

      if (isRemoving) {
        // Remove person and their individual times
        const newIndividualTimes = { ...prev.individualTimes };
        delete newIndividualTimes[personId];
        return {
          ...prev,
          assignedPeople: prev.assignedPeople.filter(id => id !== personId),
          individualTimes: newIndividualTimes
        };
      } else {
        // Add person - calculate smart start time based on their schedule
        const personSchedule = getPersonSchedule(personId);
        let smartStartTime = prev.startTime;
        let smartEndTime = prev.endTime;

        if (personSchedule.length > 0) {
          // Find the latest end time from their schedule
          const latestEndTime = personSchedule.reduce((latest, task) => {
            return task.endTime > latest ? task.endTime : latest;
          }, '00:00');

          // If their last task ends after the default start, use that as start
          if (latestEndTime > prev.startTime) {
            smartStartTime = latestEndTime;
            // Make sure end time is after start time
            if (smartEndTime <= smartStartTime) {
              // Add 2 hours to start time as default duration
              const [h, m] = smartStartTime.split(':').map(Number);
              const endHour = Math.min(h + 2, 23);
              smartEndTime = `${endHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            }
          }
        }

        return {
          ...prev,
          assignedPeople: [...prev.assignedPeople, personId],
          individualTimes: {
            ...prev.individualTimes,
            [personId]: {
              startTime: smartStartTime,
              endTime: smartEndTime
            }
          }
        };
      }
    });
  };

  // Check if person has conflicts with selected time range
  const hasTimeConflict = (personId, startTime, endTime) => {
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = parseInt(endTime.split(':')[0]);

    const allAssignments = getAllAssignments();
    return allAssignments.some(assignment => {
      if (assignment.assignedPeople.includes(personId)) {
        const assignmentStartHour = parseInt(assignment.startTime.split(':')[0]);
        const assignmentEndHour = parseInt(assignment.endTime.split(':')[0]);

        // Check if there's an overlap
        return !(endHour <= assignmentStartHour || startHour >= assignmentEndHour);
      }
      return false;
    });
  };

  // Get conflict details for person
  const getPersonConflicts = (personId, startTime, endTime) => {
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = parseInt(endTime.split(':')[0]);

    const allAssignments = getAllAssignments();
    return allAssignments.filter(assignment => {
      if (assignment.assignedPeople.includes(personId)) {
        const assignmentStartHour = parseInt(assignment.startTime.split(':')[0]);
        const assignmentEndHour = parseInt(assignment.endTime.split(':')[0]);

        return !(endHour <= assignmentStartHour || startHour >= assignmentEndHour);
      }
      return false;
    });
  };

  // Filter people based on search term
  const filteredPeople = projectPeople.filter(person => {
    const fullName = person.first_name && person.last_name
      ? `${person.last_name} ${person.first_name}`.toLowerCase()
      : person.username.toLowerCase();
    return fullName.includes(peopleSearchTerm.toLowerCase()) ||
           person.role.toLowerCase().includes(peopleSearchTerm.toLowerCase());
  });

  const getAssignmentsForTurbine = (turbineId) => {
    const assignments = turbineAssignments[turbineId] || [];
    // Filter assignments by selected date and sort by start time
    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
    return assignments
      .filter(assignment => assignment.date === selectedDateString)
      .sort((a, b) => {
        return a.startTime.localeCompare(b.startTime);
      });
  };

  // Get available people for selected task time slot
  const getAvailablePeopleForTask = (task) => {
    if (!task) return projectPeople;

    const taskStartHour = parseInt(task.startTime.split(':')[0]);
    const taskEndHour = parseInt(task.endTime.split(':')[0]);

    return projectPeople.filter(person => {
      // First, check if person is already assigned to this task
      if (task.assignedPeople && task.assignedPeople.includes(person.id)) {
        return false; // Don't show already assigned people
      }

      // Check if person has any conflicting assignments
      const allAssignments = getAllAssignments();
      const hasConflict = allAssignments.some(assignment => {
        if (assignment.id === task.id) return false; // Ignore current task
        if (!assignment.assignedPeople.includes(person.id)) return false;

        const assignmentStartHour = parseInt(assignment.startTime.split(':')[0]);
        const assignmentEndHour = parseInt(assignment.endTime.split(':')[0]);

        // Check for time overlap
        return !(taskEndHour <= assignmentStartHour || taskStartHour >= assignmentEndHour);
      });

      return !hasConflict;
    });
  };

  // Check if car has time conflict
  const hasCarTimeConflict = (carId, startTime, endTime) => {
    if (!carId) return false;

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    // Check all assignments for this date
    const allAssignments = getAllAssignments();

    return allAssignments.some(assignment => {
      // Skip if no car assigned or different car
      if (!assignment.assignedCar || assignment.assignedCar !== carId) return false;

      // Skip if this is the assignment we're editing
      if (editingAssignmentId && assignment.id === editingAssignmentId) return false;

      const assignmentStart = timeToMinutes(assignment.startTime);
      const assignmentEnd = timeToMinutes(assignment.endTime);

      // Check for time overlap
      return (startMinutes < assignmentEnd && endMinutes > assignmentStart);
    });
  };

  // Get car conflicts for time range
  const getCarConflicts = (carId, startTime, endTime) => {
    if (!carId) return [];

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    const allAssignments = getAllAssignments();

    return allAssignments.filter(assignment => {
      // Only show assignments with the same car
      if (!assignment.assignedCar || assignment.assignedCar !== carId) return false;

      // Skip if this is the assignment we're editing
      if (editingAssignmentId && assignment.id === editingAssignmentId) return false;

      const assignmentStart = timeToMinutes(assignment.startTime);
      const assignmentEnd = timeToMinutes(assignment.endTime);

      // Check for time overlap
      return (startMinutes < assignmentEnd && endMinutes > assignmentStart);
    });
  };

  // Calculate person utilization percentage for the selected date
  const getPersonUtilization = (personId) => {
    // Calculate total available working hours based on timeline
    const timelineStart = timeToMinutes(timelineStartTime);
    const timelineEnd = timeToMinutes(timelineEndTime);
    const totalAvailableMinutes = timelineEnd - timelineStart;

    if (totalAvailableMinutes <= 0) return 0;

    // Get all assignments for this person on the selected date
    const allAssignments = getAllAssignments();

    // Calculate total assigned time for this person
    let totalAssignedMinutes = 0;

    // Track time intervals to handle overlapping assignments
    const timeIntervals = [];

    allAssignments.forEach(assignment => {
      if (assignment.assignedPeople && assignment.assignedPeople.includes(personId)) {
        // Check if person has individual times for this assignment
        const individualTimes = assignment.individual_times?.[personId];

        if (individualTimes) {
          // Use individual times if available
          const startMinutes = timeToMinutes(individualTimes.start_time);
          const endMinutes = timeToMinutes(individualTimes.end_time);
          timeIntervals.push({ start: startMinutes, end: endMinutes });
        } else {
          // Use assignment default times
          const startMinutes = timeToMinutes(assignment.startTime);
          const endMinutes = timeToMinutes(assignment.endTime);
          timeIntervals.push({ start: startMinutes, end: endMinutes });
        }
      }
    });

    // Merge overlapping intervals to avoid double-counting
    if (timeIntervals.length === 0) return 0;

    // Sort intervals by start time
    timeIntervals.sort((a, b) => a.start - b.start);

    // Merge overlapping intervals
    const mergedIntervals = [timeIntervals[0]];
    for (let i = 1; i < timeIntervals.length; i++) {
      const current = timeIntervals[i];
      const lastMerged = mergedIntervals[mergedIntervals.length - 1];

      if (current.start <= lastMerged.end) {
        // Overlapping - merge by extending the end time
        lastMerged.end = Math.max(lastMerged.end, current.end);
      } else {
        // Non-overlapping - add as new interval
        mergedIntervals.push(current);
      }
    }

    // Calculate total minutes from merged intervals
    totalAssignedMinutes = mergedIntervals.reduce((total, interval) => {
      return total + (interval.end - interval.start);
    }, 0);

    // Calculate percentage
    const percentage = Math.min(100, Math.round((totalAssignedMinutes / totalAvailableMinutes) * 100));

    return percentage;
  };

  // Get person's schedule for the selected date (list of assignments with times)
  const getPersonSchedule = (personId) => {
    const allAssignments = getAllAssignments();
    const schedule = [];

    allAssignments.forEach(assignment => {
      if (assignment.assignedPeople && assignment.assignedPeople.includes(personId)) {
        const individualTimes = assignment.individual_times?.[personId];
        const startTime = individualTimes?.start_time || assignment.startTime;
        const endTime = individualTimes?.end_time || assignment.endTime;

        schedule.push({
          id: assignment.id,
          turbineId: assignment.turbineId,
          subcategory: assignment.subcategory,
          category: assignment.category,
          startTime,
          endTime,
          categoryColor: assignment.categoryColor
        });
      }
    });

    // Sort by start time
    schedule.sort((a, b) => {
      const timeA = timeToMinutes(a.startTime);
      const timeB = timeToMinutes(b.startTime);
      return timeA - timeB;
    });

    return schedule;
  };

  // Calculate car utilization percentage for the selected date
  const getCarUtilization = (carId) => {
    const timelineStart = timeToMinutes(timelineStartTime);
    const timelineEnd = timeToMinutes(timelineEndTime);
    const totalAvailableMinutes = timelineEnd - timelineStart;

    if (totalAvailableMinutes <= 0) return 0;

    const allAssignments = getAllAssignments();
    const timeIntervals = [];

    allAssignments.forEach(assignment => {
      if (assignment.assignedCar === carId) {
        const startMinutes = timeToMinutes(assignment.startTime);
        const endMinutes = timeToMinutes(assignment.endTime);
        timeIntervals.push({ start: startMinutes, end: endMinutes });
      }
    });

    if (timeIntervals.length === 0) return 0;

    timeIntervals.sort((a, b) => a.start - b.start);

    const mergedIntervals = [timeIntervals[0]];
    for (let i = 1; i < timeIntervals.length; i++) {
      const current = timeIntervals[i];
      const lastMerged = mergedIntervals[mergedIntervals.length - 1];

      if (current.start <= lastMerged.end) {
        lastMerged.end = Math.max(lastMerged.end, current.end);
      } else {
        mergedIntervals.push(current);
      }
    }

    const totalAssignedMinutes = mergedIntervals.reduce((total, interval) => {
      return total + (interval.end - interval.start);
    }, 0);

    return Math.min(100, Math.round((totalAssignedMinutes / totalAvailableMinutes) * 100));
  };

  // Check if car is assigned at specific time slot
  const isCarAssignedAtTime = (carId, timeSlot) => {
    const allAssignments = getAllAssignments();

    return allAssignments.some(assignment => {
      if (!assignment.assignedCar || assignment.assignedCar !== carId) return false;

      const slotStart = timeToMinutes(timeSlot);
      const slotEnd = slotStart + 15; // Each slot is 15 minutes
      const assignmentStart = timeToMinutes(assignment.startTime);
      const assignmentEnd = timeToMinutes(assignment.endTime);

      // Handle overnight assignments (end time is next day)
      if (assignmentEnd < assignmentStart) {
        // Overnight shift - check if slot overlaps with either part
        return (slotStart < assignmentEnd + 1440) || (slotEnd > assignmentStart) ||
               (slotStart >= assignmentStart) || (slotEnd <= assignmentEnd);
      }

      // Normal assignment - check if there's any overlap between slot and assignment time ranges
      return slotStart < assignmentEnd && slotEnd > assignmentStart;
    });
  };

  // Get car assignment at specific time slot
  const getCarAssignmentAtTime = (carId, timeSlot) => {
    const allAssignments = getAllAssignments();

    return allAssignments.find(assignment => {
      if (!assignment.assignedCar || assignment.assignedCar !== carId) return false;

      const slotStart = timeToMinutes(timeSlot);
      const slotEnd = slotStart + 15; // Each slot is 15 minutes
      const assignmentStart = timeToMinutes(assignment.startTime);
      const assignmentEnd = timeToMinutes(assignment.endTime);

      // Handle overnight assignments (end time is next day)
      if (assignmentEnd < assignmentStart) {
        // Overnight shift - check if slot overlaps with either part
        return (slotStart < assignmentEnd + 1440) || (slotEnd > assignmentStart) ||
               (slotStart >= assignmentStart) || (slotEnd <= assignmentEnd);
      }

      // Normal assignment - check if there's any overlap between slot and assignment time ranges
      return slotStart < assignmentEnd && slotEnd > assignmentStart;
    });
  };

  // Generate timeline with 15-minute intervals (00:00 to 23:45)
  const generateTimeSlots = () => {
    const slots = [];
    // Generate all 24-hour slots in correct order
    for (let hour = 0; hour <= 23; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:15`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
      slots.push(`${hour.toString().padStart(2, '0')}:45`);
    }
    return slots;
  };



  const allTimeSlots = generateTimeSlots();

  // Filter timeline slots based on selected time range
  const getFilteredTimeSlots = () => {
    const startMinutes = timeToMinutes(timelineStartTime);
    const endMinutes = timeToMinutes(timelineEndTime);

    return allTimeSlots.filter(slot => {
      const slotMinutes = timeToMinutes(slot);

      // Handle overnight ranges (e.g., 22:00 to 06:00)
      if (endMinutes < startMinutes) {
        return slotMinutes >= startMinutes || slotMinutes <= endMinutes;
      }

      // Normal range (e.g., 07:00 to 19:00)
      return slotMinutes >= startMinutes && slotMinutes <= endMinutes;
    });
  };

  const timeSlots = getFilteredTimeSlots();

  // Get all assignments for timeline view
  const getAllAssignments = () => {
    const allAssignments = [];
    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
    Object.keys(turbineAssignments).forEach(turbineId => {
      turbineAssignments[turbineId].forEach(assignment => {
        // Only include assignments for the selected date
        if (assignment.date === selectedDateString) {
          allAssignments.push({
            ...assignment,
            turbineName: `T${turbineId}`
          });
        }
      });
    });
    return allAssignments;
  };

  // Get individual times for a person in an assignment
  const getIndividualTimes = (assignment, personId) => {
    if (assignment.individual_times && assignment.individual_times[personId]) {
      return {
        startTime: assignment.individual_times[personId].start_time,
        endTime: assignment.individual_times[personId].end_time
      };
    }
    // Fallback to assignment general times
    return {
      startTime: assignment.startTime,
      endTime: assignment.endTime
    };
  };

  // Check if person is assigned at specific time
  const isPersonAssignedAtTime = (personId, timeSlot) => {
    const allAssignments = getAllAssignments();

    return allAssignments.some(assignment => {
      if (!assignment.assignedPeople.includes(personId)) return false;

      const { startTime, endTime } = getIndividualTimes(assignment, personId);
      const slotStart = timeToMinutes(timeSlot);
      const slotEnd = slotStart + 15; // Each slot is 15 minutes
      const assignmentStart = timeToMinutes(startTime);
      const assignmentEnd = timeToMinutes(endTime);

      // Handle overnight assignments (end time is next day)
      if (assignmentEnd < assignmentStart) {
        // Overnight shift - check if slot overlaps with either part
        return (slotStart < assignmentEnd + 1440) || (slotEnd > assignmentStart) ||
               (slotStart >= assignmentStart) || (slotEnd <= assignmentEnd);
      }

      // Normal assignment - check if there's any overlap between slot and assignment time ranges
      return slotStart < assignmentEnd && slotEnd > assignmentStart;
    });
  };

  // Get assignment details for person at specific time
  const getPersonAssignmentAtTime = (personId, timeSlot) => {
    const allAssignments = getAllAssignments();

    return allAssignments.find(assignment => {
      if (!assignment.assignedPeople.includes(personId)) return false;

      const { startTime, endTime } = getIndividualTimes(assignment, personId);
      const slotStart = timeToMinutes(timeSlot);
      const slotEnd = slotStart + 15; // Each slot is 15 minutes
      const assignmentStart = timeToMinutes(startTime);
      const assignmentEnd = timeToMinutes(endTime);

      // Handle overnight assignments (end time is next day)
      if (assignmentEnd < assignmentStart) {
        // Overnight shift - check if slot overlaps with either part
        return (slotStart < assignmentEnd + 1440) || (slotEnd > assignmentStart) ||
               (slotStart >= assignmentStart) || (slotEnd <= assignmentEnd);
      }

      // Normal assignment - check if there's any overlap between slot and assignment time ranges
      return slotStart < assignmentEnd && slotEnd > assignmentStart;
    });
  };

  // Get all assignments (primary + alternative) for person at specific time
  const getAllPersonAssignmentsAtTime = (personId, timeSlot) => {
    const allAssignments = getAllAssignments();

    return allAssignments.filter(assignment => {
      if (!assignment.assignedPeople.includes(personId)) return false;

      const { startTime, endTime } = getIndividualTimes(assignment, personId);
      const slotStart = timeToMinutes(timeSlot);
      const slotEnd = slotStart + 15; // Each slot is 15 minutes
      const assignmentStart = timeToMinutes(startTime);
      const assignmentEnd = timeToMinutes(endTime);

      // Handle overnight assignments (end time is next day)
      if (assignmentEnd < assignmentStart) {
        // Overnight shift - check if slot overlaps with either part
        return (slotStart < assignmentEnd + 1440) || (slotEnd > assignmentStart) ||
               (slotStart >= assignmentStart) || (slotEnd <= assignmentEnd);
      }

      // Normal assignment - check if there's any overlap between slot and assignment time ranges
      return slotStart < assignmentEnd && slotEnd > assignmentStart;
    }).sort((a, b) => {
      // Sort: primary tasks first, then alternative tasks
      if (a.isAlternative === b.isAlternative) return 0;
      return a.isAlternative ? 1 : -1;
    });
  };

  // Drag and Drop Functions
  const handleTaskDragStart = (e, assignment, personId) => {
    setDraggedTask({ assignment, personId });
    setIsDragging(true);
    setDragStartX(e.clientX);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaskDragEnd = () => {
    setDraggedTask(null);
    setIsDragging(false);
    setDragStartX(0);
    setResizeHandle(null);
  };

  const calculateNewTime = (dragX, originalStartTime) => {
    const pixelDiff = dragX - dragStartX;
    const slotsDiff = Math.round(pixelDiff / 40); // 40px per slot
    const minutesDiff = slotsDiff * 15; // 15 minutes per slot

    const originalMinutes = timeToMinutes(originalStartTime);
    const newMinutes = originalMinutes + minutesDiff;

    // Ensure time stays within bounds (0-1440 minutes = 24 hours)
    const boundedMinutes = Math.max(0, Math.min(1440 - 15, newMinutes));
    return minutesToTime(boundedMinutes);
  };


  const handleTaskDrop = async (e, targetTimeSlot) => {
    e.preventDefault();

    if (!draggedTask) return;

    const { assignment } = draggedTask;
    const newStartTime = calculateNewTime(e.clientX, assignment.startTime);

    // Calculate duration and new end time
    const originalDuration = timeToMinutes(assignment.endTime) - timeToMinutes(assignment.startTime);
    const newEndTime = minutesToTime(timeToMinutes(newStartTime) + originalDuration);

    await updateTaskTime(assignment.id, newStartTime, newEndTime);
    handleTaskDragEnd();
  };

  const handleTimeSlotDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Move task functions
  const handleTaskMoveStart = (e, assignment, personId) => {
    e.preventDefault();
    e.stopPropagation();
    const { startTime: personStartTime, endTime: personEndTime } = getIndividualTimes(assignment, personId);
    console.log('Starting task move:', assignment.turbineName, personStartTime, '->', personEndTime);
    setDragMode('move');
    setDraggedTask({
      assignment: { ...assignment },
      personId: personId,
      originalStartTime: personStartTime,
      originalEndTime: personEndTime
    });
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handleCarTaskMoveStart = (e, assignment, carId) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Starting car task move:', assignment.turbineName, assignment.startTime, '->', assignment.endTime);
    setDragMode('move');
    setDraggedTask({
      assignment: { ...assignment },
      carId: carId,
      originalStartTime: assignment.startTime,
      originalEndTime: assignment.endTime
    });
    setIsDragging(true);
    setDragStartX(e.clientX);
  };

  const handleTaskMoveEnd = () => {
    setDraggedTask(null);
    setIsDragging(false);
    setDragStartX(0);
    setDragMode(null);
    setDropZone(null);
  };

  const moveTaskForPerson = async (assignment, personId, newStartTime, newEndTime) => {
    try {
      // Same logic as resize - split task if multiple people
      await resizeTaskForPerson(assignment, personId, newStartTime, newEndTime);
    } catch (error) {
      console.error('Error moving task:', error);
      alert('Failed to move task. Please try again.');
    }
  };

  const moveTaskForCar = async (assignment, newStartTime, newEndTime) => {
    try {
      // Same logic as resize for cars
      await resizeTaskForCar(assignment, newStartTime, newEndTime);
    } catch (error) {
      console.error('Error moving car task:', error);
      alert('Failed to move car task. Please try again.');
    }
  };

  // Mouse event handlers for resize and move
  const handleMouseMove = (e) => {
    if (!draggedTask || (!resizeHandle && dragMode !== 'move')) return;

    // Prevent default behavior to avoid conflicts
    e.preventDefault();

    const { originalStartTime, originalEndTime } = draggedTask;
    const pixelDiff = e.clientX - dragStartX;
    const slotsDiff = Math.round(pixelDiff / 40);
    const minutesDiff = slotsDiff * 15;

    let newStartTime = originalStartTime;
    let newEndTime = originalEndTime;

    if (dragMode === 'move') {
      // Move entire task (preserve duration)
      const originalStartMinutes = timeToMinutes(originalStartTime);
      const originalEndMinutes = timeToMinutes(originalEndTime);
      const duration = originalEndMinutes - originalStartMinutes;

      const newStartMinutes = Math.max(0, Math.min(1440 - duration, originalStartMinutes + minutesDiff));
      newStartTime = minutesToTime(newStartMinutes);
      newEndTime = minutesToTime(newStartMinutes + duration);

      // Detect drop zone for visual feedback
      const timelineElement = e.target.closest('.timeline-container');
      if (timelineElement) {
        const timeSlotElement = document.elementFromPoint(e.clientX, e.clientY);
        const personRow = timeSlotElement?.closest('[data-person-id]');
        const timeSlot = timeSlotElement?.getAttribute('data-time-slot');

        if (personRow && timeSlot) {
          const personId = parseInt(personRow.getAttribute('data-person-id'));
          setDropZone({ personId, timeSlot });
        } else {
          setDropZone(null);
        }
      }
    } else if (resizeHandle === 'left') {
      // Resize from left (change start time, keep end time fixed)
      const originalStartMinutes = timeToMinutes(originalStartTime);
      const newStartMinutes = Math.max(0, originalStartMinutes + minutesDiff);
      newStartTime = minutesToTime(newStartMinutes);

      // Ensure start time doesn't go past end time (minimum 15 minutes duration)
      if (timeToMinutes(newStartTime) >= timeToMinutes(originalEndTime)) {
        newStartTime = minutesToTime(timeToMinutes(originalEndTime) - 15);
      }
      // Keep end time unchanged
      newEndTime = originalEndTime;
    } else if (resizeHandle === 'right') {
      // Resize from right (change end time, keep start time fixed)
      const originalEndMinutes = timeToMinutes(originalEndTime);
      const newEndMinutes = Math.min(1440, originalEndMinutes + minutesDiff);
      newEndTime = minutesToTime(newEndMinutes);

      // Ensure end time doesn't go before start time (minimum 15 minutes duration)
      if (timeToMinutes(newEndTime) <= timeToMinutes(originalStartTime)) {
        newEndTime = minutesToTime(timeToMinutes(originalStartTime) + 15);
      }
      // Keep start time unchanged
      newStartTime = originalStartTime;
    }

    // Store the preview values for visual feedback
    setDraggedTask(prev => ({
      ...prev,
      previewStartTime: newStartTime,
      previewEndTime: newEndTime
    }));
  };

  const resizeTaskForPerson = async (assignment, personId, newStartTime, newEndTime) => {
    try {
      const token = localStorage.getItem('access_token');

      // Use the new individual_times API endpoint
      await axios.put(
        `${API_BASE_URL}/api/task-assignments/${assignment.id}/update-individual-time/`,
        {
          person_id: personId,
          start_time: newStartTime,
          end_time: newEndTime
        },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      // Reload assignments
      await loadTaskAssignments();
      console.log('Individual time updated successfully');
    } catch (error) {
      console.error('Error updating individual time:', error);
      alert('Failed to update task time. Please try again.');
    }
  };

  const resizeTaskForCar = async (assignment, newStartTime, newEndTime) => {
    try {
      const token = localStorage.getItem('access_token');

      // For cars, we update the general task time (not individual)
      await axios.patch(
        `${API_BASE_URL}/api/task-assignments/${assignment.id}/`,
        {
          start_time: newStartTime,
          end_time: newEndTime
        },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      // Reload assignments
      await loadTaskAssignments();
      console.log('Car task time updated successfully');
    } catch (error) {
      console.error('Error updating car task time:', error);
      alert('Failed to update car task time. Please try again.');
    }
  };

  const handleMouseUp = async (e) => {
    if (!draggedTask || (!resizeHandle && dragMode !== 'move')) return;

    e.preventDefault();

    const { assignment, personId, carId, originalStartTime, originalEndTime } = draggedTask;

    // Use the preview values calculated during mousemove
    const newStartTime = draggedTask.previewStartTime || originalStartTime;
    const newEndTime = draggedTask.previewEndTime || originalEndTime;

    // Update task time only if there's a significant change
    if (newStartTime !== originalStartTime || newEndTime !== originalEndTime) {
      if (personId) {
        // Handle person tasks
        if (dragMode === 'move') {
          await moveTaskForPerson(assignment, personId, newStartTime, newEndTime);
        } else {
          await resizeTaskForPerson(assignment, personId, newStartTime, newEndTime);
        }
      } else if (carId) {
        // Handle car tasks
        if (dragMode === 'move') {
          await moveTaskForCar(assignment, newStartTime, newEndTime);
        } else {
          await resizeTaskForCar(assignment, newStartTime, newEndTime);
        }
      }
    }

    // Reset all drag states
    setDraggedTask(null);
    setResizeHandle(null);
    setDragStartX(0);
    setDragMode(null);
    setIsDragging(false);
    setDropZone(null);
  };

  // Add global mouse event listeners for resize and move
  useEffect(() => {
    if (resizeHandle || dragMode === 'move') {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizeHandle, dragMode, draggedTask, dragStartX]);

  const handleRemovePersonFromTaskAtTime = async (personId, assignment, timeSlot) => {
    const personName = allPeople.find(p => p.id === personId);
    const displayName = personName?.first_name && personName?.last_name
      ? `${personName.last_name} ${personName.first_name}`
      : personName?.username || 'Unknown';

    const confirmMessage = `Remove ${displayName} from time slot ${timeSlot}?\n\nT${assignment.turbineId}: ${assignment.subcategory}\n\nThis will only remove them from this 15-minute slot. The task may be split into multiple parts.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');

      // Convert time strings to minutes for easier calculation
      const slotMinutes = timeToMinutes(timeSlot);
      const slotEndMinutes = slotMinutes + 15; // 15-minute slot
      const taskStartMinutes = timeToMinutes(assignment.startTime);
      const taskEndMinutes = timeToMinutes(assignment.endTime);

      // Check if this is a single 15-minute slot task
      if (taskEndMinutes - taskStartMinutes <= 15) {
        // Task is only 15 minutes or less - remove person from entire task
        if (assignment.assignedPeople.length <= 1) {
          // Delete entire task if person is the only one
          await axios.delete(`${API_BASE_URL}/api/task-assignments/${assignment.id}/`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          setTurbineAssignments(prev => ({
            ...prev,
            [assignment.turbineId]: prev[assignment.turbineId].filter(task => task.id !== assignment.id)
          }));
        } else {
          // Just remove person from task
          const updatedAssignment = {
            project: parseInt(selectedProject),
            turbine_id: assignment.turbineId,
            subcategory_id: assignment.subcategory_id, // Use subcategory_id (integer PK)
            start_time: assignment.startTime,
            end_time: assignment.endTime,
            people_needed: assignment.peopleNeeded,
            assigned_people: assignment.assignedPeople.filter(id => id !== personId),
            assigned_car: assignment.assignedCar,
            is_alternative: assignment.isAlternative || false,
            date: assignment.date
          };

          await axios.put(`${API_BASE_URL}/api/task-assignments/${assignment.id}/`, updatedAssignment, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          });

          setTurbineAssignments(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(turbineId => {
              updated[turbineId] = updated[turbineId].map(task =>
                task.id === assignment.id
                  ? { ...task, assignedPeople: task.assignedPeople.filter(id => id !== personId) }
                  : task
              );
            });
            return updated;
          });
        }
        return;
      }

      // Task is longer than 15 minutes - need to split it
      const newTasks = [];

      // Create task before the removed slot (if exists)
      if (slotMinutes > taskStartMinutes) {
        const beforeEndTime = minutesToTime(slotMinutes);
        newTasks.push({
          project: parseInt(selectedProject),
          turbine_id: assignment.turbineId,
          subcategory_id: assignment.subcategory_id, // Use subcategory_id (integer PK)
          start_time: assignment.startTime,
          end_time: beforeEndTime,
          people_needed: assignment.peopleNeeded,
          assigned_people: assignment.assignedPeople, // Keep all people
          assigned_car: assignment.assignedCar,
          is_alternative: assignment.isAlternative || false,
          date: assignment.date
        });
      }

      // Create task after the removed slot (if exists)
      if (slotEndMinutes < taskEndMinutes) {
        const afterStartTime = minutesToTime(slotEndMinutes);
        newTasks.push({
          project: parseInt(selectedProject),
          turbine_id: assignment.turbineId,
          subcategory_id: assignment.subcategory_id, // Use subcategory_id (integer PK)
          start_time: afterStartTime,
          end_time: assignment.endTime,
          people_needed: assignment.peopleNeeded,
          assigned_people: assignment.assignedPeople, // Keep all people
          assigned_car: assignment.assignedCar,
          is_alternative: assignment.isAlternative || false,
          date: assignment.date
        });
      }

      // Create the removed slot task with person removed
      const removedSlotPeople = assignment.assignedPeople.filter(id => id !== personId);
      if (removedSlotPeople.length > 0) {
        // Only create if there are still people assigned
        newTasks.push({
          project: parseInt(selectedProject),
          turbine_id: assignment.turbineId,
          subcategory_id: assignment.subcategory_id, // Use subcategory_id (integer PK)
          start_time: timeSlot,
          end_time: minutesToTime(slotEndMinutes),
          people_needed: assignment.peopleNeeded,
          assigned_people: removedSlotPeople,
          assigned_car: assignment.assignedCar,
          is_alternative: assignment.isAlternative || false,
          date: assignment.date
        });
      }

      // Delete original task
      await axios.delete(`${API_BASE_URL}/api/task-assignments/${assignment.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Create new tasks
      const createdTasks = [];
      for (const newTask of newTasks) {
        const response = await axios.post(`${API_BASE_URL}/api/task-assignments/`, newTask, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        createdTasks.push(response.data);
      }

      // Update local state
      setTurbineAssignments(prev => {
        const turbineAssignments = prev[assignment.turbineId] || [];
        const updatedAssignments = turbineAssignments.filter(task => task.id !== assignment.id);

        // Add new tasks with proper format conversion
        createdTasks.forEach(task => {
          updatedAssignments.push({
            ...task,
            turbineId: task.turbine_id,
            categoryColor: task.category_color,
            startTime: task.start_time,
            endTime: task.end_time,
            peopleNeeded: task.people_needed,
            assignedPeople: task.assigned_people,
            assignedCar: task.assigned_car,
            turbineName: `T${task.turbine_id}`
          });
        });

        return {
          ...prev,
          [assignment.turbineId]: updatedAssignments
        };
      });

    } catch (error) {
      console.error('Error removing person from time slot:', error);
      console.error('Error details:', error.response?.data);
      alert('Error removing person from time slot. Please try again.');
    }
  };

  // Handle assignment editing
  const handleEditAssignment = (assignment, personId) => {
    setEditingAssignment({
      ...assignment,
      personId,
      originalStartTime: assignment.startTime,
      originalEndTime: assignment.endTime
    });
    setShowAssignmentEditModal(true);
  };

  const handleSaveAssignmentEdit = () => {
    if (!editingAssignment) return;

    const updatedAssignment = {
      ...editingAssignment,
      startTime: editingAssignment.startTime,
      endTime: editingAssignment.endTime
    };

    setTurbineAssignments(prev => ({
      ...prev,
      [editingAssignment.turbineId]: prev[editingAssignment.turbineId].map(assignment =>
        assignment.id === editingAssignment.id ? updatedAssignment : assignment
      )
    }));

    setShowAssignmentEditModal(false);
    setEditingAssignment(null);
  };

  const handleDeleteAssignment = async (assignmentToDelete) => {
    const confirmMessage = `Delete task assignment?\n\nTurbine: T${assignmentToDelete.turbineId}\nTask: ${assignmentToDelete.subcategory}\nTime: ${assignmentToDelete.startTime} - ${assignmentToDelete.endTime}\n\nThis will remove the entire task assignment.`;

    if (window.confirm(confirmMessage)) {
      try {
        const token = localStorage.getItem('access_token');
        await axios.delete(`${API_BASE_URL}/api/task-assignments/${assignmentToDelete.id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        // Update local state only after successful API call
        setTurbineAssignments(prev => ({
          ...prev,
          [assignmentToDelete.turbineId]: prev[assignmentToDelete.turbineId].filter(assignment =>
            assignment.id !== assignmentToDelete.id
          )
        }));

        // Refresh all project assignments for experience tracking
        loadAllProjectAssignments();

      } catch (error) {
        console.error('Error deleting assignment:', error);
        alert('Error deleting assignment. Please try again.');
        return; // Don't close modal if there was an error
      }
    }

    setShowAssignmentEditModal(false);
    setEditingAssignment(null);
  };

  const selectAllTurbines = () => {
    setSelectedTurbines(new Set(turbines.map(t => t.id)));
  };

  const deselectAllTurbines = () => {
    setSelectedTurbines(new Set());
  };

  // Get only selected turbines for display
  const activeTurbines = turbines.filter(turbine => selectedTurbines.has(turbine.id));

  // Turbine collapse/expand functions
  const toggleTurbineCollapse = (turbineId) => {
    setCollapsedTurbines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(turbineId)) {
        newSet.delete(turbineId);
      } else {
        newSet.add(turbineId);
      }
      return newSet;
    });
  };

  // Task management functions
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleAddTaskCategory = () => {
    setShowTaskModal(true);
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the category "${categoryName}"?\n\nThis will also delete all subcategories and may affect existing assignments.`
    );

    if (!confirmDelete) return;

    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      await axios.delete(`${API_BASE_URL}/api/task-categories/${categoryId}/`, { headers });

      // Remove from local state
      setTaskCategories(prev => prev.filter(cat => cat.id !== categoryId));

      alert('Category deleted successfully!');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category. Please try again.');
    }
  };

  const handleDeleteSubcategory = (category, subcategoryIndex, subcategoryName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the subcategory "${subcategoryName}"?\n\nThis may affect existing assignments.`
    );

    if (!confirmDelete) return;

    // Update the category by removing the subcategory
    const updatedSubcategories = category.subcategories.filter((_, index) => index !== subcategoryIndex);

    const updatedCategory = {
      ...category,
      subcategories: updatedSubcategories
    };

    // Update local state immediately for responsive UI
    setTaskCategories(prev =>
      prev.map(cat => cat.id === category.id ? updatedCategory : cat)
    );

    // Save to backend
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios.put(`${API_BASE_URL}/api/task-categories/${category.id}/`, updatedCategory, { headers })
      .then(() => {
        alert('Subcategory deleted successfully!');
      })
      .catch((error) => {
        console.error('Error deleting subcategory:', error);
        // Revert local state on error
        setTaskCategories(prev =>
          prev.map(cat => cat.id === category.id ? category : cat)
        );
        alert('Error deleting subcategory. Please try again.');
      });
  };

  const handleSaveTaskCategory = async () => {
    if (!newTaskData.categoryName.trim()) return;

    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};


    try {
      if (selectedTaskForEdit) {
        // Edit existing category
        const updatedCategory = {
          ...selectedTaskForEdit,
          name: newTaskData.categoryName,
          color: newTaskData.categoryColor,
          subcategories: newTaskData.subcategories.filter(sub => sub.name.trim())
            .map(sub => ({
              id: sub.id || Date.now() + Math.random(),
              name: sub.name,
              description: sub.description?.trim() || ''
            }))
        };

        // Save to backend
        await axios.put(`${API_BASE_URL}/api/task-categories/${selectedTaskForEdit.id}/`, updatedCategory, { headers });

        setTaskCategories(prev =>
          prev.map(cat => cat.id === selectedTaskForEdit.id ? updatedCategory : cat)
        );
      } else {
        // Create new category
        const newCategory = {
          name: newTaskData.categoryName,
          color: newTaskData.categoryColor,
          project: parseInt(selectedProject),
          subcategories: newTaskData.subcategories.filter(sub => sub.name.trim())
            .map(sub => ({
              name: sub.name,
              description: sub.description?.trim() || ''
            }))
        };

        // Save to backend
        const response = await axios.post(`${API_BASE_URL}/api/task-categories/`, newCategory, { headers });
        const savedCategory = response.data;

        setTaskCategories(prev => [...prev, savedCategory]);
      }

      setNewTaskData({
        categoryName: '',
        categoryColor: '#3B82F6',
        subcategories: [{ name: '', description: '' }]
      });
      setSelectedTaskForEdit(null);
      setShowTaskModal(false);
    } catch (error) {
      console.error('Error saving task category:', error);
      alert('Error saving category. Please try again.');
    }
  };

  const handleAddSubcategory = () => {
    setNewTaskData(prev => ({
      ...prev,
      subcategories: [...prev.subcategories, { name: '', description: '' }]
    }));
  };

  const handleUpdateSubcategory = (index, field, value) => {
    setNewTaskData(prev => ({
      ...prev,
      subcategories: prev.subcategories.map((sub, i) =>
        i === index ? { ...sub, [field]: value } : sub
      )
    }));
  };

  const handleRemoveSubcategory = (index) => {
    if (newTaskData.subcategories.length > 1) {
      setNewTaskData(prev => ({
        ...prev,
        subcategories: prev.subcategories.filter((_, i) => i !== index)
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-steel-50 p-6">
      <div className="w-full mx-auto">
        {/* Header */}
        <Toolbar
        projects={projects}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        handleDateChange={handleDateChange}
        copyTasksToNextDay={copyTasksToNextDay}
        setShowExportModal={setShowExportModal}
        setShowLiveMap={setShowLiveMap}
        showLiveMap={showLiveMap}
        setShowTimeline={setShowTimeline}
        showTimeline={showTimeline}
      />

        {/* Turbine Selection Panel */}
        {selectedProject && turbines.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-steel-200 mb-4">
            <div className="flex items-center justify-between p-2 cursor-pointer" onClick={() => setShowTurbineSelection(!showTurbineSelection)}>
              <div>
                <h3 className="text-base font-semibold text-slate-900 flex items-center">
                  Select Turbines for Work Planning
                  <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${showTurbineSelection ? 'rotate-90' : ''}`} />
                </h3>
                <p className="text-xs text-slate-600">
                  {selectedTurbines.size > 0
                    ? `${selectedTurbines.size} of ${turbines.length} selected`
                    : 'Choose turbines for planning'
                  }
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAllTurbines();
                  }}
                  className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-600 transition-colors"
                >
                  All ({turbines.length})
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deselectAllTurbines();
                  }}
                  className="px-2 py-1 text-xs bg-steel-500 text-white rounded hover:bg-steel-600 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>

            {showTurbineSelection && (
              <div className="p-3 pt-0 border-t border-steel-200">
                <div className="grid grid-cols-6 md:grid-cols-10 lg:grid-cols-12 xl:grid-cols-16 gap-1.5">
                  {turbines.map(turbine => (
                    <label
                      key={turbine.id}
                      title={turbine.turbine_id ? `T${turbine.turbine_number || turbine.id} (${turbine.turbine_id})` : `T${turbine.id}`}
                      className={`flex items-center justify-center space-x-1 p-1.5 rounded border cursor-pointer transition-all ${
                        selectedTurbines.has(turbine.id)
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-steel-200 bg-steel-50 hover:border-steel-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTurbines.has(turbine.id)}
                        onChange={() => toggleTurbineSelection(turbine.id)}
                        className="w-3 h-3 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                      />
                      <span className="text-xs font-medium truncate max-w-[60px]">
                        T{turbine.turbine_number || turbine.id}
                        {turbine.turbine_id && <span className="text-[10px] opacity-75"> ({turbine.turbine_id.length > 6 ? turbine.turbine_id.substring(0, 6) + '...' : turbine.turbine_id})</span>}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="mt-2 text-xs text-slate-600">
                  Selected: {selectedTurbines.size} of {turbines.length}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content - Two Columns with 1:2 ratio */}
        {selectedProject ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Left Column - narrower (1/3) - Sticky */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-4 space-y-4">
              {/* People Section */}
              <PeopleList
                collapsed={collapsedColumns.people}
                toggleCollapse={() => setCollapsedColumns(prev => ({ ...prev, people: !prev.people }))}
                selectedTaskForPeopleFilter={selectedTaskForPeopleFilter}
                getAvailablePeopleForTask={getAvailablePeopleForTask}
                projectPeople={projectPeople}
                selectedProject={selectedProject}
                selectedDate={selectedDate}
                getPersonUtilization={getPersonUtilization}
                allProjectAssignments={allProjectAssignments}
                allPeople={allPeople}
              />

              {/* Cars Section */}
              <CarList
                collapsed={collapsedColumns.cars}
                toggleCollapse={() => setCollapsedColumns(prev => ({ ...prev, cars: !prev.cars }))}
                projectCars={projectCars}
                selectedProject={selectedProject}
                getCarUtilization={getCarUtilization}
              />
              </div>
            </div>

            {/* Right Column - wider (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Active Turbines Section */}
              <Timeline
                activeTurbines={activeTurbines}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                toggleTurbineCollapse={toggleTurbineCollapse}
                collapsedTurbines={collapsedTurbines}
                getAssignmentsForTurbine={getAssignmentsForTurbine}
                taskCategories={taskCategories}
                getCategoriesForTurbine={getCategoriesForTurbine}
                turbinesWithTaskLists={turbines}
                setExpandedTasks={setExpandedTasks}
                setSelectedTaskForPeopleFilter={setSelectedTaskForPeopleFilter}
                setQuickAddTurbineId={setQuickAddTurbineId}
                setQuickAddStep={setQuickAddStep}
                setQuickAddSelectedTasks={setQuickAddSelectedTasks}
                setQuickAddPeople={setQuickAddPeople}
                setQuickAddCar={setQuickAddCar}
                setQuickAddPeopleSearch={setQuickAddPeopleSearch}
                setQuickAddTaskSearch={setQuickAddTaskSearch}
                setShowQuickAddModal={setShowQuickAddModal}
                expandedTasks={expandedTasks}
                showTimeSliders={showTimeSliders}
                setShowTimeSliders={setShowTimeSliders}
                getIndividualTimes={getIndividualTimes}
                timeToMinutes={timeToMinutes}
                minutesToTime={minutesToTime}
                resizeTaskForPerson={resizeTaskForPerson}
                allPeople={allPeople}
                projectCars={projectCars}
                unmarkTaskCompleted={unmarkTaskCompleted}
                markTaskCompleted={markTaskCompleted}
                setTurbineAssignments={setTurbineAssignments}
                API_BASE_URL={API_BASE_URL}
                axios={axios}
                selectedProject={selectedProject}
                setTaskAssignData={setTaskAssignData}
                setSelectedTaskForAssign={setSelectedTaskForAssign}
                setSelectedTurbineForAssign={setSelectedTurbineForAssign}
                setEditingAssignmentId={setEditingAssignmentId}
                setShowTaskAssignModal={setShowTaskAssignModal}
                setShowProjectOverview={setShowProjectOverview}
                loadAllProjectAssignments={loadAllProjectAssignments}
                loadTaskAssignments={loadTaskAssignments}
              />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-12">
            <div className="text-center text-slate-500">
              <Settings className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Select a Project</h3>
              <p>Choose a project to view turbines and start work planning</p>
            </div>
          </div>
        )}

        {/* Timeline Schedule View */}
        {showTimeline && selectedProject && projectPeople.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-steel-200 mt-6">
            <div className="p-4 border-b border-steel-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-green-600" />
                    Daily Schedule Timeline
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Timeline view of people assignments for {format(selectedDate, 'EEE, MMM d, yyyy')}
                  </p>
                </div>

                {/* Time Range Selector */}
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-slate-700">View Range:</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={timelineStartTime}
                        onChange={(e) => setTimelineStartTime(e.target.value)}
                        className="px-2 py-1 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <span className="text-slate-500 text-sm">to</span>
                      <input
                        type="time"
                        value={timelineEndTime}
                        onChange={(e) => setTimelineEndTime(e.target.value)}
                        className="px-2 py-1 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Quick Presets */}
                  <div className="flex space-x-1">
                    <button
                      onClick={() => {
                        setTimelineStartTime('07:00');
                        setTimelineEndTime('19:00');
                      }}
                      className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                    >
                      Day Shift
                    </button>
                    <button
                      onClick={() => {
                        setTimelineStartTime('19:00');
                        setTimelineEndTime('07:00');
                      }}
                      className="px-2 py-1 text-xs bg-brand-100 text-brand-700 rounded hover:bg-brand-200 transition-colors"
                    >
                      Night Shift
                    </button>
                    <button
                      onClick={() => {
                        setTimelineStartTime('01:00');
                        setTimelineEndTime('00:00');
                      }}
                      className="px-2 py-1 text-xs bg-steel-100 text-slate-700 rounded hover:bg-steel-200 transition-colors"
                    >
                      Full Day
                    </button>
                  </div>

                  {/* Fullscreen Button */}
                  <button
                    onClick={() => setIsTimelineFullscreen(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors shadow-sm"
                    title="View timeline in fullscreen"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
                    </svg>
                    <span className="text-sm font-medium">Fullscreen</span>
                  </button>

                  {/* Export Button */}
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors shadow-sm"
                    title="Export plan for WhatsApp/PDF"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm font-medium">Export Plan</span>
                  </button>
                </div>
              </div>

              {/* Timeline Info */}
              <div className="mt-3 text-xs text-slate-500">
                Showing: {timeSlots.length} time slots ({timelineStartTime} - {timelineEndTime})
                {timeToMinutes(timelineEndTime) < timeToMinutes(timelineStartTime) && (
                  <span className="ml-2 text-brand-600">(Overnight shift)</span>
                )}
              </div>
            </div>

            <div className="p-4">
              {/* Scrollable Timeline Container */}
              <div className="timeline-container overflow-x-auto overflow-y-visible" style={{ minHeight: `${40 + (projectPeople.length + projectCars.length) * 24 + 40}px` }}>
                <div style={{ minWidth: `${145 + (timeSlots.length * 40)}px` }}>
                  {/* Timeline Header */}
                  <div className="grid" style={{ gridTemplateColumns: `145px repeat(${timeSlots.length}, 40px)` }}>
                    <div className="p-1 font-medium text-slate-700 border-b border-steel-200 sticky left-0 bg-white z-10 h-[20px] flex items-center w-[145px]">
                      People
                    </div>
                    {timeSlots.map(time => (
                      <div key={time} className={`p-1 text-xs font-medium text-center border-b border-steel-200 h-[20px] flex items-center justify-center ${
                        time === '00:00' ? 'text-brand-700 bg-brand-50' : 'text-slate-600'
                      }`}>
                        {time.substring(0, 5)}
                      </div>
                    ))}
                  </div>

                  {/* Timeline Rows for Each Person */}
                  <div className="space-y-1 mt-2">
                    {projectPeople.map(person => (
                      <div key={person.id} className="flex hover:bg-steel-50 rounded" data-person-id={person.id}>
                        {/* Person Info */}
                        <div className="p-1 flex items-center border-r border-steel-100 sticky left-0 bg-white z-10 h-[20px]" style={{ minWidth: '145px', width: '145px' }}>
                          <div>
                            <div className="text-xs text-slate-900 truncate">
                              {person.first_name && person.last_name
                                ? `${person.last_name} ${person.first_name}`
                                : person.username}
                            </div>
                          </div>
                        </div>

                        {/* Time Blocks Container */}
                        <div className="flex">
                          {/* Time Blocks - Grouped consecutive assignments */}
                          {groupTimeSlotsByAssignment(person.id, timeSlots).map((block, blockIndex) => {
                          const isAssigned = block.assignment !== null;
                          const assignment = block.assignment;
                          const blockWidth = isAssigned ? block.slotCount * 40 : 40; // 40px per slot, single slots for empty

                          return (
                            <div
                              key={`${block.startTime}-${blockIndex}`}
                              className={`text-xs border border-steel-100 min-h-[20px] h-[20px] flex items-center justify-center relative group overflow-visible ${
                                isAssigned
                                  ? 'p-1 border-steel-300 hover:opacity-80 rounded-md select-none transition-all duration-150'
                                  : 'bg-steel-50 hover:bg-steel-100 cursor-pointer'
                              } ${
                                dropZone && dropZone.personId === person.id && dropZone.timeSlot === block.startTime
                                  ? 'bg-green-200 border-green-400 ring-2 ring-green-300 shadow-lg'
                                  : ''
                              } ${
                                isAssigned && dragMode === 'move'
                                  ? 'cursor-grabbing'
                                  : isAssigned
                                    ? 'cursor-grab'
                                    : ''
                              }`}
                              data-time-slot={block.startTime}
                              style={{
                                width: `${blockWidth}px`,
                                backgroundColor: dropZone && dropZone.personId === person.id && dropZone.timeSlot === block.startTime
                                  ? '#BBF7D0'
                                  : isAssigned && assignment?.isAlternative
                                    ? '#FEF3C7' // Amber-100 for alternative tasks
                                    : isAssigned && assignment?.categoryColor
                                      ? `${assignment.categoryColor}20`
                                      : isAssigned
                                        ? '#DBEAFE'
                                        : '#F9FAFB', // Always gray background for empty slots
                                border: dropZone && dropZone.personId === person.id && dropZone.timeSlot === block.startTime
                                  ? '2px solid #10B981'
                                  : isAssigned && assignment?.isAlternative
                                    ? '2px dashed #F59E0B' // Amber-500 dashed border for alternative
                                    : isAssigned ? `2px solid ${assignment?.categoryColor || '#3B82F6'}` : '1px solid #e5e7eb',
                                borderRadius: isAssigned ? '8px' : undefined,
                                opacity: draggedTask && draggedTask.assignment && draggedTask.assignment.id === assignment?.id && draggedTask.personId === person.id ? 0.5 : (isAssigned && assignment?.isAlternative ? 0.8 : 1),
                                transform: draggedTask && draggedTask.assignment && draggedTask.assignment.id === assignment?.id && draggedTask.personId === person.id ? 'scale(1.02)' : 'scale(1)',
                                position: 'relative',
                                zIndex: isAssigned && assignment?.isAlternative ? 10 : 1
                              }}
                              title={
                                assignment
                                  ? (() => {
                                      const { startTime, endTime } = getIndividualTimes(assignment, person.id);
                                      return `${assignment.turbineName}: ${assignment.category ? `${assignment.category} - ${assignment.subcategory}` : assignment.subcategory} (${startTime}-${endTime})\nDuration: ${block.slotCount * 15} minutes\nDouble-click to edit, drag to move, drag edges to resize`;
                                    })()
                                  : ''
                              }
                              onDrop={(e) => {
                                if (!isAssigned) {
                                  handleTaskDrop(e, block.startTime);
                                }
                              }}
                              onDragOver={handleTimeSlotDragOver}
                              onMouseDown={(e) => {
                                if (assignment && e.button === 0) { // Left mouse button only
                                  handleTaskMoveStart(e, assignment, person.id);
                                }
                              }}
                              onDoubleClick={(e) => {
                                if (assignment) {
                                  handleEditAssignment(assignment, person.id);
                                }
                              }}
                            >
                              {assignment && (
                                <div className="text-center w-full px-1 overflow-hidden max-w-full flex items-center justify-center">
                                  <div className="truncate text-xs whitespace-nowrap overflow-hidden max-w-full flex items-center gap-1" style={{ color: assignment.categoryColor || '#1E40AF' }}>
                                    {assignment.isAlternative && (
                                      <span className="text-amber-600 font-bold text-xs bg-amber-200 px-1 rounded" title="Alternative task">
                                        ALT
                                      </span>
                                    )}
                                    {assignment.turbineName} {assignment.category ? `${assignment.category} - ${assignment.subcategory}` : assignment.subcategory}
                                  </div>

                                  {/* Alternative tasks overlay */}
                                  {block.alternativeAssignments && Object.keys(block.alternativeAssignments).length > 0 && (
                                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                                      {Object.entries(block.alternativeAssignments).map(([timeSlot, altAssignments]) =>
                                        altAssignments.map((altAssignment, altIndex) => (
                                          <div
                                            key={`alt-${altAssignment.id}-${altIndex}`}
                                            className="absolute top-0 h-full bg-amber-100 border border-dashed border-amber-400 rounded opacity-60"
                                            style={{
                                              left: `${((timeToMinutes(timeSlot) - timeToMinutes(block.startTime)) / 15) * 40}px`,
                                              width: '40px',
                                              zIndex: 15
                                            }}
                                            title={`Alternative: ${altAssignment.turbineName} ${altAssignment.subcategory}`}
                                          >
                                            <div className="text-xs text-amber-700 px-1 truncate">
                                              ALT
                                            </div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}

                                  {/* Remove button - appears on hover */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemovePersonFromTaskAtTime(person.id, assignment, block.startTime);
                                    }}
                                    className="absolute top-0 right-0 w-3 h-3 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:bg-red-600"
                                    title={`Remove ${person.first_name && person.last_name ? `${person.last_name} ${person.first_name}` : person.username} from this task`}
                                  >
                                    ×
                                  </button>

                                  {/* Left resize handle */}
                                  <div
                                    className="resize-handle absolute left-0 top-0 w-2 h-full bg-brand-500 opacity-0 group-hover:opacity-100 cursor-ew-resize transition-opacity z-20"
                                    title="Drag to change start time"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setDragMode('resize');
                                      setResizeHandle('left');
                                      const { startTime: personStartTime, endTime: personEndTime } = getIndividualTimes(assignment, person.id);
                                      setDraggedTask({
                                        assignment: { ...assignment },
                                        personId: person.id,
                                        originalStartTime: personStartTime,
                                        originalEndTime: personEndTime
                                      });
                                      setDragStartX(e.clientX);
                                    }}
                                  />

                                  {/* Right resize handle */}
                                  <div
                                    className="resize-handle absolute right-0 top-0 w-2 h-full bg-brand-500 opacity-0 group-hover:opacity-100 cursor-ew-resize transition-opacity z-20"
                                    title="Drag to change end time"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setDragMode('resize');
                                      setResizeHandle('right');
                                      const { startTime: personStartTime, endTime: personEndTime } = getIndividualTimes(assignment, person.id);
                                      setDraggedTask({
                                        assignment: { ...assignment },
                                        personId: person.id,
                                        originalStartTime: personStartTime,
                                        originalEndTime: personEndTime
                                      });
                                      setDragStartX(e.clientX);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    ))}

                    {/* Cars Rows - under people */}
                    {projectCars.length > 0 && (
                      <>
                        {/* Separator */}
                        <div className="border-t border-steel-300 my-3"></div>

                        {projectCars.map(car => (
                          <div key={`car-${car.id}`} className="flex hover:bg-steel-50 rounded">
                            {/* Car Info */}
                            <div className="p-1 flex items-center border-r border-steel-100 sticky left-0 bg-white z-10 h-[20px]" style={{ minWidth: '145px', width: '145px' }}>
                              <div className="truncate">
                                <span className="text-xs text-slate-900 truncate">
                                  {car.car_brand} {car.car_model}
                                </span>
                                <span className="text-xs text-slate-500 truncate">
                                  ({car.plate_number})
                                </span>
                              </div>
                            </div>

                            {/* Time Blocks Container */}
                            <div className="flex">
                              {/* Time Blocks - Grouped consecutive car assignments */}
                              {groupCarTimeSlotsByAssignment(car.id, timeSlots).map((block, blockIndex) => {
                              const isAssigned = block.assignment !== null;
                              const assignment = block.assignment;
                              const blockWidth = isAssigned ? block.slotCount * 40 : 40; // 40px per slot, single slots for empty

                              return (
                                <div
                                  key={`car-${block.startTime}-${blockIndex}`}
                                  className={`text-xs border border-steel-100 min-h-[20px] h-[20px] flex items-center justify-center relative group overflow-visible ${
                                    isAssigned
                                      ? 'p-1 border-steel-300 hover:opacity-80 rounded-md select-none transition-all duration-150'
                                      : 'bg-steel-50 hover:bg-steel-100 cursor-pointer'
                                  } ${
                                    dropZone && dropZone.carId === car.id && dropZone.timeSlot === block.startTime
                                      ? 'bg-green-200 border-green-400 ring-2 ring-green-300 shadow-lg'
                                      : ''
                                  } ${
                                    isAssigned && dragMode === 'move'
                                      ? 'cursor-grabbing'
                                      : isAssigned
                                        ? 'cursor-grab'
                                        : ''
                                  }`}
                                  data-time-slot={block.startTime}
                                  style={{
                                    width: `${blockWidth}px`,
                                    backgroundColor: dropZone && dropZone.carId === car.id && dropZone.timeSlot === block.startTime
                                      ? '#BBF7D0'
                                      : isAssigned && assignment?.isAlternative
                                        ? '#FEF3C7' // Amber-100 for alternative tasks
                                        : isAssigned && assignment?.categoryColor
                                          ? `${assignment.categoryColor}20`
                                          : isAssigned
                                            ? '#FED7AA'  // Orange-200
                                            : '#F9FAFB', // Always gray background for empty slots
                                    border: dropZone && dropZone.carId === car.id && dropZone.timeSlot === block.startTime
                                      ? '2px solid #10B981'
                                      : isAssigned && assignment?.isAlternative
                                        ? '2px dashed #F59E0B' // Amber-500 dashed border for alternative
                                        : isAssigned ? `2px solid ${assignment?.categoryColor || '#EA580C'}` : '1px solid #e5e7eb',
                                    borderRadius: isAssigned ? '8px' : undefined,
                                    opacity: draggedTask && draggedTask.assignment && draggedTask.assignment.id === assignment?.id && draggedTask.carId === car.id ? 0.5 : (isAssigned && assignment?.isAlternative ? 0.8 : 1),
                                    transform: draggedTask && draggedTask.assignment && draggedTask.assignment.id === assignment?.id && draggedTask.carId === car.id ? 'scale(1.02)' : 'scale(1)',
                                    position: 'relative',
                                    zIndex: isAssigned && assignment?.isAlternative ? 10 : 1
                                  }}
                                  title={
                                    assignment
                                      ? `T${assignment.turbineId}: ${assignment.subcategory} (${assignment.startTime}-${assignment.endTime})\nCar assignment - Duration: ${block.slotCount * 15} minutes\nDouble-click to edit, drag to move, drag edges to resize`
                                      : ''
                                  }
                                  onDrop={(e) => {
                                    if (!isAssigned) {
                                      handleTaskDrop(e, block.startTime);
                                    }
                                  }}
                                  onDragOver={handleTimeSlotDragOver}
                                  onMouseDown={(e) => {
                                    if (assignment && e.button === 0) { // Left mouse button only
                                      handleCarTaskMoveStart(e, assignment, car.id);
                                    }
                                  }}
                                  onDoubleClick={(e) => {
                                    if (assignment) {
                                      handleEditAssignment(assignment, null);
                                    }
                                  }}
                                >
                                  {assignment && (
                                    <div className="text-center w-full px-1 overflow-hidden max-w-full flex items-center justify-center">
                                      <div className="truncate text-xs whitespace-nowrap overflow-hidden max-w-full flex items-center gap-1" style={{ color: assignment.categoryColor || '#EA580C' }}>
                                        {assignment.isAlternative && (
                                          <span className="text-amber-600 font-bold text-xs bg-amber-200 px-1 rounded" title="Alternative task">
                                            ALT
                                          </span>
                                        )}
                                        {assignment.turbineName} {assignment.category ? `${assignment.category} - ${assignment.subcategory}` : assignment.subcategory}
                                      </div>

                                      {/* Left resize handle for cars */}
                                      <div
                                        className="resize-handle absolute left-0 top-0 w-2 h-full bg-orange-500 opacity-0 group-hover:opacity-100 cursor-ew-resize transition-opacity z-20"
                                        title="Drag to change car assignment start time"
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setDragMode('resize');
                                          setResizeHandle('left');
                                          setDraggedTask({
                                            assignment: { ...assignment },
                                            carId: car.id,
                                            originalStartTime: assignment.startTime,
                                            originalEndTime: assignment.endTime
                                          });
                                          setDragStartX(e.clientX);
                                        }}
                                      />

                                      {/* Right resize handle for cars */}
                                      <div
                                        className="resize-handle absolute right-0 top-0 w-2 h-full bg-orange-500 opacity-0 group-hover:opacity-100 cursor-ew-resize transition-opacity z-20"
                                        title="Drag to change car assignment end time"
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setDragMode('resize');
                                          setResizeHandle('right');
                                          setDraggedTask({
                                            assignment: { ...assignment },
                                            carId: car.id,
                                            originalStartTime: assignment.startTime,
                                            originalEndTime: assignment.endTime
                                          });
                                          setDragStartX(e.clientX);
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>


              {/* Legend */}
              <div className="mt-4 pt-4 border-t border-steel-200">
                <div className="flex items-center space-x-6 text-xs text-slate-600">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-steel-50 border border-steel-100 rounded"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-brand-100 border border-brand-300 rounded"></div>
                    <span>Person Assigned</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-orange-200 border border-orange-300 rounded"></div>
                    <span>Car Assigned</span>
                  </div>
                  <div className="text-slate-500">
                    Hover over assigned slots to see task details
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Assignment Modal */}
        {showTaskAssignModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-steel-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: selectedTaskForAssign?.task?.color || '#3b82f6' }}
                    />
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">
                        {editingAssignmentId ? 'Edit Task' : 'Assign Task'}
                      </h3>
                      <p className="text-sm text-slate-500">
                        T{selectedTurbineForAssign} • {selectedTaskForAssign?.subcategory?.name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowTaskAssignModal(false);
                      setPeopleSearchTerm('');
                      setEditingAssignmentId(null);
                    }}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-steel-100 rounded-md transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-5 py-4 space-y-5 max-h-[60vh] overflow-y-auto">
                {/* Time Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Start</label>
                    <input
                      type="time"
                      value={taskAssignData.startTime}
                      onChange={(e) => setTaskAssignData(prev => ({ ...prev, startTime: e.target.value }))}
                      className="w-full h-10 px-3 border border-steel-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">End</label>
                    <input
                      type="time"
                      value={taskAssignData.endTime}
                      onChange={(e) => setTaskAssignData(prev => ({ ...prev, endTime: e.target.value }))}
                      className="w-full h-10 px-3 border border-steel-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Options Row */}
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-steel-200 rounded-md hover:bg-steel-50">
                    <input
                      type="checkbox"
                      checked={taskAssignData.isAlternative}
                      onChange={(e) => setTaskAssignData(prev => ({ ...prev, isAlternative: e.target.checked }))}
                      className="w-4 h-4 rounded border-steel-300 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm text-slate-700">Alternative</span>
                  </label>
                  <select
                    value={taskAssignData.assignedCar || ''}
                    onChange={(e) => setTaskAssignData(prev => ({
                      ...prev,
                      assignedCar: e.target.value ? parseInt(e.target.value) : null
                    }))}
                    className="flex-1 h-10 px-3 border border-steel-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">No car</option>
                    {projectCars.map(car => (
                      <option key={car.id} value={car.id}>
                        {car.plate_number} • {car.car_brand}
                      </option>
                    ))}
                  </select>
                </div>

                {/* People Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">People</label>
                    <span className="text-xs text-slate-500">
                      {taskAssignData.assignedPeople.length} / {taskAssignData.peopleNeeded}
                    </span>
                  </div>

                  {/* Assigned People with Individual Times */}
                  {taskAssignData.assignedPeople.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {taskAssignData.assignedPeople.map(personId => {
                        const person = projectPeople.find(p => p.id === personId);
                        if (!person) return null;
                        const individualTimes = taskAssignData.individualTimes?.[personId] || {
                          startTime: taskAssignData.startTime,
                          endTime: taskAssignData.endTime
                        };
                        return (
                          <div
                            key={personId}
                            className="flex items-center gap-2 p-2 bg-steel-50 border border-steel-200 rounded-md"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-slate-900">
                                {person.first_name && person.last_name
                                  ? `${person.last_name} ${person.first_name}`
                                  : person.username}
                              </span>
                            </div>
                            <input
                              type="time"
                              value={individualTimes.startTime}
                              onChange={(e) => setTaskAssignData(prev => ({
                                ...prev,
                                individualTimes: {
                                  ...prev.individualTimes,
                                  [personId]: {
                                    ...(prev.individualTimes?.[personId] || {}),
                                    startTime: e.target.value,
                                    endTime: prev.individualTimes?.[personId]?.endTime || prev.endTime
                                  }
                                }
                              }))}
                              className="w-24 h-8 px-2 text-sm border border-steel-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                            <span className="text-slate-400">-</span>
                            <input
                              type="time"
                              value={individualTimes.endTime}
                              onChange={(e) => setTaskAssignData(prev => ({
                                ...prev,
                                individualTimes: {
                                  ...prev.individualTimes,
                                  [personId]: {
                                    ...(prev.individualTimes?.[personId] || {}),
                                    startTime: prev.individualTimes?.[personId]?.startTime || prev.startTime,
                                    endTime: e.target.value
                                  }
                                }
                              }))}
                              className="w-24 h-8 px-2 text-sm border border-steel-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                            <button
                              onClick={() => handlePersonToggle(personId)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add People - Always visible */}
                  <div className="border border-steel-200 rounded-md overflow-hidden">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search to add people..."
                        value={peopleSearchTerm}
                        onChange={(e) => setPeopleSearchTerm(e.target.value)}
                        className="w-full h-10 pl-9 pr-3 text-sm border-0 border-b border-steel-200 focus:outline-none focus:ring-0"
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {filteredPeople
                        .filter(p => !taskAssignData.assignedPeople.includes(p.id))
                        .filter(person => {
                          // Filter out people who are fully busy (8+ hours = 100%)
                          const schedule = getPersonSchedule(person.id);
                          if (schedule.length === 0) return true; // Free all day

                          // Calculate total busy minutes
                          let totalBusyMinutes = 0;
                          schedule.forEach(task => {
                            const [sh, sm] = task.startTime.split(':').map(Number);
                            const [eh, em] = task.endTime.split(':').map(Number);
                            totalBusyMinutes += (eh * 60 + em) - (sh * 60 + sm);
                          });

                          // If 12+ hours busy (720 min = 7:00-19:00), they're at 100% - hide them
                          return totalBusyMinutes < 720;
                        })
                        .slice(0, 15)
                        .map(person => {
                          // Get person's schedule for today
                          const personSchedule = getPersonSchedule(person.id);

                          return (
                            <div
                              key={person.id}
                              className="border-b border-steel-100 last:border-0 hover:bg-steel-50"
                            >
                              <button
                                onClick={() => handlePersonToggle(person.id)}
                                className="w-full flex items-center justify-between px-3 py-2"
                              >
                                <span className="text-sm font-medium text-slate-900">
                                  {person.first_name && person.last_name
                                    ? `${person.last_name} ${person.first_name}`
                                    : person.username}
                                </span>
                                <Plus className="w-4 h-4 text-slate-400" />
                              </button>

                              {/* Person's schedule */}
                              <div className="px-3 pb-2 flex flex-wrap gap-1">
                                {personSchedule.length > 0 ? (
                                  personSchedule.map((task, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center text-xs px-1.5 py-0.5 rounded"
                                      style={{
                                        backgroundColor: `${task.categoryColor}20`,
                                        color: task.categoryColor
                                      }}
                                      title={`T${task.turbineId}: ${task.subcategory}`}
                                    >
                                      {task.startTime}-{task.endTime}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-green-600">Free all day</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {filteredPeople.filter(p => !taskAssignData.assignedPeople.includes(p.id)).length === 0 && (
                        <div className="px-3 py-4 text-center text-sm text-slate-500">
                          {peopleSearchTerm ? 'No results' : 'All people assigned'}
                        </div>
                      )}
                    </div>
                  </div>

                  {taskAssignData.assignedPeople.length < taskAssignData.peopleNeeded && (
                    <p className="mt-2 text-xs text-amber-600">
                      Need {taskAssignData.peopleNeeded - taskAssignData.assignedPeople.length} more
                    </p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-steel-200 bg-steel-50 flex items-center justify-between">
                {editingAssignmentId ? (
                  <button
                    onClick={async () => {
                      if (window.confirm('Delete this task?')) {
                        try {
                          const token = localStorage.getItem('access_token');
                          await axios.delete(`${API_BASE_URL}/api/task-assignments/${editingAssignmentId}/`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          setTurbineAssignments(prev => {
                            const updated = { ...prev };
                            Object.keys(updated).forEach(tid => {
                              updated[tid] = updated[tid].filter(t => t.id !== editingAssignmentId);
                            });
                            return updated;
                          });
                          setShowTaskAssignModal(false);
                          setEditingAssignmentId(null);
                          loadAllProjectAssignments();
                        } catch (error) {
                          console.error('Error deleting:', error);
                          alert('Failed to delete');
                        }
                      }
                    }}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Delete task
                  </button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowTaskAssignModal(false);
                      setPeopleSearchTerm('');
                      setEditingAssignmentId(null);
                    }}
                    className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-steel-200 rounded-md hover:bg-steel-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTaskAssignment}
                    className="h-9 px-4 text-sm font-medium text-white bg-brand-500 rounded-md hover:bg-brand-600 transition-colors"
                  >
                    {editingAssignmentId ? 'Save' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Edit Modal */}
        {showAssignmentEditModal && editingAssignment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-slate-900">Edit Task Assignment</h3>
                  <button
                    onClick={() => {
                      setShowAssignmentEditModal(false);
                      setEditingAssignment(null);
                    }}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Assignment Details */}
                <div className="mb-6 p-4 bg-steel-50 rounded-xl">
                  <div className="flex items-center mb-2">
                    <div
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: editingAssignment.categoryColor || '#3B82F6' }}
                    ></div>
                    <h4 className="font-medium text-slate-900">{editingAssignment.category}</h4>
                  </div>
                  <p className="text-sm text-slate-600">{editingAssignment.subcategory}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Turbine: T{editingAssignment.turbineId} |
                    People: {editingAssignment.assignedPeople.length}/{editingAssignment.peopleNeeded}
                  </p>
                </div>

                {/* Time Configuration */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-slate-900">Time Configuration</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={editingAssignment.startTime}
                        onChange={(e) => setEditingAssignment(prev => ({
                          ...prev,
                          startTime: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={editingAssignment.endTime}
                        onChange={(e) => setEditingAssignment(prev => ({
                          ...prev,
                          endTime: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Time Summary */}
                  <div className="p-3 bg-brand-50 rounded-xl">
                    <div className="text-sm text-slate-700">
                      <div className="flex justify-between">
                        <span>Original:</span>
                        <span>{editingAssignment.originalStartTime} - {editingAssignment.originalEndTime}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>New:</span>
                        <span>{editingAssignment.startTime} - {editingAssignment.endTime}</span>
                      </div>
                    </div>
                  </div>

                  {/* Assigned Person Info */}
                  <div className="p-3 bg-green-50 rounded-xl">
                    <div className="text-sm text-slate-700">
                      <div className="font-medium">Person being edited:</div>
                      <div>
                        {(() => {
                          const person = projectPeople.find(p => p.id === editingAssignment.personId);
                          return person?.first_name && person?.last_name
                            ? `${person.last_name} ${person.first_name}`
                            : person?.username || 'Unknown';
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex justify-between mt-8 pt-6 border-t border-steel-200">
                  <button
                    onClick={() => handleDeleteAssignment(editingAssignment)}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Assignment</span>
                  </button>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowAssignmentEditModal(false);
                        setEditingAssignment(null);
                      }}
                      className="px-4 py-2 text-slate-700 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAssignmentEdit}
                      className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Category Modal */}
        {showTaskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-7xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {selectedTaskForEdit ? 'Edit Task Category' : 'Add Task Category'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowTaskModal(false);
                      setSelectedTaskForEdit(null);
                      setNewTaskData({
                        categoryName: '',
                        categoryColor: '#3B82F6',
                        subcategories: [{ name: '', description: '' }]
                      });
                    }}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Category Name and Color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Category Name *
                      </label>
                      <input
                        type="text"
                        value={newTaskData.categoryName}
                        onChange={(e) => setNewTaskData(prev => ({ ...prev, categoryName: e.target.value }))}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        placeholder="Enter category name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Category Color
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={newTaskData.categoryColor}
                          onChange={(e) => setNewTaskData(prev => ({ ...prev, categoryColor: e.target.value }))}
                          className="w-10 h-10 border border-steel-300 rounded-xl cursor-pointer"
                        />
                        <div className="flex items-center space-x-2 text-sm text-slate-600">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: newTaskData.categoryColor }}
                          ></div>
                          <span>{newTaskData.categoryColor}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tasks - Table Format */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Tasks
                      </label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            // Add 5 tasks at once for bulk entry
                            const newSubcategories = Array(5).fill(null).map(() => ({
                              name: '',
                              description: ''
                            }));
                            setNewTaskData(prev => ({
                              ...prev,
                              subcategories: [...prev.subcategories, ...newSubcategories]
                            }));
                          }}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-steel-100 text-slate-700 rounded-xl hover:bg-steel-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add 5 Tasks</span>
                        </button>
                        <button
                          onClick={handleAddSubcategory}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-brand-100 text-brand-700 rounded-xl hover:bg-brand-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Task</span>
                        </button>
                      </div>
                    </div>

                    {/* Table View for Tasks */}
                    <div className="border border-steel-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-steel-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-8">#</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Task Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Description</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-steel-200">
                            {newTaskData.subcategories.map((subcategory, subIndex) => (
                              <tr key={subIndex} className={`hover:bg-steel-50 ${
                                newTaskData.editingSubcategoryIndex === subIndex ? 'bg-brand-50' : ''
                              }`}>
                                <td className="px-3 py-2 text-sm text-slate-500">{subIndex + 1}</td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={subcategory.name}
                                    onChange={(e) => handleUpdateSubcategory(subIndex, 'name', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="Enter name"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={subcategory.description || ''}
                                    onChange={(e) => handleUpdateSubcategory(subIndex, 'description', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-transparent"
                                    placeholder="Optional description"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center space-x-1">
                                    <button
                                      onClick={() => {
                                        // Duplicate this row
                                        const newSubcategory = { ...subcategory };
                                        const newSubcategories = [...newTaskData.subcategories];
                                        newSubcategories.splice(subIndex + 1, 0, newSubcategory);
                                        setNewTaskData(prev => ({ ...prev, subcategories: newSubcategories }));
                                      }}
                                      className="p-1 text-brand-600 hover:bg-brand-100 rounded transition-colors"
                                      title="Duplicate row"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                    {newTaskData.subcategories.length > 1 && (
                                      <button
                                        onClick={() => handleRemoveSubcategory(subIndex)}
                                        className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                        title="Delete row"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {newTaskData.subcategories.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          No tasks added. Click "Add Task" to start.
                        </div>
                      )}
                    </div>

                    {/* Quick Add Section for Bulk Import */}
                    <div className="mt-3 p-3 bg-steel-50 rounded-xl">
                      <div className="text-xs text-slate-600 mb-2">
                        <strong>Quick Add:</strong> Paste multiple task names (one per line)
                      </div>
                      <div className="flex space-x-2">
                        <textarea
                          id="bulkSubcategoriesInput"
                          className="flex-1 px-2 py-1 text-sm border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                          placeholder="e.g.&#10;Base&#10;M1&#10;M2&#10;M3&#10;M4&#10;M5&#10;Top&#10;Nacelle"
                          rows="3"
                        />
                        <button
                          onClick={() => {
                            const textarea = document.getElementById('bulkSubcategoriesInput');
                            const lines = textarea.value.split('\n').filter(line => line.trim());
                            if (lines.length > 0) {
                              const newSubcategories = lines.map(name => ({
                                name: name.trim(),
                                description: ''
                              }));
                              setNewTaskData(prev => ({
                                ...prev,
                                subcategories: [...prev.subcategories, ...newSubcategories]
                              }));
                              textarea.value = '';
                            }
                          }}
                          className="px-3 py-1 bg-brand-600 text-white text-sm rounded-xl hover:bg-brand-700 transition-colors"
                        >
                          Add All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-steel-200">
                  <button
                    onClick={() => setShowTaskModal(false)}
                    className="px-4 py-2 text-slate-700 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTaskCategory}
                    disabled={!newTaskData.categoryName.trim()}
                    className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Save Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl border border-steel-200 shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Export Work Plan</h2>
                  <p className="text-sm text-slate-500">
                    {format(selectedDate, 'EEEE, dd MMMM yyyy')}
                  </p>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-steel-100 rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Preview Section - Main focus */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-900">Preview</label>
                    <span className="text-xs text-slate-500">{getAllAssignments().length} tasks</span>
                  </div>
                  <div className="bg-steel-50 rounded-xl border border-steel-200 p-4 font-mono text-sm text-slate-800 whitespace-pre-wrap overflow-y-auto" style={{ minHeight: '350px', maxHeight: '450px' }}>
                    {generateExportText()}
                  </div>
                </div>

                {/* Export Formats - Compact */}
                <div>
                  <label className="text-sm font-medium text-slate-900 mb-3 block">Export as</label>
                  <div className="flex flex-wrap gap-2">
                    {/* Copy to Clipboard */}
                    <button
                      onClick={() => {
                        const text = generateExportText();
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(text).then(() => {
                            alert('Copied to clipboard!');
                          }).catch(() => {
                            // Fallback
                            const textarea = document.createElement('textarea');
                            textarea.value = text;
                            document.body.appendChild(textarea);
                            textarea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textarea);
                            alert('Copied to clipboard!');
                          });
                        } else {
                          // Fallback for older browsers / non-HTTPS
                          const textarea = document.createElement('textarea');
                          textarea.value = text;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textarea);
                          alert('Copied to clipboard!');
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-steel-100 text-slate-700 rounded-md hover:bg-steel-200 transition-colors text-sm font-medium"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>

                    {/* Download as TXT */}
                    <button
                      onClick={() => {
                        const text = generateExportText();
                        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `work-plan-${format(selectedDate, 'yyyy-MM-dd')}.txt`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-steel-100 text-slate-700 rounded-md hover:bg-steel-200 transition-colors text-sm font-medium"
                    >
                      <FileText className="w-4 h-4" />
                      TXT
                    </button>

                    {/* Download as PDF */}
                    <button
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        const projectName = projects.find(p => p.id.toString() === selectedProject)?.name || 'Project';
                        const allAssignments = getAllAssignments();

                        // Group by turbine
                        const turbineGroups = {};
                        allAssignments.forEach(a => {
                          if (!turbineGroups[a.turbineId]) turbineGroups[a.turbineId] = [];
                          turbineGroups[a.turbineId].push(a);
                        });

                        // Build task table with individual times per person
                        let taskRows = '';
                        Object.keys(turbineGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(turbineId => {
                          turbineGroups[turbineId].forEach(a => {
                            const peopleWithTimes = (a.assignedPeople || []).map(pid => {
                              const p = allPeople.find(x => x.id === pid);
                              if (!p) return null;
                              const individualTimes = a.individual_times?.[pid];
                              const startTime = individualTimes?.start_time || a.startTime;
                              const endTime = individualTimes?.end_time || a.endTime;
                              return { name: `${p.first_name} ${p.last_name}`, time: `${startTime}-${endTime}` };
                            }).filter(Boolean);

                            const peopleHtml = peopleWithTimes.length > 0
                              ? peopleWithTimes.map(p => `<div style="margin-bottom:2px;"><span style="color:#6b7280;font-size:11px;">${p.time}</span> ${p.name}</div>`).join('')
                              : '<span style="color:#9ca3af;">-</span>';

                            taskRows += `<tr>
                              <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-weight:500;">T${a.turbineId}</td>
                              <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;"><span style="background:${a.categoryColor || '#6366f1'};color:white;padding:3px 8px;border-radius:4px;font-size:12px;">${a.category}</span></td>
                              <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">${a.subcategory}</td>
                              <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">${peopleHtml}</td>
                            </tr>`;
                          });
                        });

                        // Build personnel schedule
                        const personSchedules = {};
                        allAssignments.forEach(a => {
                          (a.assignedPeople || []).forEach(pid => {
                            if (!personSchedules[pid]) personSchedules[pid] = [];
                            const individualTimes = a.individual_times?.[pid];
                            const startTime = individualTimes?.start_time || a.startTime;
                            const endTime = individualTimes?.end_time || a.endTime;
                            personSchedules[pid].push({
                              startTime, endTime,
                              turbineId: a.turbineId,
                              task: a.subcategory
                            });
                          });
                        });

                        let personnelRows = '';
                        Object.keys(personSchedules).forEach(pid => {
                          const person = allPeople.find(p => p.id === parseInt(pid));
                          if (!person) return;
                          const schedule = personSchedules[pid].sort((a, b) => a.startTime.localeCompare(b.startTime));

                          // Calculate total hours
                          let totalMinutes = 0;
                          schedule.forEach(task => {
                            const [sh, sm] = task.startTime.split(':').map(Number);
                            const [eh, em] = task.endTime.split(':').map(Number);
                            totalMinutes += (eh * 60 + em) - (sh * 60 + sm);
                          });
                          const totalHours = Math.floor(totalMinutes / 60);
                          const totalMins = totalMinutes % 60;

                          const tasksHtml = schedule.map(t => `<div style="margin-bottom:2px;"><span style="color:#6b7280;font-size:11px;">${t.startTime}-${t.endTime}</span> T${t.turbineId} - ${t.task}</div>`).join('');

                          personnelRows += `<tr>
                            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-weight:500;vertical-align:top;">${person.first_name} ${person.last_name}</td>
                            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;">${tasksHtml}</td>
                            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-weight:500;vertical-align:top;">${totalHours}h${totalMins > 0 ? ` ${totalMins}m` : ''}</td>
                          </tr>`;
                        });

                        printWindow.document.write(`<!DOCTYPE html><html><head>
                          <title>Work Plan ${format(selectedDate, 'yyyy-MM-dd')}</title>
                          <style>
                            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 40px; color: #111827; }
                            h1 { font-size: 24px; font-weight: 600; margin-bottom: 4px; }
                            h2 { font-size: 16px; font-weight: 600; margin: 24px 0 12px 0; color: #374151; }
                            .subtitle { color: #6b7280; margin-bottom: 24px; font-size: 14px; }
                            table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
                            th { background: #f9fafb; padding: 12px; text-align: left; font-weight: 500; font-size: 13px; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
                            .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px; }
                            @media print { body { margin: 24px; } .page-break { page-break-before: always; } }
                          </style>
                        </head><body>
                          <h1>${projectName}</h1>
                          <div class="subtitle">${format(selectedDate, 'EEEE, dd MMMM yyyy')} • ${allAssignments.length} tasks • ${Object.keys(personSchedules).length} personnel</div>

                          <h2>Task Details</h2>
                          <table>
                            <thead><tr><th>Turbine</th><th>Category</th><th>Task</th><th>Personnel & Times</th></tr></thead>
                            <tbody>${taskRows || '<tr><td colspan="4" style="padding:24px;text-align:center;color:#9ca3af;">No tasks scheduled</td></tr>'}</tbody>
                          </table>

                          <h2>Personnel Schedule</h2>
                          <table>
                            <thead><tr><th>Person</th><th>Tasks</th><th>Total</th></tr></thead>
                            <tbody>${personnelRows || '<tr><td colspan="3" style="padding:24px;text-align:center;color:#9ca3af;">No personnel assigned</td></tr>'}</tbody>
                          </table>

                          <div class="footer">Generated ${format(new Date(), 'HH:mm, dd MMM yyyy')}</div>
                        </body></html>`);
                        printWindow.document.close();
                        printWindow.print();
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-steel-100 text-slate-700 rounded-md hover:bg-steel-200 transition-colors text-sm font-medium"
                    >
                      <FileDown className="w-4 h-4" />
                      PDF
                    </button>

                    {/* Download as CSV */}
                    <button
                      onClick={() => {
                        const allAssignments = getAllAssignments();
                        // CSV with one row per person per task (with individual times)
                        let csv = 'Turbine,Category,Task,Person,Start Time,End Time,Duration (min)\n';
                        allAssignments.forEach(a => {
                          if (a.assignedPeople && a.assignedPeople.length > 0) {
                            a.assignedPeople.forEach(pid => {
                              const p = allPeople.find(x => x.id === pid);
                              if (!p) return;
                              const individualTimes = a.individual_times?.[pid];
                              const startTime = individualTimes?.start_time || a.startTime;
                              const endTime = individualTimes?.end_time || a.endTime;

                              // Calculate duration
                              const [sh, sm] = startTime.split(':').map(Number);
                              const [eh, em] = endTime.split(':').map(Number);
                              const duration = (eh * 60 + em) - (sh * 60 + sm);

                              csv += `"T${a.turbineId}","${a.category}","${a.subcategory}","${p.first_name} ${p.last_name}","${startTime}","${endTime}","${duration}"\n`;
                            });
                          } else {
                            // Task with no assigned people
                            csv += `"T${a.turbineId}","${a.category}","${a.subcategory}","","${a.startTime}","${a.endTime}",""\n`;
                          }
                        });
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const el = document.createElement('a');
                        el.href = url;
                        el.download = `work-plan-${format(selectedDate, 'yyyy-MM-dd')}.csv`;
                        document.body.appendChild(el);
                        el.click();
                        document.body.removeChild(el);
                        URL.revokeObjectURL(url);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-steel-100 text-slate-700 rounded-md hover:bg-steel-200 transition-colors text-sm font-medium"
                    >
                      <BarChart3 className="w-4 h-4" />
                      CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Copy Tasks Modal - Two column layout */}
        {showCopyModal && (() => {
          const availability = copyTargetDate ? analyzePeopleAvailability(copyTargetDate, copyTasksData.tasks) : { allTasks: [], summary: { ok: 0, duplicates: 0, noOneAvailable: 0, someUnavailable: 0 } };
          const { allTasks, summary } = availability;
          const hasAnyIssues = summary.duplicates > 0 || summary.noOneAvailable > 0 || summary.someUnavailable > 0;

          // Group tasks by turbine for display
          const tasksByTurbine = {};
          allTasks.forEach(task => {
            if (!tasksByTurbine[task.turbineId]) {
              tasksByTurbine[task.turbineId] = [];
            }
            tasksByTurbine[task.turbineId].push(task);
          });
          const sortedTurbineIds = Object.keys(tasksByTurbine).sort((a, b) => parseInt(a) - parseInt(b));

          return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl border border-steel-200 shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Copy Incomplete Tasks</h2>
                  <p className="text-sm text-slate-500">
                    From {format(selectedDate, 'EEE, MMM d, yyyy')}
                  </p>
                </div>
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-steel-100 rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content - Two columns */}
              <div className="flex-1 overflow-hidden grid grid-cols-5">
                {/* Left column: Date selection and summary (2/5 width) */}
                <div className="col-span-2 p-6 space-y-4 overflow-y-auto border-r border-steel-200">
                  {/* Target date picker */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Copy to date
                    </label>
                    <input
                      type="date"
                      value={copyTargetDate ? format(copyTargetDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        const newDate = e.target.value ? new Date(e.target.value + 'T12:00:00') : null;
                        setCopyTargetDate(newDate);
                      }}
                      min={format(addDays(selectedDate, 1), 'yyyy-MM-dd')}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                    {copyTargetDate && (
                      <p className="text-sm text-slate-500 mt-1">
                        {format(copyTargetDate, 'EEEE, MMMM d, yyyy')}
                      </p>
                    )}
                  </div>

                  {/* Summary */}
                  {copyTargetDate && copyTasksData.tasks.length > 0 && (
                    <div className="bg-steel-50 border border-steel-200 rounded-xl p-4">
                      <div className="text-sm font-medium text-slate-700 mb-3">Summary</div>
                      <div className="space-y-2 text-sm">
                        {summary.ok > 0 && (
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span>{summary.ok} task{summary.ok !== 1 ? 's' : ''} ready to copy</span>
                          </div>
                        )}
                        {summary.duplicates > 0 && (
                          <div className="flex items-center gap-2 text-amber-600">
                            <Copy className="w-4 h-4" />
                            <span>{summary.duplicates} duplicate{summary.duplicates !== 1 ? 's' : ''} (already exist)</span>
                          </div>
                        )}
                        {summary.someUnavailable > 0 && (
                          <div className="flex items-center gap-2 text-orange-600">
                            <User className="w-4 h-4" />
                            <span>{summary.someUnavailable} with some people unavailable</span>
                          </div>
                        )}
                        {summary.noOneAvailable > 0 && (
                          <div className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{summary.noOneAvailable} with no people available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* All OK message */}
                  {copyTargetDate && copyTasksData.tasks.length > 0 && !hasAnyIssues && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">All tasks ready!</span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        All people are available on the target date.
                      </p>
                    </div>
                  )}
                </div>

                {/* Right column: Task list (3/5 width) */}
                <div className="col-span-3 p-4 overflow-y-auto bg-steel-50">
                  <div className="text-sm font-medium text-slate-700 mb-3">
                    Tasks to copy ({copyTasksData.tasks.length})
                  </div>

                  {copyTasksData.tasks.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Copy className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>No tasks to copy</p>
                    </div>
                  ) : !copyTargetDate ? (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>Select a target date to see task status</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sortedTurbineIds.map(turbineId => (
                        <div key={turbineId} className="bg-white border border-steel-200 rounded-xl overflow-hidden shadow-sm">
                          {/* Turbine header */}
                          <div className="bg-steel-100 px-3 py-1.5 border-b border-steel-200">
                            <span className="font-semibold text-slate-800 text-sm">T{turbineId}</span>
                          </div>

                          {/* Tasks for this turbine */}
                          <div className="divide-y divide-steel-100">
                            {tasksByTurbine[turbineId].map(task => {
                              // Determine background and border color based on status
                              const statusStyles = {
                                ok: 'bg-white border-l-4 border-l-green-500',
                                duplicate: 'bg-amber-50 border-l-4 border-l-amber-500',
                                some_unavailable: 'bg-orange-50 border-l-4 border-l-orange-500',
                                no_people: 'bg-red-50 border-l-4 border-l-red-500'
                              };

                              return (
                                <div key={task.taskId} className={`p-3 ${statusStyles[task.status]}`}>
                                  {/* Task header with name, time and delete button */}
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-slate-900 text-sm truncate">
                                          {task.subcategory}
                                        </span>
                                        <span className="text-xs text-slate-500">
                                          {task.startTime}-{task.endTime}
                                        </span>
                                        {/* Status badges */}
                                        {task.status === 'ok' && (
                                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" /> OK
                                          </span>
                                        )}
                                        {task.isDuplicate && (
                                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                            Duplicate ({task.existingTime})
                                          </span>
                                        )}
                                        {task.status === 'no_people' && (
                                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                            No people
                                          </span>
                                        )}
                                      </div>
                                      {task.category && (
                                        <div className="text-xs text-slate-500 mt-0.5">{task.category}</div>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => {
                                        setCopyTasksData(prev => ({
                                          ...prev,
                                          tasks: prev.tasks.filter(t => t.id !== task.taskId)
                                        }));
                                      }}
                                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                      title="Remove from copy list"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* People details (only show if there are issues or people assigned) */}
                                  {(task.notOnProject.length > 0 || task.hasConflicts.length > 0 || task.available.length > 0) && (
                                    <div className="mt-2 space-y-1 text-xs">
                                      {task.notOnProject.length > 0 && (
                                        <div className="flex items-start gap-1">
                                          <X className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                                          <span className="text-slate-600">
                                            <span className="text-red-600">{task.notOnProject.map(p => p.name).join(', ')}</span>
                                            <span className="text-slate-400"> — not on project</span>
                                          </span>
                                        </div>
                                      )}
                                      {task.hasConflicts.length > 0 && (
                                        <div className="flex items-start gap-1">
                                          <AlertTriangle className="w-3 h-3 text-orange-500 mt-0.5 flex-shrink-0" />
                                          <span className="text-slate-600">
                                            <span className="text-orange-600">{task.hasConflicts.map(p => p.name).join(', ')}</span>
                                            <span className="text-slate-400"> — time conflict</span>
                                          </span>
                                        </div>
                                      )}
                                      {task.available.length > 0 && (
                                        <div className="flex items-start gap-1">
                                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                          <span className="text-slate-600">
                                            <span className="text-green-600">{task.available.map(p => p.name).join(', ')}</span>
                                            <span className="text-slate-400"> — will be assigned</span>
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-steel-200 bg-steel-50 rounded-b-lg flex-shrink-0">
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="px-4 py-2 text-slate-700 bg-white border border-steel-300 rounded-xl hover:bg-steel-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeCopyTasks}
                  disabled={!copyTargetDate || copyTasksData.tasks.length === 0}
                  className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  {copyTasksData.tasks.length === 0 ? 'No Tasks' : `Copy ${copyTasksData.tasks.length} Task${copyTasksData.tasks.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        );
        })()}

        {/* Fullscreen Timeline Modal */}
        <TimelineFullscreen
          isOpen={isTimelineFullscreen}
          onClose={() => setIsTimelineFullscreen(false)}
          selectedDate={selectedDate}
          projectPeople={projectPeople}
          projectCars={projectCars}
          timelineStartTime={timelineStartTime}
          timelineEndTime={timelineEndTime}
          setTimelineStartTime={setTimelineStartTime}
          setTimelineEndTime={setTimelineEndTime}
          turbineAssignments={turbineAssignments}
          vehicleAssignments={vehicleAssignments}
          getAllAssignments={getAllAssignments}
          getIndividualTimes={getIndividualTimes}
          isPersonAssignedAtTime={isPersonAssignedAtTime}
          isCarAssignedAtTime={isCarAssignedAtTime}
          getCarAssignmentAtTime={getCarAssignmentAtTime}
          handleEditAssignment={handleEditAssignment}
          handleRemovePersonFromTaskAtTime={handleRemovePersonFromTaskAtTime}
          handleTaskDrop={handleTaskDrop}
          handleTimeSlotDragOver={handleTimeSlotDragOver}
          handleTaskMoveStart={handleTaskMoveStart}
          groupTimeSlotsByAssignment={groupTimeSlotsByAssignment}
          groupCarTimeSlotsByAssignment={groupCarTimeSlotsByAssignment}
          timeToMinutes={timeToMinutes}
          draggedTask={draggedTask}
          setDraggedTask={setDraggedTask}
          dropZone={dropZone}
          setDropZone={setDropZone}
          dragMode={dragMode}
          setDragMode={setDragMode}
          resizeHandle={resizeHandle}
          setResizeHandle={setResizeHandle}
          dragStartX={dragStartX}
          setDragStartX={setDragStartX}
        />

        {/* Project Overview */}
        {showProjectOverview && (() => {
          // Helper to get all assignments from allProjectAssignments
          const getAllProjectAssignmentsFlat = () => {
            const allAssignments = [];
            Object.keys(allProjectAssignments).forEach(turbineId => {
              allProjectAssignments[turbineId].forEach(assignment => {
                allAssignments.push({
                  ...assignment,
                  turbineName: `T${turbineId}`
                });
              });
            });
            return allAssignments;
          };

          const getOverviewAssignmentsForTurbine = (turbineId) => {
            return allProjectAssignments[turbineId] || [];
          };

          const allOverviewAssignments = getAllProjectAssignmentsFlat();

          return (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/30 transition-opacity duration-300"
              onClick={() => {
                setShowProjectOverview(false);
                setExpandedOverviewTurbine(null);
                setSelectedOverviewTask(null);
                setOverviewActiveTab('turbines');
              }}
            />
            {/* Slide-in Panel */}
            <div
              className="ml-auto relative bg-steel-100 h-full overflow-auto shadow-2xl animate-slide-in-right"
              style={{ width: '85%', maxWidth: '1400px' }}
            >
              {/* Header */}
              <div className="bg-white border-b border-steel-200 sticky top-0 z-10">
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h1 className="text-xl font-black uppercase tracking-wide text-slate-900 flex items-center gap-2">
                        <LayoutGrid className="w-5 h-5 text-brand-600" />
                        Project Overview
                      </h1>
                      <p className="text-sm text-slate-600">
                        <span className="font-semibold text-brand-600">{projects.find(p => p.id.toString() === selectedProject)?.name}</span>
                        <span className="mx-2">•</span>
                        <span className="text-slate-500">All dates</span>
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProjectOverview(false);
                        setExpandedOverviewTurbine(null);
                        setSelectedOverviewTask(null);
                        setOverviewActiveTab('turbines');
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                      <span>Close</span>
                    </button>
                  </div>
                  {/* Tab Navigation */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => setOverviewActiveTab('turbines')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                        overviewActiveTab === 'turbines'
                          ? 'bg-steel-100 text-slate-900 border-b-2 border-brand-600'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-steel-50'
                      }`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                      Turbines
                    </button>
                    <button
                      onClick={() => setOverviewActiveTab('analytics')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
                        overviewActiveTab === 'analytics'
                          ? 'bg-steel-100 text-slate-900 border-b-2 border-brand-600'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-steel-50'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </button>
                  </div>
                </div>
              </div>

            {/* Content */}
            <div className="px-4 py-3">
              {loadingProjectOverview ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-pulse text-muted-foreground">Loading project data...</div>
                </div>
              ) : (
              <>
              {/* TURBINES TAB */}
              {overviewActiveTab === 'turbines' && (
              <>
              {/* Summary Stats - Compact */}
              {(() => {
                // Helper function to parse time string to minutes
                const parseTime = (timeStr) => {
                  if (!timeStr) return 0;
                  const [h, m] = timeStr.split(':').map(Number);
                  return h * 60 + m;
                };

                // Group assignments by logical task (turbineId + category + subcategory)
                const logicalTasksMap = {};
                let totalMinutes = 0;

                allOverviewAssignments.forEach(entry => {
                  if (entry.startTime && entry.endTime) {
                    const taskDuration = parseTime(entry.endTime) - parseTime(entry.startTime);
                    const peopleCount = entry.assignedPeople?.length || 1;
                    totalMinutes += taskDuration * peopleCount;

                    const logicalKey = `${entry.turbineId}|||${entry.category || ''}|||${entry.subcategory || ''}`;
                    if (!logicalTasksMap[logicalKey]) {
                      logicalTasksMap[logicalKey] = { isCompleted: false };
                    }
                    if (entry.is_completed) {
                      logicalTasksMap[logicalKey].isCompleted = true;
                    }
                  }
                });

                const uniqueTasks = Object.keys(logicalTasksMap).length;
                const completedTasks = Object.values(logicalTasksMap).filter(t => t.isCompleted).length;
                const pendingTasks = uniqueTasks - completedTasks;
                const totalHours = Math.floor(totalMinutes / 60);
                const remainingMins = totalMinutes % 60;

                return (
                  <div className="grid grid-cols-5 gap-2 mb-3">
                    <div className="bg-white rounded-md p-3 border border-border shadow-sm">
                      <div className="text-xl font-semibold text-brand-600">{turbines.length}</div>
                      <div className="text-xs text-muted-foreground">Turbines</div>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-border shadow-sm">
                      <div className="text-xl font-semibold text-slate-700">{uniqueTasks}</div>
                      <div className="text-xs text-muted-foreground">Unique Tasks</div>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-border shadow-sm">
                      <div className="text-xl font-semibold text-emerald-600">{completedTasks}</div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-border shadow-sm">
                      <div className="text-xl font-semibold text-amber-600">{pendingTasks}</div>
                      <div className="text-xs text-muted-foreground">Pending</div>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-border shadow-sm">
                      <div className="text-xl font-semibold text-brand-600">
                        {totalHours}h {remainingMins > 0 ? `${remainingMins}m` : ''}
                      </div>
                      <div className="text-xs text-muted-foreground">Man-Hours</div>
                    </div>
                  </div>
                );
              })()}

              {/* All Turbines Grid - Compact */}
              <div className="bg-white rounded-md border border-border shadow-sm mb-2">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                  <h2 className="text-sm font-medium text-foreground">All Turbines</h2>
                  <span className="text-xs text-muted-foreground">Click to expand</span>
                </div>
                <div className="p-2">
                  <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-1.5">
                    {turbines.map((turbine, index) => {
                      const assignments = getOverviewAssignmentsForTurbine(turbine.id);
                      const completedCount = assignments.filter(a => a.is_completed).length;
                      const totalCount = assignments.length;
                      const isExpanded = expandedOverviewTurbine === turbine.id;

                      return (
                        <div
                          key={turbine.id}
                          onClick={() => {
                            setExpandedOverviewTurbine(isExpanded ? null : turbine.id);
                            setSelectedOverviewTask(null);
                          }}
                          className={`px-2 py-1.5 rounded-md border cursor-pointer transition-all ${
                            isExpanded
                              ? 'ring-2 ring-primary bg-primary/10 border-primary'
                              : totalCount === 0
                              ? 'bg-muted/50 border-border hover:bg-muted'
                              : completedCount === totalCount
                              ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground text-xs">T{turbine.turbine_number}</span>
                            {totalCount > 0 ? (
                              <span className={`text-[10px] px-1 py-0.5 rounded font-medium ${
                                completedCount === totalCount
                                  ? 'bg-emerald-200 text-emerald-800'
                                  : 'bg-amber-200 text-amber-800'
                              }`}>
                                {completedCount}/{totalCount}
                              </span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">-</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Expanded Turbine Details - Master-Detail View */}
              {expandedOverviewTurbine && (() => {
                const turbine = turbines.find(t => t.id === expandedOverviewTurbine);
                const turbineIndex = turbines.findIndex(t => t.id === expandedOverviewTurbine);
                const assignments = getOverviewAssignmentsForTurbine(expandedOverviewTurbine);

                // Group assignments by category+subcategory
                const groupedTasks = {};
                assignments.forEach(task => {
                  const key = `${task.category || 'Uncategorized'}|||${task.subcategory || 'Unknown'}`;
                  if (!groupedTasks[key]) {
                    groupedTasks[key] = {
                      category: task.category || 'Uncategorized',
                      subcategory: task.subcategory || 'Unknown',
                      categoryColor: task.categoryColor || '#6B7280',
                      entries: []
                    };
                  }
                  groupedTasks[key].entries.push(task);
                });

                const taskGroups = Object.values(groupedTasks);
                const selectedGroup = taskGroups.find(g =>
                  `${g.category}|||${g.subcategory}` === selectedOverviewTask
                );

                if (!turbine) return null;

                // Calculate total man-hours (task duration × number of assigned people)
                const totalMinutes = assignments.reduce((acc, entry) => {
                  if (entry.startTime && entry.endTime) {
                    const [startH, startM] = entry.startTime.split(':').map(Number);
                    const [endH, endM] = entry.endTime.split(':').map(Number);
                    const startMins = startH * 60 + startM;
                    const endMins = endH * 60 + endM;
                    const taskDuration = endMins - startMins;
                    const peopleCount = entry.assignedPeople?.length || 1;
                    return acc + (taskDuration * peopleCount);
                  }
                  return acc;
                }, 0);
                const totalHours = Math.floor(totalMinutes / 60);
                const remainingMins = totalMinutes % 60;

                return (
                  <div className="bg-white rounded-md border border-border shadow-sm flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}>
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-border bg-muted/50 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <h2 className="text-lg font-semibold text-foreground">
                              T{turbine.turbine_number}
                            </h2>
                            <p className="text-sm text-muted-foreground">
                              {taskGroups.length} types • {assignments.length} entries • {assignments.filter(a => a.is_completed).length} done
                            </p>
                          </div>
                          <div className="px-3 py-2 bg-primary/10 rounded-md">
                            <div className="text-sm text-muted-foreground">Total time</div>
                            <div className="text-lg font-semibold text-primary">
                              {totalHours}h {remainingMins > 0 ? `${remainingMins}m` : ''}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setExpandedOverviewTurbine(null);
                            setSelectedOverviewTask(null);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Close
                        </button>
                      </div>
                    </div>

                    {/* Master-Detail Content */}
                    <div className="grid grid-cols-3 divide-x divide-border flex-1 overflow-hidden">
                      {/* LEFT - Task Groups (Master) */}
                      <div className="col-span-1 overflow-y-auto">
                        {taskGroups.length === 0 ? (
                          <div className="p-4 text-center text-muted-foreground text-base">
                            No tasks assigned
                          </div>
                        ) : (
                          <div className="divide-y divide-border">
                            {taskGroups.map((group, groupIndex) => {
                              const groupKey = `${group.category}|||${group.subcategory}`;
                              const completedCount = group.entries.filter(e => e.is_completed).length;
                              const allCompleted = completedCount === group.entries.length;

                              // Calculate total man-hours for this task group (task duration × number of assigned people)
                              const groupTotalMinutes = group.entries.reduce((acc, entry) => {
                                if (entry.startTime && entry.endTime) {
                                  const [startH, startM] = entry.startTime.split(':').map(Number);
                                  const [endH, endM] = entry.endTime.split(':').map(Number);
                                  const startMins = startH * 60 + startM;
                                  const endMins = endH * 60 + endM;
                                  const taskDuration = endMins - startMins;
                                  const peopleCount = entry.assignedPeople?.length || 1;
                                  return acc + (taskDuration * peopleCount);
                                }
                                return acc;
                              }, 0);
                              const groupHours = Math.floor(groupTotalMinutes / 60);
                              const groupMins = groupTotalMinutes % 60;

                              return (
                                <div
                                  key={groupKey}
                                  onClick={() => setSelectedOverviewTask(groupKey)}
                                  className={`px-3 py-2 cursor-pointer transition-colors ${
                                    selectedOverviewTask === groupKey
                                      ? 'bg-primary/10 border-l-3 border-primary'
                                      : allCompleted
                                      ? 'bg-emerald-50/50 hover:bg-emerald-50'
                                      : 'hover:bg-muted/50'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-muted-foreground">{groupIndex + 1}.</span>
                                    <span className={`text-xs font-medium ${allCompleted ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                                      {completedCount}/{group.entries.length}
                                    </span>
                                  </div>
                                  <div
                                    className="inline-block px-1.5 py-0.5 rounded text-xs text-white mb-1"
                                    style={{ backgroundColor: group.categoryColor }}
                                  >
                                    {group.category}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium text-foreground truncate">
                                      {group.subcategory}
                                    </div>
                                    <div className="text-xs text-muted-foreground ml-2 whitespace-nowrap">
                                      {groupHours}h{groupMins > 0 ? ` ${groupMins}m` : ''}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* RIGHT - Task Entries (Detail) */}
                      <div className="col-span-2 p-4 overflow-y-auto bg-muted/30">
                        {!selectedGroup ? (
                          <div className="h-full flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                              <ChevronLeft className="w-8 h-8 mx-auto mb-2 opacity-40" />
                              <p className="text-sm">Select a task</p>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {/* Task Header */}
                            {(() => {
                              // Calculate total man-hours for selected group (task duration × number of assigned people)
                              const selectedGroupTotalMinutes = selectedGroup.entries.reduce((acc, entry) => {
                                if (entry.startTime && entry.endTime) {
                                  const [startH, startM] = entry.startTime.split(':').map(Number);
                                  const [endH, endM] = entry.endTime.split(':').map(Number);
                                  const startMins = startH * 60 + startM;
                                  const endMins = endH * 60 + endM;
                                  const taskDuration = endMins - startMins;
                                  const peopleCount = entry.assignedPeople?.length || 1;
                                  return acc + (taskDuration * peopleCount);
                                }
                                return acc;
                              }, 0);
                              const selectedGroupHours = Math.floor(selectedGroupTotalMinutes / 60);
                              const selectedGroupMins = selectedGroupTotalMinutes % 60;

                              return (
                            <div className="mb-3">
                              <div className="flex items-center gap-2 mb-1">
                                <div
                                  className="px-2 py-1 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: selectedGroup.categoryColor }}
                                >
                                  {selectedGroup.category}
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {selectedGroup.entries.length} entries
                                </span>
                                <span className="text-sm font-medium text-primary">
                                  • {selectedGroupHours}h{selectedGroupMins > 0 ? ` ${selectedGroupMins}m` : ''}
                                </span>
                              </div>
                              <h3 className="text-base font-semibold text-foreground">
                                {selectedGroup.subcategory}
                              </h3>
                            </div>
                              );
                            })()}

                            {/* Entries Table */}
                            <div className="rounded-xl border border-border overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-gradient-to-r from-brand-50 to-steel-50 border-b-2 border-brand-500">
                                  <tr>
                                    <th className="px-3 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wider w-8">#</th>
                                    <th className="px-3 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wider w-16">Status</th>
                                    <th className="px-3 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wider w-28">Date</th>
                                    <th className="px-3 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wider w-32">Time</th>
                                    <th className="px-3 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wider">People</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {selectedGroup.entries.map((entry, entryIndex) => (
                                    <tr
                                      key={entry.id}
                                      className={entry.is_completed ? 'bg-emerald-50/50' : 'bg-white'}
                                    >
                                      <td className="px-3 py-2 text-muted-foreground">{entryIndex + 1}</td>
                                      <td className="px-3 py-2">
                                        {entry.is_completed ? (
                                          <span className="inline-flex items-center gap-1 text-emerald-700">
                                            <CheckCircle className="w-4 h-4" />
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 text-amber-600">
                                            <AlertTriangle className="w-4 h-4" />
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className="font-medium text-foreground">
                                          {entry.date ? format(new Date(entry.date), 'dd.MM.yy') : '-'}
                                        </span>
                                        <span className="text-muted-foreground ml-1">
                                          {entry.date ? format(new Date(entry.date), 'EEE') : ''}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 font-medium text-foreground">
                                        {entry.startTime}-{entry.endTime}
                                      </td>
                                      <td className="px-3 py-2">
                                        <div className="flex flex-wrap gap-1">
                                          {entry.assignedPeople && entry.assignedPeople.length > 0 ? (
                                            entry.assignedPeople.map(personId => {
                                              const person = allPeople.find(p => p.id === personId);
                                              return (
                                                <span
                                                  key={personId}
                                                  className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded"
                                                >
                                                  {person ? `${person.first_name} ${person.last_name}` : `ID:${personId}`}
                                                </span>
                                              );
                                            })
                                          ) : (
                                            <span className="text-muted-foreground">-</span>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
              </>
              )}

              {/* ANALYTICS TAB */}
              {overviewActiveTab === 'analytics' && (() => {
                // Helper function to parse time string to minutes
                const parseTimeToMinutes = (timeStr) => {
                  if (!timeStr) return 0;
                  const [h, m] = timeStr.split(':').map(Number);
                  return h * 60 + m;
                };

                // ========== PLANNED DATA (from Task Lists) ==========
                // Calculate planned tasks and hours from all turbines' task_list_data
                let totalPlannedTasks = 0;
                let totalPlannedMinutes = 0;
                const plannedByTurbine = {};
                const plannedByTask = {}; // key: "category|||subcategory"

                turbines.forEach(turbine => {
                  if (turbine.task_list_data?.categories) {
                    let turbinePlannedTasks = 0;
                    let turbinePlannedMinutes = 0;

                    turbine.task_list_data.categories.forEach(category => {
                      if (category.subcategories) {
                        category.subcategories.forEach(sub => {
                          turbinePlannedTasks++;
                          const estimatedMinutes = sub.estimated_hours ? parseFloat(sub.estimated_hours) * 60 : 0;
                          turbinePlannedMinutes += estimatedMinutes;

                          // Track by task type
                          const taskKey = `${category.name}|||${sub.name}`;
                          if (!plannedByTask[taskKey]) {
                            plannedByTask[taskKey] = {
                              category: category.name,
                              subcategory: sub.name,
                              categoryColor: category.color || '#6B7280',
                              plannedCount: 0,
                              plannedMinutes: 0,
                              scheduledCount: 0,
                              scheduledMinutes: 0,
                              completedCount: 0,
                              completedMinutes: 0
                            };
                          }
                          plannedByTask[taskKey].plannedCount++;
                          plannedByTask[taskKey].plannedMinutes += estimatedMinutes;
                        });
                      }
                    });

                    plannedByTurbine[turbine.id] = {
                      turbineNumber: turbine.turbine_number,
                      plannedTasks: turbinePlannedTasks,
                      plannedMinutes: turbinePlannedMinutes,
                      scheduledTasks: 0,
                      scheduledMinutes: 0,
                      completedTasks: 0,
                      completedMinutes: 0
                    };

                    totalPlannedTasks += turbinePlannedTasks;
                    totalPlannedMinutes += turbinePlannedMinutes;
                  }
                });

                // ========== ACTUAL DATA (from Assignments) ==========
                // Group assignments by logical task (turbineId + category + subcategory)
                // Same task on different days = 1 logical task
                const logicalTasks = {}; // key: "turbineId|||category|||subcategory"
                let totalScheduledMinutes = 0;
                let totalCompletedMinutes = 0;

                allOverviewAssignments.forEach(entry => {
                  if (entry.startTime && entry.endTime) {
                    const taskDuration = parseTimeToMinutes(entry.endTime) - parseTimeToMinutes(entry.startTime);
                    const logicalTaskKey = `${entry.turbineId}|||${entry.category || 'Uncategorized'}|||${entry.subcategory || 'Unknown'}`;

                    // Track logical tasks (unique by turbine + category + subcategory)
                    if (!logicalTasks[logicalTaskKey]) {
                      logicalTasks[logicalTaskKey] = {
                        turbineId: entry.turbineId,
                        category: entry.category || 'Uncategorized',
                        subcategory: entry.subcategory || 'Unknown',
                        totalMinutes: 0,
                        completedMinutes: 0,
                        isCompleted: false, // Will be true if ANY assignment is completed
                        assignmentCount: 0
                      };
                    }
                    logicalTasks[logicalTaskKey].totalMinutes += taskDuration;
                    logicalTasks[logicalTaskKey].assignmentCount++;
                    if (entry.is_completed) {
                      logicalTasks[logicalTaskKey].isCompleted = true;
                      logicalTasks[logicalTaskKey].completedMinutes += taskDuration;
                    }

                    totalScheduledMinutes += taskDuration;
                    if (entry.is_completed) {
                      totalCompletedMinutes += taskDuration;
                    }
                  }
                });

                // Now count unique logical tasks per turbine and per task type
                Object.values(logicalTasks).forEach(task => {
                  // Update turbine stats
                  if (plannedByTurbine[task.turbineId]) {
                    plannedByTurbine[task.turbineId].scheduledTasks++;
                    plannedByTurbine[task.turbineId].scheduledMinutes += task.totalMinutes;
                    if (task.isCompleted) {
                      plannedByTurbine[task.turbineId].completedTasks++;
                      plannedByTurbine[task.turbineId].completedMinutes += task.completedMinutes;
                    }
                  }

                  // Update task type stats
                  const taskTypeKey = `${task.category}|||${task.subcategory}`;
                  if (plannedByTask[taskTypeKey]) {
                    plannedByTask[taskTypeKey].scheduledCount++;
                    plannedByTask[taskTypeKey].scheduledMinutes += task.totalMinutes;
                    if (task.isCompleted) {
                      plannedByTask[taskTypeKey].completedCount++;
                      plannedByTask[taskTypeKey].completedMinutes += task.completedMinutes;
                    }
                  }
                });

                // Count totals from logical tasks
                const totalScheduledTasks = Object.keys(logicalTasks).length;
                const totalCompletedTasks = Object.values(logicalTasks).filter(t => t.isCompleted).length;

                const totalPlannedHours = Math.floor(totalPlannedMinutes / 60);
                const totalPlannedRemainingMins = Math.round(totalPlannedMinutes % 60);
                const totalScheduledHours = Math.floor(totalScheduledMinutes / 60);
                const totalScheduledRemainingMins = totalScheduledMinutes % 60;
                const totalCompletedHours = Math.floor(totalCompletedMinutes / 60);
                const totalCompletedRemainingMins = totalCompletedMinutes % 60;

                // Calculate coverage percentages (using logical task counts)
                const tasksCoverage = totalPlannedTasks > 0 ? Math.round((totalScheduledTasks / totalPlannedTasks) * 100) : 0;
                const tasksCompleted = totalPlannedTasks > 0 ? Math.round((totalCompletedTasks / totalPlannedTasks) * 100) : 0;
                const hoursCoverage = totalPlannedMinutes > 0 ? Math.round((totalScheduledMinutes / totalPlannedMinutes) * 100) : 0;
                const hoursCompleted = totalPlannedMinutes > 0 ? Math.round((totalCompletedMinutes / totalPlannedMinutes) * 100) : 0;

                // ========== LEGACY CALCULATIONS (man-hours with people count) ==========
                // Calculate all analytics data - total man-hours (task duration × number of assigned people)
                const totalProjectMinutes = allOverviewAssignments.reduce((acc, entry) => {
                  if (entry.startTime && entry.endTime) {
                    const taskDuration = parseTimeToMinutes(entry.endTime) - parseTimeToMinutes(entry.startTime);
                    const peopleCount = entry.assignedPeople?.length || 1;
                    return acc + (taskDuration * peopleCount);
                  }
                  return acc;
                }, 0);
                const totalProjectHours = Math.floor(totalProjectMinutes / 60);
                const totalProjectRemainingMins = totalProjectMinutes % 60;

                // Team efficiency - hours per person
                const personHours = {};
                allOverviewAssignments.forEach(entry => {
                  if (entry.assignedPeople && entry.startTime && entry.endTime) {
                    const minutes = parseTimeToMinutes(entry.endTime) - parseTimeToMinutes(entry.startTime);
                    entry.assignedPeople.forEach(personId => {
                      if (!personHours[personId]) personHours[personId] = 0;
                      personHours[personId] += minutes;
                    });
                  }
                });
                const teamRanking = Object.entries(personHours)
                  .map(([personId, minutes]) => {
                    const person = allPeople.find(p => p.id === parseInt(personId));
                    return {
                      personId: parseInt(personId),
                      name: person ? `${person.first_name} ${person.last_name}` : `ID:${personId}`,
                      minutes,
                      hours: Math.floor(minutes / 60),
                      remainingMins: minutes % 60
                    };
                  })
                  .sort((a, b) => b.minutes - a.minutes);

                // Category breakdown
                const categoryHours = {};
                allOverviewAssignments.forEach(entry => {
                  const cat = entry.category || 'Uncategorized';
                  if (entry.startTime && entry.endTime) {
                    const minutes = parseTimeToMinutes(entry.endTime) - parseTimeToMinutes(entry.startTime);
                    if (!categoryHours[cat]) {
                      categoryHours[cat] = { minutes: 0, count: 0, color: entry.categoryColor || '#6B7280' };
                    }
                    categoryHours[cat].minutes += minutes;
                    categoryHours[cat].count++;
                  }
                });
                const categoryBreakdown = Object.entries(categoryHours)
                  .map(([category, data]) => ({
                    category,
                    ...data,
                    hours: Math.floor(data.minutes / 60),
                    remainingMins: data.minutes % 60,
                    percentage: totalProjectMinutes > 0 ? Math.round((data.minutes / totalProjectMinutes) * 100) : 0
                  }))
                  .sort((a, b) => b.minutes - a.minutes);

                // Progress calculation
                const completedCount = allOverviewAssignments.filter(a => a.is_completed).length;
                const totalCount = allOverviewAssignments.length;
                const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

                // Average time per turbine
                const turbinesWithTasks = turbines.filter(t => {
                  const assignments = allProjectAssignments[t.id] || [];
                  return assignments.length > 0;
                }).length;
                const avgMinutesPerTurbine = turbinesWithTasks > 0 ? Math.round(totalProjectMinutes / turbinesWithTasks) : 0;
                const avgHoursPerTurbine = Math.floor(avgMinutesPerTurbine / 60);
                const avgMinsPerTurbine = avgMinutesPerTurbine % 60;

                return (
                  <div className="space-y-4">
                    {/* Row 1: Planned vs Actual Overview */}
                    <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-steel-200 bg-steel-50">
                        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                          <Target className="w-4 h-4 text-brand-600" />
                          Planned vs Actual
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 gap-6">
                          {/* Tasks Comparison */}
                          <div>
                            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Tasks (unique)</div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Planned (from Task Lists)</span>
                                <span className="text-sm font-semibold text-slate-900">{totalPlannedTasks}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Scheduled</span>
                                <span className="text-sm font-semibold text-brand-600">{totalScheduledTasks}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Completed</span>
                                <span className="text-sm font-semibold text-emerald-600">{totalCompletedTasks}</span>
                              </div>
                              {/* Progress bar */}
                              <div className="pt-2">
                                <div className="flex justify-between text-xs text-slate-500 mb-1">
                                  <span>Coverage</span>
                                  <span>{tasksCoverage}%</span>
                                </div>
                                <div className="w-full bg-steel-200 rounded-full h-2.5 overflow-hidden">
                                  <div className="h-full flex">
                                    <div
                                      className="bg-emerald-500 h-full"
                                      style={{ width: `${Math.min(100, tasksCompleted)}%` }}
                                      title={`Completed: ${totalCompletedTasks}`}
                                    />
                                    <div
                                      className="bg-brand-400 h-full"
                                      style={{ width: `${Math.min(100 - tasksCompleted, tasksCoverage - tasksCompleted)}%` }}
                                      title={`Scheduled (pending): ${totalScheduledTasks - totalCompletedTasks}`}
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-3 mt-1.5 text-xs">
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Completed
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-brand-400"></span>
                                    Scheduled
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-steel-200"></span>
                                    Not scheduled
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Hours Comparison */}
                          <div>
                            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Hours</div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Planned (estimated)</span>
                                <span className="text-sm font-semibold text-slate-900">
                                  {totalPlannedHours}h {totalPlannedRemainingMins > 0 ? `${totalPlannedRemainingMins}m` : ''}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Scheduled</span>
                                <span className="text-sm font-semibold text-brand-600">
                                  {totalScheduledHours}h {totalScheduledRemainingMins > 0 ? `${totalScheduledRemainingMins}m` : ''}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Completed</span>
                                <span className="text-sm font-semibold text-emerald-600">
                                  {totalCompletedHours}h {totalCompletedRemainingMins > 0 ? `${totalCompletedRemainingMins}m` : ''}
                                </span>
                              </div>
                              {/* Progress bar */}
                              <div className="pt-2">
                                <div className="flex justify-between text-xs text-slate-500 mb-1">
                                  <span>Coverage</span>
                                  <span>{hoursCoverage}%</span>
                                </div>
                                <div className="w-full bg-steel-200 rounded-full h-2.5 overflow-hidden">
                                  <div className="h-full flex">
                                    <div
                                      className="bg-emerald-500 h-full"
                                      style={{ width: `${Math.min(100, hoursCompleted)}%` }}
                                      title={`Completed: ${totalCompletedHours}h`}
                                    />
                                    <div
                                      className="bg-brand-400 h-full"
                                      style={{ width: `${Math.min(100 - hoursCompleted, hoursCoverage - hoursCompleted)}%` }}
                                      title={`Scheduled (pending): ${totalScheduledHours - totalCompletedHours}h`}
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-3 mt-1.5 text-xs">
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Completed
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-brand-400"></span>
                                    Scheduled
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-steel-200"></span>
                                    Not scheduled
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Quick Stats */}
                    <div className="grid grid-cols-4 gap-3">
                      {/* Man-Hours (with people multiplier) */}
                      <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-brand-100 rounded-xl">
                            <Clock className="w-5 h-5 text-brand-600" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-slate-900">
                              {totalProjectHours}h {totalProjectRemainingMins > 0 ? `${totalProjectRemainingMins}m` : ''}
                            </div>
                            <div className="text-xs text-slate-500">Man-Hours Total</div>
                          </div>
                        </div>
                      </div>

                      {/* Average per Turbine */}
                      <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-brand-100 rounded-xl">
                            <Wind className="w-5 h-5 text-brand-600" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-slate-900">
                              {avgHoursPerTurbine}h {avgMinsPerTurbine > 0 ? `${avgMinsPerTurbine}m` : ''}
                            </div>
                            <div className="text-xs text-slate-500">Avg per Turbine</div>
                          </div>
                        </div>
                      </div>

                      {/* Completion Rate */}
                      <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-emerald-100 rounded-xl">
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-slate-900">{hoursCompleted}%</div>
                            <div className="text-xs text-slate-500">Hours Completed</div>
                          </div>
                        </div>
                      </div>

                      {/* Team Size */}
                      <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-brand-100 rounded-xl">
                            <Users className="w-5 h-5 text-brand-600" />
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-slate-900">{teamRanking.length}</div>
                            <div className="text-xs text-slate-500">Team Members</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Team Hours & Task Time Analysis */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Team Hours */}
                      <div className="bg-white rounded-xl border border-border shadow-sm">
                        <div className="px-4 py-3 border-b border-steel-200">
                          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-600" />
                            Team Hours
                          </h3>
                        </div>
                        <div className="p-2 max-h-64 overflow-y-auto">
                          {teamRanking.length === 0 ? (
                            <div className="text-center py-4 text-slate-500 text-sm">No data available</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-slate-500 text-xs border-b bg-steel-100">
                                  <th className="py-1.5 pl-2">#</th>
                                  <th className="py-1.5">Name</th>
                                  <th className="py-1.5 text-right pr-2">Hours</th>
                                </tr>
                              </thead>
                              <tbody>
                                {teamRanking.map((person, index) => (
                                  <tr key={person.personId} className="border-b border-steel-100">
                                    <td className="py-1.5 pl-2 text-slate-500">{index + 1}.</td>
                                    <td className="py-1.5 font-medium text-slate-900">{person.name}</td>
                                    <td className="py-1.5 text-right pr-2 text-slate-600">
                                      {person.hours}h {person.remainingMins > 0 ? `${person.remainingMins}m` : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>

                      {/* Task Planned vs Actual */}
                      {(() => {
                        // Convert plannedByTask to sorted array
                        const taskComparison = Object.values(plannedByTask)
                          .map(t => ({
                            ...t,
                            plannedHours: Math.floor(t.plannedMinutes / 60),
                            plannedMins: Math.round(t.plannedMinutes % 60),
                            scheduledHours: Math.floor(t.scheduledMinutes / 60),
                            scheduledMins: t.scheduledMinutes % 60,
                            completedHours: Math.floor(t.completedMinutes / 60),
                            completedMins: t.completedMinutes % 60,
                            // Calculate difference (negative = under, positive = over)
                            diffMinutes: t.scheduledMinutes - t.plannedMinutes,
                            coverage: t.plannedCount > 0 ? Math.round((t.scheduledCount / t.plannedCount) * 100) : 0
                          }))
                          .sort((a, b) => b.plannedMinutes - a.plannedMinutes);

                        return (
                          <div className="bg-white rounded-xl border border-border shadow-sm">
                            <div className="px-4 py-3 border-b border-steel-200">
                              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-brand-500" />
                                Tasks: Planned vs Actual
                              </h3>
                            </div>
                            <div className="p-2 max-h-64 overflow-y-auto">
                              {taskComparison.length === 0 ? (
                                <div className="text-center py-4 text-slate-500 text-sm">No task list data available</div>
                              ) : (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-slate-500 text-xs border-b bg-steel-100">
                                      <th className="py-1.5 pl-2">Task</th>
                                      <th className="py-1.5 text-center" title="Planned / Scheduled">Tasks</th>
                                      <th className="py-1.5 text-right" title="Planned hours">Plan</th>
                                      <th className="py-1.5 text-right" title="Scheduled hours">Sched</th>
                                      <th className="py-1.5 text-right pr-2" title="Difference">Diff</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {taskComparison.map((task, index) => {
                                      const diffHours = Math.floor(Math.abs(task.diffMinutes) / 60);
                                      const diffMins = Math.abs(task.diffMinutes) % 60;
                                      const isOver = task.diffMinutes > 0;
                                      const isUnder = task.diffMinutes < 0;

                                      return (
                                      <tr key={index} className="border-b border-steel-100">
                                        <td className="py-1.5 pl-2">
                                          <div className="flex items-center gap-1.5">
                                            <div
                                              className="w-2 h-2 rounded-full flex-shrink-0"
                                              style={{ backgroundColor: task.categoryColor }}
                                            />
                                            <span className="text-slate-900 truncate max-w-28" title={task.subcategory}>
                                              {task.subcategory}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-1.5 text-center">
                                          <span className={task.scheduledCount < task.plannedCount ? 'text-amber-600' : 'text-slate-600'}>
                                            {task.scheduledCount}/{task.plannedCount}
                                          </span>
                                        </td>
                                        <td className="py-1.5 text-right text-slate-500">
                                          {task.plannedHours}h{task.plannedMins > 0 ? `${task.plannedMins}m` : ''}
                                        </td>
                                        <td className="py-1.5 text-right text-slate-700">
                                          {task.scheduledHours}h{task.scheduledMins > 0 ? `${task.scheduledMins}m` : ''}
                                        </td>
                                        <td className={`py-1.5 text-right pr-2 font-medium ${isOver ? 'text-red-600' : isUnder ? 'text-amber-600' : 'text-slate-400'}`}>
                                          {task.diffMinutes === 0 ? '—' : `${isOver ? '+' : '-'}${diffHours}h${diffMins > 0 ? `${diffMins}m` : ''}`}
                                        </td>
                                      </tr>
                                    )})}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Row 4: Task Anomalies - Same task, different times on different turbines */}
                    {(() => {
                      // Group tasks by subcategory and turbine to find time differences
                      const taskByTurbine = {};
                      allOverviewAssignments.forEach(entry => {
                        const taskKey = entry.subcategory || 'Unknown';
                        const turbineId = entry.turbineId;
                        if (entry.startTime && entry.endTime && turbineId) {
                          const minutes = parseTimeToMinutes(entry.endTime) - parseTimeToMinutes(entry.startTime);

                          if (!taskByTurbine[taskKey]) {
                            taskByTurbine[taskKey] = {
                              subcategory: taskKey,
                              category: entry.category || 'Uncategorized',
                              color: entry.categoryColor || '#6B7280',
                              turbines: {}
                            };
                          }
                          if (!taskByTurbine[taskKey].turbines[turbineId]) {
                            taskByTurbine[taskKey].turbines[turbineId] = {
                              turbineNumber: turbines.find(t => t.id === turbineId)?.turbine_number || turbineId,
                              totalMinutes: 0,
                              count: 0
                            };
                          }
                          taskByTurbine[taskKey].turbines[turbineId].totalMinutes += minutes;
                          taskByTurbine[taskKey].turbines[turbineId].count++;
                        }
                      });

                      // Find anomalies - tasks performed on multiple turbines with significant time differences
                      const anomalies = [];
                      Object.values(taskByTurbine).forEach(task => {
                        const turbineEntries = Object.values(task.turbines);
                        if (turbineEntries.length >= 2) {
                          const times = turbineEntries.map(t => t.totalMinutes / t.count); // avg time per turbine
                          const minTime = Math.min(...times);
                          const maxTime = Math.max(...times);
                          const diff = maxTime - minTime;
                          const diffPercentage = minTime > 0 ? Math.round((diff / minTime) * 100) : 0;

                          // Only show if difference is significant (>30% or >30 minutes)
                          if (diffPercentage > 30 || diff > 30) {
                            const minTurbine = turbineEntries.find(t => (t.totalMinutes / t.count) === minTime);
                            const maxTurbine = turbineEntries.find(t => (t.totalMinutes / t.count) === maxTime);
                            anomalies.push({
                              ...task,
                              minTime: Math.round(minTime),
                              maxTime: Math.round(maxTime),
                              diff,
                              diffPercentage,
                              minTurbineNumber: minTurbine?.turbineNumber,
                              maxTurbineNumber: maxTurbine?.turbineNumber,
                              turbineCount: turbineEntries.length
                            });
                          }
                        }
                      });
                      anomalies.sort((a, b) => b.diffPercentage - a.diffPercentage);

                      if (anomalies.length === 0) return null;

                      return (
                        <div className="bg-white rounded-xl border border-border shadow-sm">
                          <div className="px-4 py-3 border-b border-steel-200">
                            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              Time Anomalies
                              <span className="text-xs font-normal text-slate-500">
                                (same task, different execution times across turbines)
                              </span>
                            </h3>
                          </div>
                          <div className="p-2 max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-slate-500 text-xs border-b bg-steel-100">
                                  <th className="py-1.5 pl-2">Task</th>
                                  <th className="py-1.5 text-center">Turbines</th>
                                  <th className="py-1.5 text-right">Fastest</th>
                                  <th className="py-1.5 text-right">Slowest</th>
                                  <th className="py-1.5 text-right pr-2">Difference</th>
                                </tr>
                              </thead>
                              <tbody>
                                {anomalies.map((anomaly, index) => (
                                  <tr key={index} className="border-b border-steel-100">
                                    <td className="py-1.5 pl-2">
                                      <div className="flex items-center gap-1.5">
                                        <div
                                          className="w-2 h-2 rounded-full flex-shrink-0"
                                          style={{ backgroundColor: anomaly.color }}
                                        />
                                        <span className="text-slate-900 truncate max-w-40" title={anomaly.subcategory}>
                                          {anomaly.subcategory}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="py-1.5 text-center text-slate-500">{anomaly.turbineCount}</td>
                                    <td className="py-1.5 text-right">
                                      <span className="text-emerald-600">
                                        {Math.floor(anomaly.minTime / 60)}h{anomaly.minTime % 60 > 0 ? ` ${anomaly.minTime % 60}m` : ''}
                                      </span>
                                      <span className="text-slate-400 text-xs ml-1">(T{anomaly.minTurbineNumber})</span>
                                    </td>
                                    <td className="py-1.5 text-right">
                                      <span className="text-red-600">
                                        {Math.floor(anomaly.maxTime / 60)}h{anomaly.maxTime % 60 > 0 ? ` ${anomaly.maxTime % 60}m` : ''}
                                      </span>
                                      <span className="text-slate-400 text-xs ml-1">(T{anomaly.maxTurbineNumber})</span>
                                    </td>
                                    <td className="py-1.5 text-right pr-2">
                                      <span className={`font-medium ${anomaly.diffPercentage > 100 ? 'text-red-600' : 'text-amber-600'}`}>
                                        +{anomaly.diffPercentage}%
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Row 5: Turbine Comparison - Planned vs Actual per Turbine */}
                    {Object.keys(plannedByTurbine).length > 0 && (
                      <div className="bg-white rounded-xl border border-border shadow-sm">
                        <div className="px-4 py-3 border-b border-steel-200">
                          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                            <Wind className="w-4 h-4 text-brand-500" />
                            Turbines: Planned vs Actual
                          </h3>
                        </div>
                        <div className="p-2 max-h-80 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-slate-500 text-xs border-b bg-steel-100">
                                <th className="py-1.5 pl-2">Turbine</th>
                                <th className="py-1.5 text-center" title="Scheduled / Planned tasks">Tasks</th>
                                <th className="py-1.5 text-right" title="Planned hours from task list">Planned</th>
                                <th className="py-1.5 text-right" title="Scheduled hours">Scheduled</th>
                                <th className="py-1.5 text-right" title="Completed hours">Completed</th>
                                <th className="py-1.5 text-right pr-2">Progress</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.values(plannedByTurbine)
                                .sort((a, b) => parseInt(a.turbineNumber) - parseInt(b.turbineNumber))
                                .map((turbine, index) => {
                                  const plannedH = Math.floor(turbine.plannedMinutes / 60);
                                  const plannedM = Math.round(turbine.plannedMinutes % 60);
                                  const scheduledH = Math.floor(turbine.scheduledMinutes / 60);
                                  const scheduledM = turbine.scheduledMinutes % 60;
                                  const completedH = Math.floor(turbine.completedMinutes / 60);
                                  const completedM = turbine.completedMinutes % 60;
                                  const progress = turbine.plannedTasks > 0
                                    ? Math.round((turbine.completedTasks / turbine.plannedTasks) * 100)
                                    : 0;
                                  const coverage = turbine.plannedTasks > 0
                                    ? Math.round((turbine.scheduledTasks / turbine.plannedTasks) * 100)
                                    : 0;

                                  return (
                                    <tr key={index} className="border-b border-steel-100">
                                      <td className="py-1.5 pl-2 font-medium text-slate-900">
                                        T{turbine.turbineNumber}
                                      </td>
                                      <td className="py-1.5 text-center">
                                        <span className={turbine.scheduledTasks < turbine.plannedTasks ? 'text-amber-600' : 'text-slate-600'}>
                                          {turbine.scheduledTasks}/{turbine.plannedTasks}
                                        </span>
                                      </td>
                                      <td className="py-1.5 text-right text-slate-500">
                                        {plannedH}h{plannedM > 0 ? `${plannedM}m` : ''}
                                      </td>
                                      <td className="py-1.5 text-right text-brand-600">
                                        {scheduledH}h{scheduledM > 0 ? `${scheduledM}m` : ''}
                                      </td>
                                      <td className="py-1.5 text-right text-emerald-600">
                                        {completedH}h{completedM > 0 ? `${completedM}m` : ''}
                                      </td>
                                      <td className="py-1.5 text-right pr-2">
                                        <div className="flex items-center justify-end gap-2">
                                          <div className="w-16 bg-steel-200 rounded-full h-1.5 overflow-hidden">
                                            <div className="h-full flex">
                                              <div
                                                className="bg-emerald-500 h-full"
                                                style={{ width: `${Math.min(100, progress)}%` }}
                                              />
                                              <div
                                                className="bg-brand-400 h-full"
                                                style={{ width: `${Math.min(100 - progress, coverage - progress)}%` }}
                                              />
                                            </div>
                                          </div>
                                          <span className={`text-xs font-medium ${progress === 100 ? 'text-emerald-600' : 'text-slate-600'}`}>
                                            {progress}%
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              </>
              )}
            </div>
            </div>
          </div>
          );
        })()}

        {/* Quick Add Modal - allows scrolling behind */}
        {showQuickAddModal && (
          <div
            className="fixed inset-0 z-50 pointer-events-none"
          >
            <div
              className="bg-white rounded-xl shadow-xl absolute flex flex-col pointer-events-auto"
              style={{
                left: `calc(50% + ${quickAddModalPosition.x}px)`,
                top: `calc(50% + ${quickAddModalPosition.y}px)`,
                transform: 'translate(-50%, -50%)',
                width: `${quickAddModalSize.width}px`,
                height: `${quickAddModalSize.height}px`,
                maxWidth: '95vw',
                maxHeight: '95vh',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              {/* Header - Draggable */}
              <div
                className="flex items-center justify-between p-4 border-b border-steel-200 cursor-move select-none flex-shrink-0"
                onMouseDown={(e) => {
                  setIsDraggingModal(true);
                  setDragStart({
                    x: e.clientX - quickAddModalPosition.x,
                    y: e.clientY - quickAddModalPosition.y
                  });
                }}
              >
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Quick Add Task{quickAddSelectedTasks.length === 2 ? 's (Split)' : ''}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Turbine T{quickAddTurbineId}
                    {(() => {
                      const turbine = turbines.find(t => t.id === quickAddTurbineId || t.turbine_number === quickAddTurbineId);
                      return turbine?.task_list_data?.name ? ` • ${turbine.task_list_data.name}` : '';
                    })()}
                    {' - Step '}{quickAddStep} of 3
                    {quickAddSelectedTasks.length > 0 && (
                      <span className="ml-2 text-brand-600">
                        ({quickAddSelectedTasks.length} task{quickAddSelectedTasks.length > 1 ? 's' : ''} selected)
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => openTaskListEditModal(quickAddTurbineId)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100 transition-colors"
                    title="Edit Task List"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>Edit Task List</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowQuickAddModal(false);
                      setQuickAddStep(1);
                      setQuickAddSelectedTasks([]);
                      setQuickAddTaskTimes([]);
                      setQuickAddStartTime('07:00');
                      setQuickAddEndTime('19:00');
                      setQuickAddPeople([]);
                      setQuickAddCar(null);
                      setQuickAddPeopleSearch('');
                      setQuickAddTaskSearch('');
                      setQuickAddIndividualTimes({});
                      setQuickAddModalPosition({ x: 0, y: 0 });
                      setQuickAddModalSize({ width: 896, height: 600 });
                    }}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Step Indicator */}
                <div className="flex items-center mb-6">
                  <div className={`flex items-center ${quickAddStep >= 1 ? 'text-brand-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${quickAddStep >= 1 ? 'bg-brand-500 text-white' : 'bg-steel-200'}`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium">Select Task</span>
                  </div>
                  <div className={`flex-1 h-1 mx-4 ${quickAddStep >= 2 ? 'bg-brand-500' : 'bg-steel-200'}`} />
                  <div className={`flex items-center ${quickAddStep >= 2 ? 'text-brand-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${quickAddStep >= 2 ? 'bg-brand-500 text-white' : 'bg-steel-200'}`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Select People</span>
                  </div>
                  <div className={`flex-1 h-1 mx-4 ${quickAddStep >= 3 ? 'bg-brand-500' : 'bg-steel-200'}`} />
                  <div className={`flex items-center ${quickAddStep >= 3 ? 'text-brand-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${quickAddStep >= 3 ? 'bg-brand-500 text-white' : 'bg-steel-200'}`}>
                      3
                    </div>
                    <span className="ml-2 text-sm font-medium">Select Car</span>
                  </div>
                </div>

                {/* Step Content */}
                <div className="min-h-[400px]">
                  {/* Step 1: Select Task */}
                  {quickAddStep === 1 && (
                    <div className="h-full flex flex-col">
                      {/* Selected Tasks Summary & Time Schedule */}
                      {quickAddSelectedTasks.length > 0 && (
                        <div className="mb-3 p-3 bg-steel-50 rounded-xl border border-steel-200 flex-shrink-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">
                              Selected Tasks ({quickAddSelectedTasks.length})
                            </span>
                            <button
                              onClick={() => {
                                setQuickAddSelectedTasks([]);
                                setQuickAddTaskTimes([]);
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Clear all
                            </button>
                          </div>

                          {/* Time Schedule - show for all selected tasks */}
                          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                            {quickAddSelectedTasks.map((task, idx) => {
                              const taskTime = quickAddTaskTimes[idx] || { startTime: quickAddStartTime, endTime: quickAddEndTime };
                              return (
                                <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border border-steel-200">
                                  <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-slate-900 truncate">{task.subcategoryName}</div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <input
                                      type="time"
                                      value={taskTime.startTime}
                                      onChange={(e) => {
                                        const newTimes = [...quickAddTaskTimes];
                                        newTimes[idx] = { ...taskTime, startTime: e.target.value };
                                        // Adjust previous task's end time
                                        if (idx > 0) {
                                          newTimes[idx - 1] = { ...newTimes[idx - 1], endTime: e.target.value };
                                        }
                                        setQuickAddTaskTimes(newTimes);
                                      }}
                                      className="w-[70px] px-1 py-0.5 text-xs border border-steel-300 rounded text-center focus:ring-1 focus:ring-brand-500"
                                    />
                                    <span className="text-slate-400 text-xs">-</span>
                                    <input
                                      type="time"
                                      value={taskTime.endTime}
                                      onChange={(e) => {
                                        const newTimes = [...quickAddTaskTimes];
                                        newTimes[idx] = { ...taskTime, endTime: e.target.value };
                                        // Adjust next task's start time
                                        if (idx < quickAddSelectedTasks.length - 1) {
                                          newTimes[idx + 1] = { ...newTimes[idx + 1], startTime: e.target.value };
                                        }
                                        setQuickAddTaskTimes(newTimes);
                                      }}
                                      className="w-[70px] px-1 py-0.5 text-xs border border-steel-300 rounded text-center focus:ring-1 focus:ring-brand-500"
                                    />
                                  </div>
                                  <button
                                    onClick={() => {
                                      const newTasks = quickAddSelectedTasks.filter((_, i) => i !== idx);
                                      setQuickAddSelectedTasks(newTasks);
                                      setQuickAddTaskTimes(calculateProportionalTaskTimes(newTasks.length, quickAddStartTime, quickAddEndTime));
                                    }}
                                    className="text-slate-400 hover:text-red-600 flex-shrink-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Search Input */}
                      <div className="mb-2 flex-shrink-0">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search tasks... (select multiple for split)"
                            value={quickAddTaskSearch}
                            onChange={(e) => setQuickAddTaskSearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5 overflow-y-auto flex-1">
                        {quickAddCategories.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <p className="text-sm">No Task List assigned to this turbine.</p>
                            <p className="text-xs mt-1">Assign a Task List in Project settings.</p>
                          </div>
                        ) : quickAddCategories
                          .map(category => {
                            if (!quickAddTaskSearch) {
                              return category;
                            }
                            const searchLower = quickAddTaskSearch.toLowerCase();
                            const categoryMatch = category.name.toLowerCase().includes(searchLower);

                            // If category matches, show all subcategories
                            if (categoryMatch) {
                              return category;
                            }

                            // Otherwise filter subcategories
                            const filteredSubcategories = (category.subcategories || []).filter(subcategory => {
                              const nameMatch = subcategory.name.toLowerCase().includes(searchLower);
                              const descMatch = subcategory.description?.toLowerCase().includes(searchLower);
                              return nameMatch || descMatch;
                            });
                            if (!filteredSubcategories || filteredSubcategories.length === 0) return null;
                            return { ...category, subcategories: filteredSubcategories };
                          })
                          .filter(category => category !== null)
                          .map(category => (
                          <div key={category.name} className="border border-steel-200 rounded p-2">
                            <div className="flex items-center gap-1.5 mb-1">
                              <div
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: category.color }}
                              />
                              <h5 className="font-semibold text-slate-900 text-sm">{category.name}</h5>
                              <span className="text-xs text-slate-400">({(category.subcategories || []).length})</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 ml-4">
                              {(category.subcategories || []).map(subcategory => {
                                const taskKey = `${category.name}|${subcategory.name}`;
                                const isSelected = quickAddSelectedTasks.some(
                                  t => t.subcategoryId === subcategory.id
                                );
                                const selectionIndex = quickAddSelectedTasks.findIndex(
                                  t => t.subcategoryId === subcategory.id
                                );

                                // Check if this subcategory has a completed assignment for this turbine (any date)
                                // Use allProjectAssignments which contains all assignments across all dates
                                const allTurbineAssignments = allProjectAssignments[quickAddTurbineId] || [];
                                const isCompleted = allTurbineAssignments.some(
                                  a => a.subcategory_id === subcategory.id && a.is_completed
                                );

                                return (
                                  <button
                                    key={subcategory.name}
                                    disabled={isCompleted}
                                    onClick={() => {
                                      if (isCompleted) return; // Extra safety
                                      if (isSelected) {
                                        // Remove from selection
                                        const newTasks = quickAddSelectedTasks.filter(
                                          t => t.subcategoryId !== subcategory.id
                                        );
                                        setQuickAddSelectedTasks(newTasks);
                                        // Recalculate proportional times
                                        setQuickAddTaskTimes(calculateProportionalTaskTimes(newTasks.length, quickAddStartTime, quickAddEndTime));
                                      } else {
                                        // Add to selection (no limit)
                                        const newTasks = [...quickAddSelectedTasks, {
                                          subcategoryId: subcategory.id,
                                          subcategoryName: subcategory.name,
                                          categoryName: category.name,
                                          categoryColor: category.color
                                        }];
                                        setQuickAddSelectedTasks(newTasks);
                                        // Recalculate proportional times
                                        setQuickAddTaskTimes(calculateProportionalTaskTimes(newTasks.length, quickAddStartTime, quickAddEndTime));
                                      }
                                    }}
                                    className={`text-left p-1.5 rounded border transition-all relative ${
                                      isCompleted
                                        ? 'border-green-300 bg-green-50 cursor-not-allowed opacity-75'
                                        : isSelected
                                          ? 'border-brand-600 bg-brand-50 ring-2 ring-brand-300'
                                          : 'border-steel-200 hover:border-steel-300 bg-white'
                                    }`}
                                  >
                                    {isCompleted && (
                                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs bg-green-600">
                                        <Check className="w-3 h-3" />
                                      </div>
                                    )}
                                    {isSelected && !isCompleted && (
                                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold bg-brand-500">
                                        {selectionIndex + 1}
                                      </div>
                                    )}
                                    <div className={`text-xs font-medium truncate ${isCompleted ? 'text-green-700' : 'text-slate-900'}`}>
                                      {subcategory.name}
                                      {isCompleted && <span className="ml-1 text-green-600">(Done)</span>}
                                    </div>
                                    {subcategory.description && (
                                      <div className={`text-xs truncate ${isCompleted ? 'text-green-600' : 'text-slate-500'}`}>
                                        {subcategory.description}
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Select People */}
                  {quickAddStep === 2 && (
                    <div className="grid grid-cols-3 gap-4">
                      {/* Left Column - Selected People with Times */}
                      <div>
                        <h4 className="text-lg font-semibold text-slate-900 mb-4">
                          {quickAddSelectedTasks.length === 2 ? 'Selected People' : 'Selected People & Time'}
                        </h4>
                        <div className="space-y-4">
                          {quickAddPeople.length === 0 ? (
                            <div className="text-center py-8 text-slate-400 border-2 border-dashed border-steel-200 rounded-xl">
                              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Select people from the right</p>
                              {quickAddSelectedTasks.length < 2 && (
                                <p className="text-xs mt-1">Each person can have different hours</p>
                              )}
                            </div>
                          ) : quickAddSelectedTasks.length >= 2 ? (
                            /* Simplified view when multiple tasks selected - time is already defined */
                            <div className="space-y-3">
                              {/* Time split summary */}
                              <div className="p-3 bg-steel-50 rounded-xl border border-steel-200">
                                <div className="text-xs font-medium text-slate-500 mb-2">Time Schedule (from Step 1)</div>
                                <div className="space-y-1">
                                  {quickAddSelectedTasks.map((task, index) => {
                                    const taskTime = quickAddTaskTimes[index] || { startTime: quickAddStartTime, endTime: quickAddEndTime };
                                    const colors = ['blue', 'green', 'purple', 'orange', 'pink', 'cyan'];
                                    const color = colors[index % colors.length];
                                    return (
                                      <div key={index} className={`flex items-center gap-2 p-2 bg-${color}-50 rounded border border-${color}-200`}>
                                        <div className={`w-5 h-5 rounded-full bg-${color}-600 flex items-center justify-center text-white text-xs font-bold`}>{index + 1}</div>
                                        <span className="text-xs text-slate-700 flex-1 truncate">{task.subcategoryName}</span>
                                        <span className={`text-xs font-semibold text-${color}-700`}>{taskTime.startTime} - {taskTime.endTime}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Selected people list */}
                              <div className="p-3 bg-brand-50 rounded-xl border border-brand-200">
                                <div className="text-sm font-medium text-brand-800 mb-2">
                                  {quickAddPeople.length} {quickAddPeople.length === 1 ? 'person' : 'people'} selected
                                </div>
                                <div className="space-y-1 max-h-[250px] overflow-y-auto">
                                  {quickAddPeople.map(personId => {
                                    const person = projectPeople.find(p => p.id === personId);
                                    return person ? (
                                      <div key={personId} className="flex items-center justify-between p-2 bg-white rounded border border-brand-200">
                                        <span className="text-sm text-slate-900">
                                          {person.last_name} {person.first_name}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => setQuickAddPeople(prev => prev.filter(id => id !== personId))}
                                          className="text-red-500 hover:text-red-700 text-xs"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Full view with time controls when 1 task selected */
                            <div className="space-y-3">
                              {/* Global time setting */}
                              <div className="p-3 bg-steel-50 rounded-xl border border-steel-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-3 h-3 rounded"
                                      style={{ backgroundColor: quickAddSelectedTasks[0]?.categoryColor || '#3B82F6' }}
                                    />
                                    <span className="text-xs font-medium text-slate-700">{quickAddSelectedTasks[0]?.subcategory}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="time"
                                      value={quickAddStartTime}
                                      onChange={(e) => setQuickAddStartTime(e.target.value)}
                                      className="w-20 px-1 py-0.5 text-xs border border-steel-300 rounded text-center focus:ring-1 focus:ring-brand-500"
                                    />
                                    <span className="text-slate-400 text-xs">-</span>
                                    <input
                                      type="time"
                                      value={quickAddEndTime}
                                      onChange={(e) => setQuickAddEndTime(e.target.value)}
                                      className="w-20 px-1 py-0.5 text-xs border border-steel-300 rounded text-center focus:ring-1 focus:ring-brand-500"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Selected people list */}
                              <div className="p-3 bg-brand-50 rounded-xl border border-brand-200">
                                <div className="text-sm font-medium text-brand-800 mb-2">
                                  {quickAddPeople.length} {quickAddPeople.length === 1 ? 'person' : 'people'} selected
                                </div>
                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                  {quickAddPeople.map(personId => {
                                    const person = projectPeople.find(p => p.id === personId);
                                    const schedule = getPersonSchedule(personId);
                                    const individualTime = quickAddIndividualTimes[personId];
                                    const hasCustomTime = individualTime &&
                                      (individualTime.startTime !== quickAddStartTime || individualTime.endTime !== quickAddEndTime);

                                    return person ? (
                                      <div key={personId} className="p-2 bg-white rounded border border-brand-200">
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm text-slate-900">
                                            {person.last_name} {person.first_name}
                                          </span>
                                          <div className="flex items-center gap-2">
                                            {hasCustomTime && (
                                              <span className="text-xs text-brand-600 font-medium">
                                                {individualTime.startTime}-{individualTime.endTime}
                                              </span>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                // Toggle individual time editing
                                                if (hasCustomTime) {
                                                  // Reset to global time
                                                  setQuickAddIndividualTimes(prev => {
                                                    const newTimes = { ...prev };
                                                    delete newTimes[personId];
                                                    return newTimes;
                                                  });
                                                } else {
                                                  // Set custom time (same as global initially)
                                                  setQuickAddIndividualTimes(prev => ({
                                                    ...prev,
                                                    [personId]: { startTime: quickAddStartTime, endTime: quickAddEndTime }
                                                  }));
                                                }
                                              }}
                                              className={`text-xs px-1.5 py-0.5 rounded ${hasCustomTime ? 'bg-brand-100 text-brand-700' : 'bg-steel-100 text-slate-600 hover:bg-steel-200'}`}
                                              title={hasCustomTime ? 'Reset to global time' : 'Set custom time'}
                                            >
                                              {hasCustomTime ? '↻' : '⏱'}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setQuickAddPeople(prev => prev.filter(id => id !== personId))}
                                              className="text-red-500 hover:text-red-700 text-xs"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        </div>
                                        {/* Busy times info */}
                                        {schedule.length > 0 && (
                                          <div className="mt-1 text-xs text-orange-600">
                                            Busy: {schedule.map(s => `${s.startTime}-${s.endTime}`).join(', ')}
                                          </div>
                                        )}
                                        {/* Individual time controls - only when custom time is set */}
                                        {hasCustomTime && (
                                          <div className="flex items-center gap-2 mt-2">
                                            <input
                                              type="time"
                                              value={individualTime.startTime}
                                              onChange={(e) => {
                                                setQuickAddIndividualTimes(prev => ({
                                                  ...prev,
                                                  [personId]: { ...individualTime, startTime: e.target.value }
                                                }));
                                              }}
                                              className="flex-1 px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                                            />
                                            <span className="text-slate-400 text-xs">-</span>
                                            <input
                                              type="time"
                                              value={individualTime.endTime}
                                              onChange={(e) => {
                                                setQuickAddIndividualTimes(prev => ({
                                                  ...prev,
                                                  [personId]: { ...individualTime, endTime: e.target.value }
                                                }));
                                              }}
                                              className="flex-1 px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Column - People Selection (spans 2 columns) */}
                      <div className="col-span-2">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4">Select People</h4>

                        {/* Search Input */}
                        <div className="mb-3">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              placeholder="Search people by name..."
                              value={quickAddPeopleSearch}
                              onChange={(e) => setQuickAddPeopleSearch(e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* People Grid */}
                        {console.log('Quick Add - projectPeople:', projectPeople.map(p => ({ id: p.id, name: `${p.last_name} ${p.first_name}`, is_active: p.is_active })))}
                        <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                          {projectPeople
                            .filter(person => person.is_active !== false)
                            .filter(person => {
                              if (!quickAddPeopleSearch) return true;
                              const searchLower = quickAddPeopleSearch.toLowerCase();
                              const fullName = `${person.last_name} ${person.first_name}`.toLowerCase();
                              return fullName.includes(searchLower);
                            })
                            .sort((a, b) => {
                              const utilA = getPersonUtilization(a.id);
                              const utilB = getPersonUtilization(b.id);
                              return utilA - utilB;
                            })
                            .map(person => {
                              const isSelected = quickAddPeople.includes(person.id);
                              const utilization = getPersonUtilization(person.id);
                              const isFullyBooked = utilization >= 100;
                              const schedule = getPersonSchedule(person.id);

                              return (
                                <button
                                  key={person.id}
                                  onClick={() => {
                                    if (isSelected) {
                                      setQuickAddPeople(prev => prev.filter(id => id !== person.id));
                                      // Also remove individual time
                                      setQuickAddIndividualTimes(prev => {
                                        const newTimes = { ...prev };
                                        delete newTimes[person.id];
                                        return newTimes;
                                      });
                                    } else {
                                      // Add person
                                      setQuickAddPeople(prev => [...prev, person.id]);

                                      // Auto-adjust start time if person is busy
                                      if (schedule.length > 0) {
                                        // Find the latest end time from this person's schedule
                                        const personLatestEnd = schedule.reduce((latest, slot) => {
                                          return slot.endTime > latest ? slot.endTime : latest;
                                        }, '00:00');

                                        // Update global start time if this person's busy time ends later
                                        if (personLatestEnd > quickAddStartTime) {
                                          setQuickAddStartTime(personLatestEnd);
                                        }
                                      }
                                    }
                                  }}
                                  className={`w-full text-left p-2 rounded-xl border-2 transition-all ${
                                    isSelected
                                      ? 'border-brand-600 bg-brand-50'
                                      : isFullyBooked
                                      ? 'border-red-200 bg-red-50 opacity-60'
                                      : 'border-steel-200 hover:border-steel-300 bg-white'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1">
                                        <span className="font-medium text-slate-900 text-sm truncate">
                                          {person.last_name} {person.first_name}
                                        </span>
                                        {isFullyBooked && (
                                          <span className="text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded flex-shrink-0">
                                            FULL
                                          </span>
                                        )}
                                      </div>
                                      {/* Utilization bar */}
                                      <div className="mt-1 flex items-center gap-2">
                                        <div className="flex-1 bg-steel-200 rounded-full h-1.5">
                                          <div
                                            className={`h-1.5 rounded-full transition-all ${
                                              utilization >= 100 ? 'bg-red-500' :
                                              utilization >= 76 ? 'bg-orange-500' :
                                              utilization >= 51 ? 'bg-yellow-500' :
                                              utilization >= 26 ? 'bg-green-400' :
                                              'bg-steel-400'
                                            }`}
                                            style={{ width: `${Math.min(utilization, 100)}%` }}
                                          />
                                        </div>
                                        <span className={`text-xs font-semibold ${
                                          utilization >= 100 ? 'text-red-600' :
                                          utilization >= 76 ? 'text-orange-600' :
                                          utilization >= 51 ? 'text-yellow-600' :
                                          utilization >= 26 ? 'text-green-600' :
                                          'text-slate-500'
                                        }`}>
                                          {utilization}%
                                        </span>
                                      </div>
                                      {/* Schedule - busy times */}
                                      {schedule.length > 0 && (
                                        <div className="mt-1.5 space-y-0.5">
                                          {schedule.map((task, idx) => (
                                            <div key={idx} className="flex items-center gap-1 text-xs">
                                              <span className="font-mono text-slate-500 w-[85px] flex-shrink-0">
                                                {task.startTime}-{task.endTime}
                                              </span>
                                              <span
                                                className="px-1 py-0.5 rounded text-white text-[10px] truncate"
                                                style={{ backgroundColor: task.categoryColor || '#6B7280' }}
                                              >
                                                T{task.turbineId} {task.subcategory}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {schedule.length === 0 && (
                                        <div className="mt-1 text-xs text-green-600">
                                          ✓ Available all day
                                        </div>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <div className="w-4 h-4 bg-brand-500 rounded-full flex items-center justify-center ml-1 flex-shrink-0">
                                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Select Car */}
                  {quickAddStep === 3 && (
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Assign a car (optional)</h4>
                      <div className="grid grid-cols-2 gap-2 max-h-[450px] overflow-y-auto">
                        <button
                          onClick={() => setQuickAddCar(null)}
                          className={`text-left p-2 rounded-xl border-2 transition-all ${
                            quickAddCar === null
                              ? 'border-brand-600 bg-brand-50'
                              : 'border-steel-200 hover:border-steel-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-slate-900 text-sm">No car</div>
                              <div className="text-xs text-slate-600">Skip assignment</div>
                            </div>
                            {quickAddCar === null && (
                              <div className="w-4 h-4 bg-brand-500 rounded-full flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>

                        {projectCars
                          .sort((a, b) => {
                            const utilA = getCarUtilization(a.id);
                            const utilB = getCarUtilization(b.id);
                            return utilA - utilB;
                          })
                          .map(car => {
                            const isSelected = quickAddCar === car.id;
                            const utilization = getCarUtilization(car.id);
                            const isFullyBooked = utilization >= 100;

                            return (
                              <button
                                key={car.id}
                                onClick={() => setQuickAddCar(car.id)}
                                className={`text-left p-2 rounded-xl border-2 transition-all ${
                                  isSelected
                                    ? 'border-brand-600 bg-brand-50'
                                    : isFullyBooked
                                    ? 'border-green-200 bg-green-50'
                                    : 'border-steel-200 hover:border-steel-300 bg-white'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="font-medium text-slate-900 text-sm truncate">
                                        {car.car_brand && car.car_model
                                          ? `${car.car_brand} ${car.car_model}`
                                          : car.name || `Car #${car.id}`}
                                      </span>
                                      {isFullyBooked && (
                                        <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded flex-shrink-0">
                                          FULL
                                        </span>
                                      )}
                                    </div>
                                    {(car.plate_number || car.registration_number) && (
                                      <div className="text-xs text-slate-600 truncate">{car.plate_number || car.registration_number}</div>
                                    )}
                                    {/* Utilization bar */}
                                    <div className="mt-1 flex items-center gap-2">
                                      <div className="flex-1 bg-steel-200 rounded-full h-1.5">
                                        <div
                                          className={`h-1.5 rounded-full transition-all ${
                                            utilization === 100 ? 'bg-green-600' :
                                            utilization >= 76 ? 'bg-red-500' :
                                            utilization >= 51 ? 'bg-orange-500' :
                                            utilization >= 26 ? 'bg-yellow-500' :
                                            'bg-steel-400'
                                          }`}
                                          style={{ width: `${Math.min(utilization, 100)}%` }}
                                        />
                                      </div>
                                      <span className={`text-xs font-semibold ${
                                        utilization === 100 ? 'text-green-700' :
                                        utilization >= 76 ? 'text-red-600' :
                                        utilization >= 51 ? 'text-orange-600' :
                                        utilization >= 26 ? 'text-yellow-600' :
                                        'text-slate-500'
                                      }`}>
                                        {utilization}%
                                      </span>
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <div className="w-4 h-4 bg-brand-500 rounded-full flex items-center justify-center ml-1 flex-shrink-0">
                                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {/* Navigation Buttons - Fixed at bottom */}
              <div className="flex items-center justify-between p-4 border-t border-steel-200 flex-shrink-0">
                <button
                  onClick={() => {
                    if (quickAddStep > 1) {
                      setQuickAddStep(quickAddStep - 1);
                    } else {
                      setShowQuickAddModal(false);
                      setQuickAddStep(1);
                      setQuickAddSelectedTasks([]);
                      setQuickAddTaskTimes([]);
                      setQuickAddStartTime('07:00');
                      setQuickAddEndTime('19:00');
                      setQuickAddPeople([]);
                      setQuickAddCar(null);
                      setQuickAddPeopleSearch('');
                      setQuickAddModalPosition({ x: 0, y: 0 });
                      setQuickAddModalSize({ width: 896, height: 600 });
                    }
                  }}
                  className="px-4 py-2 text-slate-700 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
                >
                  {quickAddStep === 1 ? 'Cancel' : 'Back'}
                </button>

                  {quickAddStep < 3 ? (
                    <button
                      onClick={() => {
                        if (quickAddStep === 1 && quickAddSelectedTasks.length > 0) {
                          setQuickAddStep(2);
                        } else if (quickAddStep === 2) {
                          setQuickAddStep(3);
                        }
                      }}
                      disabled={quickAddStep === 1 && quickAddSelectedTasks.length === 0}
                      className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors disabled:bg-steel-300 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (quickAddSelectedTasks.length === 0 || quickAddPeople.length === 0) {
                          alert('Please select at least one task and at least one person');
                          return;
                        }

                        try {
                          console.log('🔵 Quick Add - selectedProject:', selectedProject);
                          console.log('🔵 Quick Add - selectedProject.id:', selectedProject?.id);
                          console.log('🟢 Quick Add - Creating assignment for turbine ID:', quickAddTurbineId);
                          console.log('🟢 Quick Add - Selected tasks:', quickAddSelectedTasks);
                          console.log('🟢 Quick Add - Task times:', quickAddTaskTimes);

                          // Check if we have multiple tasks with time splits
                          const hasMultipleTasks = quickAddSelectedTasks.length > 1;

                          // Create assignments for each selected task
                          const assignmentsToCreate = quickAddSelectedTasks.map((task, index) => {
                            // Get times from quickAddTaskTimes array, or use global times as fallback
                            const taskTime = quickAddTaskTimes[index] || { startTime: quickAddStartTime, endTime: quickAddEndTime };
                            const taskStartTime = taskTime.startTime;
                            const taskEndTime = taskTime.endTime;

                            // Build individual_times object for backend
                            const individualTimesForBackend = {};
                            quickAddPeople.forEach(personId => {
                              const personTimes = quickAddIndividualTimes[personId] || { startTime: quickAddStartTime, endTime: quickAddEndTime };

                              // For multiple tasks, calculate proportional times for each person
                              if (hasMultipleTasks && quickAddTaskTimes.length > 0) {
                                // Calculate the proportion of this task in global time range
                                const toMinutes = (t) => parseInt(t.split(':')[0]) * 60 + parseInt(t.split(':')[1]);
                                const toTime = (mins) => {
                                  const h = Math.floor(mins / 60);
                                  const m = mins % 60;
                                  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                };

                                const globalStart = toMinutes(quickAddStartTime);
                                const globalEnd = toMinutes(quickAddEndTime);
                                const globalDuration = globalEnd - globalStart;

                                const taskStart = toMinutes(taskStartTime);
                                const taskEnd = toMinutes(taskEndTime);

                                // Calculate proportional position for this person's time range
                                const personStart = toMinutes(personTimes.startTime);
                                const personEnd = toMinutes(personTimes.endTime);
                                const personDuration = personEnd - personStart;

                                // Map task's position in global range to person's range
                                const taskStartRatio = globalDuration > 0 ? (taskStart - globalStart) / globalDuration : 0;
                                const taskEndRatio = globalDuration > 0 ? (taskEnd - globalStart) / globalDuration : 1;

                                const personTaskStart = Math.round(personStart + taskStartRatio * personDuration);
                                const personTaskEnd = Math.round(personStart + taskEndRatio * personDuration);

                                individualTimesForBackend[personId] = {
                                  start_time: toTime(personTaskStart),
                                  end_time: toTime(personTaskEnd)
                                };
                              } else {
                                individualTimesForBackend[personId] = {
                                  start_time: personTimes.startTime,
                                  end_time: personTimes.endTime
                                };
                              }
                            });

                            return {
                              subcategory_id: task.subcategoryId,
                              turbine: quickAddTurbineId,
                              start_time: taskStartTime,
                              end_time: taskEndTime,
                              individual_times: individualTimesForBackend,
                              date: format(selectedDate, 'yyyy-MM-dd'),
                              project: selectedProject,
                              assigned_people: quickAddPeople,
                              assigned_car: quickAddCar,
                              is_alternative: false
                            };
                          });

                          console.log('🔵 Quick Add - Assignments to create:', assignmentsToCreate);

                          // Create all assignments
                          for (const assignment of assignmentsToCreate) {
                            console.log('🔵 Quick Add - Creating assignment:', assignment);
                            await axios.post(
                              `${API_BASE_URL}/api/task-assignments/`,
                              assignment,
                              {
                                headers: {
                                  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                                  'Content-Type': 'application/json'
                                }
                              }
                            );
                          }

                          console.log('Quick add assignments created successfully');

                          // Refresh assignments
                          loadTaskAssignments();
                          // Refresh all project assignments for experience tracking
                          loadAllProjectAssignments();

                          // Close modal and reset
                          setShowQuickAddModal(false);
                          setQuickAddStep(1);
                          setQuickAddSelectedTasks([]);
                          setQuickAddTaskTimes([]);
                          setQuickAddStartTime('07:00');
                          setQuickAddEndTime('19:00');
                          setQuickAddPeople([]);
                          setQuickAddCar(null);
                          setQuickAddPeopleSearch('');
                          setQuickAddTaskSearch('');
                          setQuickAddIndividualTimes({});
                          setQuickAddModalPosition({ x: 0, y: 0 });
                          setQuickAddModalSize({ width: 896, height: 600 });

                          alert(`${assignmentsToCreate.length > 1 ? 'Tasks' : 'Task'} assigned successfully!`);
                        } catch (error) {
                          console.error('🔴 Error creating quick add assignment:', error);
                          console.error('🔴 Error response data:', error.response?.data);

                          // Expand error arrays to see actual messages
                          if (error.response?.data) {
                            Object.keys(error.response.data).forEach(field => {
                              const errors = error.response.data[field];
                              console.error(`🔴 Field "${field}" errors:`, errors);
                            });
                          }

                          console.error('🔴 Error response status:', error.response?.status);
                          console.error('🔴 Error response headers:', error.response?.headers);

                          const errorMessage = error.response?.data?.error
                            || error.response?.data?.message
                            || JSON.stringify(error.response?.data)
                            || 'Failed to create assignment';

                          alert('Error: ' + errorMessage);
                        }
                      }}
                      disabled={quickAddPeople.length === 0}
                      className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:bg-steel-300 disabled:cursor-not-allowed"
                    >
                      Create Assignment
                    </button>
                  )}
              </div>

              {/* Resize Handle */}
              <div
                className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setIsResizingModal(true);
                  const rect = e.currentTarget.parentElement.getBoundingClientRect();
                  setDragStart({
                    x: rect.left,
                    y: rect.top
                  });
                }}
              >
                <svg className="w-4 h-4 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Task List Edit Modal */}
        {showTaskListEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-steel-200">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Edit Task List</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Creating a copy for this project (Default and Project fields are locked)
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTaskListEditModal(false);
                    setEditingTaskListData(null);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={taskListFormData.name}
                      onChange={(e) => setTaskListFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      placeholder="Task list name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={taskListFormData.description}
                      onChange={(e) => setTaskListFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      placeholder="Optional description"
                    />
                  </div>
                </div>

                {/* Locked fields info */}
                <div className="flex items-center space-x-6 mb-6 p-3 bg-steel-50 rounded-xl">
                  <div className="flex items-center space-x-2 opacity-60">
                    <input type="checkbox" checked={false} disabled className="h-4 w-4" />
                    <span className="text-sm text-slate-700">Default</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-slate-700">Project:</span>
                    <span className="text-sm font-medium text-brand-600">
                      {projects.find(p => p.id.toString() === selectedProject)?.name || 'Current Project'}
                    </span>
                  </div>
                </div>

                {/* Categories */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-900">Categories</h4>
                    <button
                      onClick={addTaskListCategory}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Category</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {taskListFormData.categories.map((category, catIndex) => (
                      <div key={catIndex} className="border border-steel-200 rounded-xl">
                        {/* Category Header */}
                        <div className="flex items-center p-3 bg-steel-50 rounded-t-lg">
                          <button
                            onClick={() => toggleTaskListCategoryExpansion(catIndex)}
                            className="mr-2 text-slate-500 hover:text-slate-700"
                          >
                            <ChevronDown className={`w-5 h-5 transition-transform ${expandedTaskListCategories.has(catIndex) ? '' : '-rotate-90'}`} />
                          </button>
                          <input
                            type="color"
                            value={category.color}
                            onChange={(e) => {
                              const newCategories = [...taskListFormData.categories];
                              newCategories[catIndex].color = e.target.value;
                              setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                            }}
                            className="w-8 h-8 rounded cursor-pointer mr-2"
                          />
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => {
                              const newCategories = [...taskListFormData.categories];
                              newCategories[catIndex].name = e.target.value;
                              setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                            }}
                            className="flex-1 px-2 py-1 border border-steel-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                            placeholder="Category name"
                          />
                          <span className="mx-2 text-sm text-slate-500">
                            {category.subcategories.filter(s => s.name.trim()).length} tasks
                          </span>
                          {taskListFormData.categories.length > 1 && (
                            <button
                              onClick={() => removeTaskListCategory(catIndex)}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Tasks */}
                        {expandedTaskListCategories.has(catIndex) && (
                          <div className="p-3 space-y-2">
                            {category.subcategories.map((sub, subIndex) => (
                              <div key={subIndex} className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={sub.name}
                                  onChange={(e) => {
                                    const newCategories = [...taskListFormData.categories];
                                    newCategories[catIndex].subcategories[subIndex].name = e.target.value;
                                    setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                                  }}
                                  className="flex-1 px-2 py-1 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                  placeholder="Task name"
                                />
                                <input
                                  type="text"
                                  value={sub.description || ''}
                                  onChange={(e) => {
                                    const newCategories = [...taskListFormData.categories];
                                    newCategories[catIndex].subcategories[subIndex].description = e.target.value;
                                    setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                                  }}
                                  className="w-32 px-2 py-1 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                  placeholder="Description"
                                />
                                <input
                                  type="number"
                                  value={sub.estimated_hours || ''}
                                  onChange={(e) => {
                                    const newCategories = [...taskListFormData.categories];
                                    newCategories[catIndex].subcategories[subIndex].estimated_hours = e.target.value;
                                    setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                                  }}
                                  className="w-16 px-2 py-1 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                  placeholder="Hours"
                                  step="0.5"
                                  min="0"
                                />
                                {category.subcategories.length > 1 && (
                                  <button
                                    onClick={() => {
                                      const newCategories = [...taskListFormData.categories];
                                      newCategories[catIndex].subcategories = newCategories[catIndex].subcategories.filter((_, i) => i !== subIndex);
                                      setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const newCategories = [...taskListFormData.categories];
                                newCategories[catIndex].subcategories.push({ name: '', description: '', estimated_hours: '' });
                                setTaskListFormData(prev => ({ ...prev, categories: newCategories }));
                              }}
                              className="flex items-center space-x-1 px-2 py-1 text-sm text-brand-600 hover:text-brand-700"
                            >
                              <Plus className="w-3 h-3" />
                              <span>Add Task</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-4 border-t border-steel-200">
                <button
                  onClick={() => {
                    setShowTaskListEditModal(false);
                    setEditingTaskListData(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 rounded-xl hover:bg-steel-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTaskListEdit}
                  disabled={!taskListFormData.name.trim() || taskListFormData.categories.filter(c => c.name.trim()).length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-600 transition-colors disabled:bg-steel-400 disabled:cursor-not-allowed"
                >
                  Save Task List
                </button>
              </div>
            </div>
          </div>
        )}

        {/* LIVE Map Modal */}
        {showLiveMap && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full h-full mx-2 my-2 flex flex-col overflow-hidden">
              {/* Modal Content - LiveMap handles its own header */}
              <div className="flex-1 relative">
                <LiveMap
                  key={liveMapKey}
                  selectedProject={selectedProject}
                  selectedDate={selectedDate}
                  onClose={() => setShowLiveMap(false)}
                  onRefresh={() => setLiveMapKey(prev => prev + 1)}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyWorkPlanning;